import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import AdvancedChart, { AdvancedChartTooltipData, ChartElement, formatRevenueWithUnits } from '../components/AdvancedChart';
import LeftControlPanel from '../components/LeftControlPanel';
import RightAdjustmentPanel from '../components/RightAdjustmentPanel';
import { DateRangeSelector } from '../components/DateRangeSelector';
import AdvancedDateNavigator from '../components/AdvancedDateNavigator';
import CenteredLoadingSpinner from '../components/CenteredLoadingSpinner';
import DataSourceAttribution from '../components/DataSourceAttribution';
import { mockStockData, getFinancialMetricData, getBalanceSheetMetricData, getCashFlowMetricData, getFinancialDataPeriods, calculatePE, calculateFlatEPSTTM, calculateFairPriceFlatEPSTTM, loadStockData, FinancialDateMode, resolveFmpAtReportedDate } from '../data/mockStockData';
import { config } from '../config/config';
import { fetchIncomeStatement, fetchIncomeStatementTTMSeries, fetchCashFlowStatementTTMSeries, fetchKeyMetrics, fetchQuarterlyCashFlowStatements, fetchHistoricalPrices, IncomeStatement, CashFlowStatement, BalanceSheet } from '../services/stockApi';
import { fetchFiscalStandardizedIncomeStatement, fetchFiscalStandardizedBalanceSheet, fetchFiscalStandardizedCashFlowStatement, fetchFiscalRoicRatios, fetchFiscalStockPrices, fetchFiscalCompanyFilings, FiscalIncomeRow, FiscalCashRow } from '../services/fiscalApi';
import { adminApi, isUnauthorized } from '../services/adminApi';
import type { SnackbarVariant } from '../components/Snackbar';
import '../components/ResponsiveStyles.css';

const ADVANCED_CHART_PREFS_STORAGE_VERSION = 1;

// Helper function to format financial values coming in as "millions USD".
// Display as K (thousands of millions) with 2 decimals.
function formatRevenueToK(value: number): string {
  if (!Number.isFinite(value)) return '0.00K';
  const valueInK = value / 1000;
  return valueInK.toFixed(2) + 'K';
}

// Values are stored as millions. Display buybacks in billions for clarity.

type TooltipValueFormat = 'number2' | 'k' | 'percent2' | 'money2';

function getTooltipItemStyle(label: string, format: TooltipValueFormat): React.CSSProperties {
  return {
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    flex: '0 0 auto',
    minWidth: 'max-content',
    minHeight: '1.25rem',
  };
}

function quantileSorted(values: number[], q: number): number {
  if (!values.length) return 0;
  const qClamped = Math.min(1, Math.max(0, q));
  if (values.length === 1) return values[0];
  const pos = (values.length - 1) * qClamped;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return values[lo];
  const w = pos - lo;
  return values[lo] * (1 - w) + values[hi] * w;
}
interface AdvancedChartScreenProps {
  isDarkMode: boolean;
  isAdmin?: boolean;
  onToggleTheme: () => void;
  onBackToMain: () => void;
  selectedStock?: string;
  authToken?: string | null;
  authExpiresAtUtc?: string | null;
  authUserId?: number | null;
  authUsername?: string | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
  onVisibleRangeChange?: (
    startDate: Date,
    endDate: Date,
    source?: 'date-range-container' | 'prev-next-quarter-year' | 'other'
  ) => void;
  onPeriodTypeChange?: (type: 'Annual Data' | 'Quarterly Data' | 'TTM') => void;
  onAnalysisDateModeChange?: (mode: FinancialDateMode) => void;
  disableMinHeight?: boolean;
  // Separates persisted slider/visibility state between different tabs that reuse this screen.
  // Example: Research "Charts" vs "Back Testing".
  prefsScope?: 'Charts' | 'Back Testing' | 'Charts fpm';
  // Overrides which data/source scope to use without changing persisted state scope.
  dataScope?: 'Charts' | 'Back Testing' | 'Charts fpm';
  // Optionally read visibility from another scope (e.g., Back Testing mirrors Charts).
  visibilityScope?: 'Charts' | 'Back Testing' | 'Charts fpm';
}

const DEFAULT_ELEMENT_VISIBILITY: Record<string, boolean> = {
  'price-line': true,
  'revenue': false,
  'pe': false,
  'p-fcf-ratio': false,
  'p-ocf-ratio': false,
  'net-income': false,
  'gross-profit': false,
  'operating-income': false,
  'pretax-income': false,
  'eps-diluted': false,
  'flat-eps-ttm': false,
  'fair-price': false,
  'entry-point-1': false,
  'entry-point-2': false,
  'fair-price-flat-eps-ttm': false,
  'flat-ocf-ttm': false,
  'fair-price-ocf': false,
  'entry-point-1-ocf': false,
  'entry-point-2-ocf': false,
};

const AdvancedChartScreen: React.FC<AdvancedChartScreenProps> = ({
  isDarkMode,
  isAdmin = false,
  onToggleTheme,
  onBackToMain,
  selectedStock = 'MSFT',
  authToken,
  authExpiresAtUtc,
  authUserId,
  authUsername,
  onUnauthorized,
  onNotify,
  onVisibleRangeChange,
  onPeriodTypeChange,
  onAnalysisDateModeChange,
  disableMinHeight = false,
  prefsScope = 'Charts',
  dataScope,
  visibilityScope,
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  const [selectedPeriodType, setSelectedPeriodType] = useState<'Annual Data' | 'Quarterly Data' | 'TTM'>('Annual Data');
  const [showNoTooltipNormalizedFields, setShowNoTooltipNormalizedFields] = useState(true);
  const [chartMasked, setChartMasked] = useState(true);
  const pendingChartRevealRef = useRef<string | null>(null);
  const isChartsScope = prefsScope === 'Charts' || prefsScope === 'Charts fpm';
  const [chartsHydrated, setChartsHydrated] = useState(() => !isChartsScope);
  const [elementsReadySymbol, setElementsReadySymbol] = useState<string | null>(null);
  const [visibilitySuppressed, setVisibilitySuppressed] = useState(true);
  const showChartsInlineAttribution = !onVisibleRangeChange;
  const isSymbolReadyForRenderRef = useRef(false);
  const toggleMaskRafRef = useRef<number | null>(null);
  const toggleMaskRaf2Ref = useRef<number | null>(null);
  const visibilityKeyRef = useRef<string | null>(null);
  const normalizedUsername = (authUsername ?? '').trim().toLowerCase();
  const hiddenAnalysisElementIds = React.useMemo<string[]>(
    () => [],
    []
  );
  const hiddenAnalysisCategoryItemKeys = React.useMemo(
    () => ['analysis:pe', 'analysis:flat-eps-ttm', 'analysis-ocf:p-ocf-ratio', 'analysis-ocf:flat-ocf-ttm'],
    []
  );
  const hiddenAnalysisElementIdSet = React.useMemo(
    () => new Set(hiddenAnalysisElementIds),
    [hiddenAnalysisElementIds]
  );
  const isRestrictedAnalysisUser =
    normalizedUsername === 'garp_cuorse' || normalizedUsername === 'garp_course';

  const clampRestrictedVisibility = React.useCallback(
    (visibility: Record<string, boolean>): Record<string, boolean> => {
      if (!isRestrictedAnalysisUser) return visibility;
      const next = { ...visibility };
      hiddenAnalysisElementIds.forEach((id) => {
        next[id] = false;
      });
      return next;
    },
    [hiddenAnalysisElementIds, isRestrictedAnalysisUser]
  );

  useEffect(() => {
    if (!isChartsScope) {
      setChartsHydrated(true);
    }
  }, [isChartsScope]);

  const loadRequestIdRef = useRef(0);
  const loadRetryCountRef = useRef<Record<string, number>>({});
  const hadAuthTokenRef = useRef<boolean>(typeof authToken === 'string' && authToken.trim().length > 0);
  const [authLoadKick, setAuthLoadKick] = useState(0);
  const roicRequestIdRef = useRef(0);
  const ratioSeriesLoadedSymbolRef = useRef<string | null>(null);
  const loadedSymbolRef = useRef<string | null>(null);
  const [isSwitchingSymbol, setIsSwitchingSymbol] = useState(false);

  // Analysis series X-date alignment:
  // - as-reported: fiscal period end (current behavior)
  // - at-reported: earnings date (report date)
  const [analysisDateModeEps, setAnalysisDateModeEps] = useState<FinancialDateMode>('as-reported');
  const [analysisDateModeOcf, setAnalysisDateModeOcf] = useState<FinancialDateMode>('as-reported');

  useEffect(() => {
    const hasToken = typeof authToken === 'string' && authToken.trim().length > 0;
    if (hasToken && !hadAuthTokenRef.current) {
      hadAuthTokenRef.current = true;
      setAuthLoadKick((v) => v + 1);
      return;
    }
    if (!hasToken) {
      hadAuthTokenRef.current = false;
    }
  }, [authToken]);

  useEffect(() => {
    // Debug: confirm chart screen renders and preferences propagate.
    // eslint-disable-next-line no-console
    console.log('AdvancedChartScreen render', { selectedStock, prefsScope });
  }, [selectedStock, prefsScope]);

  const toUTCDateKey = (value: Date | string): string => {
    const d = value instanceof Date ? value : new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const dateKeyToUTCDate = (dateKey: string, endOfDay: boolean): Date => {
    // Use explicit UTC timestamps to avoid timezone shifts.
    return new Date(`${dateKey}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`);
  };

  const getFiscalAnchorDate = (
    row: { date: string; filingDate?: string; periodEndDate?: string; earningDate?: string },
    mode: FinancialDateMode
  ): Date => {
    const periodEnd = row.periodEndDate || row.date;
    const periodKey = periodEnd ? periodEnd.slice(0, 10) : '';
    if (mode === 'at-reported') {
      const preferred = row.filingDate || row.earningDate || (periodKey ? quarterlyFilingDates.get(periodKey) : undefined);
      const altKey = row.date ? row.date.slice(0, 10) : '';
      const fallback = periodKey
        ? quarterlyFilingDates.get(periodKey) || (altKey && altKey !== periodKey ? quarterlyFilingDates.get(altKey) : undefined)
        : (altKey ? quarterlyFilingDates.get(altKey) : undefined);
      const findClosestFilingDate = (key: string): string | undefined => {
        if (!key || quarterlyFilingDates.size === 0) return undefined;
        const targetMs = Date.parse(key);
        if (!Number.isFinite(targetMs)) return undefined;

        let bestKey: string | null = null;
        let bestDiff = Number.POSITIVE_INFINITY;
        quarterlyFilingDates.forEach((_value, existingKey) => {
          const existingMs = Date.parse(existingKey);
          if (!Number.isFinite(existingMs)) return;
          const diff = Math.abs(existingMs - targetMs);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestKey = existingKey;
          }
        });
        if (!bestKey) return undefined;

        // Only accept close matches (about 1.5 months) to avoid mismatches.
        const maxDiffMs = 46 * 24 * 60 * 60 * 1000;
        if (bestDiff > maxDiffMs) return undefined;
        return quarterlyFilingDates.get(bestKey);
      };

      const closest = !fallback
        ? (findClosestFilingDate(periodKey) || (altKey && altKey !== periodKey ? findClosestFilingDate(altKey) : undefined))
        : undefined;

      const raw = preferred || fallback || closest || row.date;
      return new Date(raw);
    }
    return new Date(periodEnd);
  };

  const shiftAtReportedDate = (date: Date, mode: FinancialDateMode): Date => {
    if (mode !== 'at-reported') return date;
    return new Date(date.getTime() - 3 * 86400000);
  };
  
  // Chart resize state - using vh for responsive height
  const initialChartHeight = prefsScope === 'Back Testing' ? 52 : 55;
  const [chartHeight, setChartHeight] = useState(initialChartHeight); // vh to fit everything on screen (leave room for X-axis labels)
  const [chartWidth, setChartWidth] = useState<number | null>(null); // null means 100%
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<'vertical' | 'horizontal' | 'both'>('vertical');
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 });
  const [sizeStart, setSizeStart] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State for ROIC data (loaded from API)
  const [roicData, setRoicData] = useState<number[]>([]);
  const [roicPoints, setRoicPoints] = useState<Array<{ dateKey: string; value: number }>>([]);
  const [roicSource, setRoicSource] = useState<'fiscal' | 'fmp' | null>(null);
  const [keyMetricsSeries, setKeyMetricsSeries] = useState<{
    dates: Date[];
    pfcf: Array<number | null>;
    pocf: Array<number | null>;
    pocfSourceField: Array<string | null>;
    ocfPerShare: Array<number | null>;
    fcfPerShare: Array<number | null>;
  }>({ dates: [], pfcf: [], pocf: [], pocfSourceField: [], ocfPerShare: [], fcfPerShare: [] });

  // TTM Income Statement series (AS-IS) for charting (when using real API)
  const [incomeTtmSeries, setIncomeTtmSeries] = useState<IncomeStatement[]>([]);
  // TTM Cash Flow series (AS-IS) for charting (when using real API)
  const [cashFlowTtmSeries, setCashFlowTtmSeries] = useState<CashFlowStatement[]>([]);
  const [fiscalIncomeAnnual, setFiscalIncomeAnnual] = useState<FiscalIncomeRow[]>([]);
  const [fiscalIncomeQuarter, setFiscalIncomeQuarter] = useState<FiscalIncomeRow[]>([]);
  const [fmpIncomeQuarter, setFmpIncomeQuarter] = useState<IncomeStatement[]>([]);
  const [fiscalBalanceAnnual, setFiscalBalanceAnnual] = useState<BalanceSheet[]>([]);
  const [fiscalBalanceQuarter, setFiscalBalanceQuarter] = useState<BalanceSheet[]>([]);
  const [fiscalCashAnnual, setFiscalCashAnnual] = useState<FiscalCashRow[]>([]);
  const [fiscalCashQuarter, setFiscalCashQuarter] = useState<FiscalCashRow[]>([]);
  const [fiscalPricePoints, setFiscalPricePoints] = useState<Array<{ date: Date; close: number }>>([]);
  const [fmpPricePoints, setFmpPricePoints] = useState<Array<{ date: Date; close: number }>>([]);
  // Maps fiscal period-end date string (YYYY-MM-DD) → filingDate string, used to delay
  // OCF/FCF-per-share anchors until the quarter is actually publicly known.
  const [quarterlyFilingDates, setQuarterlyFilingDates] = useState<Map<string, string>>(new Map());
  const [fmpFiscalYearEndKeys, setFmpFiscalYearEndKeys] = useState<string[]>([]);
  const resolvedDataScope = prefsScope === 'Charts' ? 'Charts' : (dataScope ?? prefsScope);
  const prefersFiscal = resolvedDataScope === 'Charts';

  useEffect(() => {
    // Debug: confirm At Reported toggles and available fiscal dates.
    // eslint-disable-next-line no-console
    console.log('Analysis date mode', {
      selectedStock,
      analysisDateModeEps,
      analysisDateModeOcf,
      prefersFiscal,
      fiscalIncomeQuarterCount: fiscalIncomeQuarter.length,
      fiscalCashQuarterCount: fiscalCashQuarter.length,
    });

    if (prefersFiscal && fiscalIncomeQuarter.length > 0) {
      const sample = fiscalIncomeQuarter.slice(0, 4).map((row) => ({
        date: row.date,
        periodEndDate: row.periodEndDate,
        filingDate: row.filingDate,
      }));
      // eslint-disable-next-line no-console
      console.log('Fiscal income sample', sample);
    }

    if (prefersFiscal && fiscalCashQuarter.length > 0) {
      const sample = fiscalCashQuarter.slice(0, 4).map((row) => ({
        date: row.date,
        periodEndDate: row.periodEndDate,
        filingDate: row.filingDate,
      }));
      // eslint-disable-next-line no-console
      console.log('Fiscal cash sample', sample);
    }
  }, [analysisDateModeEps, analysisDateModeOcf, prefersFiscal, selectedStock, fiscalIncomeQuarter, fiscalCashQuarter]);
  
  const getDefaultElementVisibility = (scope: 'Charts' | 'Back Testing' | 'Charts fpm'): Record<string, boolean> => ({
    ...clampRestrictedVisibility({
      ...DEFAULT_ELEMENT_VISIBILITY,
    }),
  });

  const defaultElementVisibility = getDefaultElementVisibility(prefsScope);
  const defaultOrderCounter = defaultElementVisibility.revenue ? 3 : 2;

  // Counter for tracking order in which elements are made visible
  const [orderCounter, setOrderCounter] = useState(defaultOrderCounter);

  const [navigatorInsets, setNavigatorInsets] = useState<{ leftInsetPx: number; rightInsetPx: number } | null>(null);
  const [jumpPulseKey, setJumpPulseKey] = useState<string | null>(null);
  const [jumpDebugText, setJumpDebugText] = useState<string>('');
  const [quarterEndDebugText, setQuarterEndDebugText] = useState<string>('');
  const jumpPulseTimeoutRef = useRef<number | null>(null);

  const hasFmpApiKey = typeof config.api?.apiKey === 'string' && config.api.apiKey.trim().length > 0;
  const chartsDebug =
    typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.REACT_APP_CHARTS_DEBUG === 'true';
  const showChartOverlayDebug = false;

  useEffect(() => {
    if (!chartsDebug || !dataLoaded) {
      setQuarterEndDebugText('');
      return;
    }
    if (!fiscalIncomeQuarter.length) {
      setQuarterEndDebugText('quarterEnds: n/a');
      return;
    }

    const formatQuarter = (key: string): string => {
      const parts = key.split('-');
      if (parts.length < 2) return key;
      const month = Number(parts[1]);
      if (!Number.isFinite(month)) return key;
      const quarter = Math.floor((month - 1) / 3) + 1;
      return `${key} (Q${quarter})`;
    };

    // In At Reported mode the Flat EPS (ttm) anchors use the Fiscal earningDate.
    // A dedicated effect (declared after flatEPSTTMData) renders the detailed log
    // for this mode (quarterly EPS + effective TTM at each report date), so skip
    // here to avoid overwriting it.
    if (analysisDateModeEps === 'at-reported') {
      return;
    }

    const quarterEndKeys = fiscalIncomeQuarter
      .map((row) => String(row.periodEndDate || row.date || '').slice(0, 10))
      .filter((key) => /^\d{4}-\d{2}-\d{2}$/.test(key));

    const uniqueQuarterEnds = Array.from(new Set(quarterEndKeys)).sort();
    const preview = uniqueQuarterEnds.slice(-8).map(formatQuarter).join(', ');
    setQuarterEndDebugText(`quarterEnds(last8)=${preview || 'n/a'}`);
  }, [chartsDebug, dataLoaded, fiscalIncomeQuarter, analysisDateModeEps]);

  useEffect(() => {
    if (!chartsDebug || !dataLoaded) return;
    if (!fiscalIncomeQuarter.length) return;

    const quarterEndKeys = fiscalIncomeQuarter
      .map((row) => String(row.periodEndDate || row.date || '').slice(0, 10))
      .filter((key) => /^\d{4}-\d{2}-\d{2}$/.test(key));

    const uniqueQuarterEnds = Array.from(new Set(quarterEndKeys)).sort();

    // eslint-disable-next-line no-console
    console.log('QuarterEnds', uniqueQuarterEnds.join(', '));
  }, [chartsDebug, dataLoaded, fiscalIncomeQuarter]);
  const fiscalIncomeAvailability = React.useMemo(() => {
    const empty = {
      revenue: false,
      grossProfit: false,
      operatingIncome: false,
      pretaxIncome: false,
      netIncome: false,
      epsDiluted: false,
    };

    if (!prefersFiscal) return empty;
    const rows = selectedPeriodType === 'Annual Data'
      ? fiscalIncomeAnnual
      : fiscalIncomeQuarter;
    if (!rows.length) return empty;

    return {
      revenue: rows.some((row) => Number.isFinite(Number(row.revenue))),
      grossProfit: rows.some((row) => Number.isFinite(Number(row.grossProfit))),
      operatingIncome: rows.some((row) => Number.isFinite(Number(row.operatingIncome))),
      pretaxIncome: rows.some((row) => Number.isFinite(Number(row.incomeBeforeTax))),
      netIncome: rows.some((row) => Number.isFinite(Number(row.netIncome))),
      epsDiluted: rows.some((row) => Number.isFinite(Number(row.epsDiluted))),
    };
  }, [prefsScope, selectedPeriodType, fiscalIncomeAnnual, fiscalIncomeQuarter]);

  const fiscalBalanceAvailability = React.useMemo(() => {
    const empty = {
      cashAndCashEquivalents: false,
      totalAssets: false,
      shortTermDebt: false,
      longTermDebt: false,
      totalLiabilities: false,
      totalStockholdersEquity: false,
    };

    if (!prefersFiscal) return empty;
    const rows = selectedPeriodType === 'Annual Data'
      ? fiscalBalanceAnnual
      : fiscalBalanceQuarter;
    if (!rows.length) return empty;

    return {
      cashAndCashEquivalents: rows.some((row) => Number.isFinite(Number(row.cashAndCashEquivalents))),
      totalAssets: rows.some((row) => Number.isFinite(Number(row.totalAssets))),
      shortTermDebt: rows.some((row) => Number.isFinite(Number(row.shortTermDebt))),
      longTermDebt: rows.some((row) => Number.isFinite(Number(row.longTermDebt))),
      totalLiabilities: rows.some((row) => Number.isFinite(Number(row.totalLiabilities))),
      totalStockholdersEquity: rows.some((row) => Number.isFinite(Number(row.totalStockholdersEquity))),
    };
  }, [prefsScope, selectedPeriodType, fiscalBalanceAnnual, fiscalBalanceQuarter]);

  const fiscalCashAvailability = React.useMemo(() => {
    const empty = {
      operatingCashFlow: false,
      freeCashFlow: false,
      dividends: false,
      buybacks: false,
    };

    if (!prefersFiscal) return empty;
    const rows = selectedPeriodType === 'Annual Data'
      ? fiscalCashAnnual
      : fiscalCashQuarter;
    if (!rows.length) return empty;

    const hasFreeCashFlow = rows.some((row) => {
      const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
      const capex = Number(row.capitalExpenditure ?? row.investmentsInPropertyPlantAndEquipment);
      return Number.isFinite(ocf) && Number.isFinite(capex);
    });

    return {
      operatingCashFlow: rows.some((row) => Number.isFinite(Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities))),
      freeCashFlow: hasFreeCashFlow,
      dividends: rows.some((row) => Number.isFinite(Number(row.dividendsPaid ?? row.commonDividendsPaid))),
      buybacks: rows.some((row) => Number.isFinite(Number(row.commonStockRepurchased))),
    };
  }, [prefsScope, selectedPeriodType, fiscalCashAnnual, fiscalCashQuarter]);

  const fiscalOcfPerShareRollingTtm = React.useMemo(() => {
    if (!prefersFiscal) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    if (!fiscalCashQuarter.length || !fiscalIncomeQuarter.length) {
      return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    }

    const incomeByDateKey = new Map(
      fiscalIncomeQuarter.map((row) => [toUTCDateKey(row.date), row])
    );

    const rows = fiscalCashQuarter
      .map((row) => {
        const dateKey = toUTCDateKey(row.date);
        const income = incomeByDateKey.get(dateKey);
        const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
        const netIncome = Number(income?.netIncome);
        const eps = Number(income?.eps ?? income?.epsDiluted);
        if (!Number.isFinite(ocf) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) return null;
        return { date: getFiscalAnchorDate(row, analysisDateModeOcf), ocf, netIncome, eps };
      })
      .filter((row): row is { date: Date; ocf: number; netIncome: number; eps: number } => !!row)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (rows.length < 4) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    const out: Array<{ date: Date; value: number; sourceField?: string | null }> = [];
    for (let i = 3; i < rows.length; i += 1) {
      let ocfSum = 0;
      let netIncomeSum = 0;
      let epsSum = 0;
      for (let j = i - 3; j <= i; j += 1) {
        ocfSum += rows[j].ocf;
        netIncomeSum += rows[j].netIncome;
        epsSum += rows[j].eps;
      }
      if (!Number.isFinite(ocfSum) || !Number.isFinite(netIncomeSum) || !Number.isFinite(epsSum) || epsSum === 0) {
        continue;
      }
      const shares = netIncomeSum / epsSum;
      if (!Number.isFinite(shares) || shares <= 0) continue;
      const ocfPerShare = ocfSum / shares;
      if (!Number.isFinite(ocfPerShare)) continue;
      out.push({ date: rows[i].date, value: ocfPerShare, sourceField: 'fiscalQuarterlyRollingTtmDerived' });
    }

    return out;
  }, [analysisDateModeOcf, prefersFiscal, fiscalCashQuarter, fiscalIncomeQuarter, quarterlyFilingDates]);

  const fiscalOcfPerShareAnnual = React.useMemo(() => {
    if (!prefersFiscal) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    if (!fiscalCashAnnual.length || !fiscalIncomeAnnual.length) {
      return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    }

    const incomeByDateKey = new Map(
      fiscalIncomeAnnual.map((row) => [toUTCDateKey(row.date), row])
    );

    return fiscalCashAnnual
      .map((row) => {
        const dateKey = toUTCDateKey(row.date);
        const income = incomeByDateKey.get(dateKey);
        const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
        const netIncome = Number(income?.netIncome);
        const eps = Number(income?.eps ?? income?.epsDiluted);
        if (!Number.isFinite(ocf) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) return null;
        const shares = netIncome / eps;
        if (!Number.isFinite(shares) || shares <= 0) return null;
        const ocfPerShare = ocf / shares;
        if (!Number.isFinite(ocfPerShare)) return null;
        return { date: getFiscalAnchorDate(row, analysisDateModeOcf), value: ocfPerShare, sourceField: 'fiscalAnnualDerived' };
      })
      .filter((row): row is { date: Date; value: number; sourceField: string } => !!row)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [analysisDateModeOcf, prefersFiscal, fiscalCashAnnual, fiscalIncomeAnnual, quarterlyFilingDates]);

  const fiscalOcfPerShareQuarter = React.useMemo(() => {
    if (!prefersFiscal) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    if (!fiscalCashQuarter.length || !fiscalIncomeQuarter.length) {
      return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    }

    const incomeByDateKey = new Map(
      fiscalIncomeQuarter.map((row) => [toUTCDateKey(row.date), row])
    );

    return fiscalCashQuarter
      .map((row) => {
        const dateKey = toUTCDateKey(row.date);
        const income = incomeByDateKey.get(dateKey);
        const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
        const netIncome = Number(income?.netIncome);
        const eps = Number(income?.eps ?? income?.epsDiluted);
        if (!Number.isFinite(ocf) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) return null;
        const shares = netIncome / eps;
        if (!Number.isFinite(shares) || shares <= 0) return null;
        const ocfPerShare = ocf / shares;
        if (!Number.isFinite(ocfPerShare)) return null;
        return { date: getFiscalAnchorDate(row, analysisDateModeOcf), value: ocfPerShare, sourceField: 'fiscalQuarterlyDerived' };
      })
      .filter((row): row is { date: Date; value: number; sourceField: string } => !!row)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [analysisDateModeOcf, prefersFiscal, fiscalCashQuarter, fiscalIncomeQuarter, quarterlyFilingDates]);

  const fiscalOcfAvailable = React.useMemo(() => {
    if (!prefersFiscal) return false;
    if (selectedPeriodType === 'TTM') return fiscalOcfPerShareRollingTtm.length > 0;
    if (selectedPeriodType === 'Annual Data') return fiscalOcfPerShareAnnual.length > 0;
    return fiscalOcfPerShareQuarter.length > 0;
  }, [prefersFiscal, selectedPeriodType, fiscalOcfPerShareRollingTtm.length, fiscalOcfPerShareAnnual.length, fiscalOcfPerShareQuarter.length]);

  const fiscalFcfPerShareRollingTtm = React.useMemo(() => {
    if (!prefersFiscal) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    if (!fiscalCashQuarter.length || !fiscalIncomeQuarter.length) {
      return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    }

    const incomeByDateKey = new Map(
      fiscalIncomeQuarter.map((row) => [toUTCDateKey(row.date), row])
    );

    const rows = fiscalCashQuarter
      .map((row) => {
        const dateKey = toUTCDateKey(row.date);
        const income = incomeByDateKey.get(dateKey);
        const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
        const capex = Number(row.capitalExpenditure ?? row.investmentsInPropertyPlantAndEquipment);
        const netIncome = Number(income?.netIncome);
        const eps = Number(income?.eps ?? income?.epsDiluted);
        if (!Number.isFinite(ocf) || !Number.isFinite(capex) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) {
          return null;
        }
        const normalizedCapex = capex > 0 ? -capex : capex;
        const fcf = ocf + normalizedCapex;
        return { date: getFiscalAnchorDate(row, analysisDateModeOcf), fcf, netIncome, eps };
      })
      .filter((row): row is { date: Date; fcf: number; netIncome: number; eps: number } => !!row)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (rows.length < 4) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    const out: Array<{ date: Date; value: number; sourceField?: string | null }> = [];
    for (let i = 3; i < rows.length; i += 1) {
      let fcfSum = 0;
      let netIncomeSum = 0;
      let epsSum = 0;
      for (let j = i - 3; j <= i; j += 1) {
        fcfSum += rows[j].fcf;
        netIncomeSum += rows[j].netIncome;
        epsSum += rows[j].eps;
      }
      if (!Number.isFinite(fcfSum) || !Number.isFinite(netIncomeSum) || !Number.isFinite(epsSum) || epsSum === 0) {
        continue;
      }
      const shares = netIncomeSum / epsSum;
      if (!Number.isFinite(shares) || shares <= 0) continue;
      const fcfPerShare = fcfSum / shares;
      if (!Number.isFinite(fcfPerShare)) continue;
      out.push({ date: rows[i].date, value: fcfPerShare, sourceField: 'fiscalQuarterlyRollingTtmDerived' });
    }

    return out;
  }, [analysisDateModeOcf, prefersFiscal, fiscalCashQuarter, fiscalIncomeQuarter, quarterlyFilingDates]);

  const fiscalPeAvailable = React.useMemo(() => {
    if (!prefersFiscal) return false;
    if (!fiscalIncomeQuarter.length) return false;
    return fiscalIncomeQuarter.some((row) => Number.isFinite(Number(row.eps ?? row.epsDiluted)));
  }, [analysisDateModeEps, prefersFiscal, fiscalIncomeQuarter, quarterlyFilingDates]);

  const fiscalFlatEpsTtmPoints = useMemo(() => {
    if (!prefersFiscal) return [] as Array<{ date: Date; value: number }>;
    if (!fiscalIncomeQuarter.length) return [] as Array<{ date: Date; value: number }>;

    const rows = fiscalIncomeQuarter
      .map((row) => ({
        date: shiftAtReportedDate(getFiscalAnchorDate(row, analysisDateModeEps), analysisDateModeEps),
        eps: Number(row.eps ?? row.epsDiluted),
      }))
      .filter((row): row is { date: Date; eps: number } =>
        !!row.date && Number.isFinite(row.date.getTime()) && Number.isFinite(row.eps)
      )
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (rows.length < 4) return [] as Array<{ date: Date; value: number }>;

    const out: Array<{ date: Date; value: number }> = [];
    for (let i = 3; i < rows.length; i += 1) {
      const sum = rows[i - 3].eps + rows[i - 2].eps + rows[i - 1].eps + rows[i].eps;
      if (!Number.isFinite(sum)) continue;
      out.push({ date: rows[i].date, value: sum });
    }

    return out;
  }, [analysisDateModeEps, prefersFiscal, fiscalIncomeQuarter, quarterlyFilingDates, selectedPeriodType]);

  const chartsUseFiscalSource = React.useMemo(() => {
    return prefersFiscal && (
      Object.values(fiscalIncomeAvailability).some(Boolean) ||
      Object.values(fiscalBalanceAvailability).some(Boolean) ||
      Object.values(fiscalCashAvailability).some(Boolean) ||
      fiscalFlatEpsTtmPoints.length > 0 ||
      roicSource === 'fiscal'
    );
  }, [prefersFiscal, fiscalIncomeAvailability, fiscalBalanceAvailability, fiscalCashAvailability, fiscalFlatEpsTtmPoints.length, roicSource]);

  const chartSourceLabelByElementId = React.useMemo(() => {
    if (!isAdmin) return {} as Record<string, string>;
    const baseLabel = chartsUseFiscalSource
      ? 'fiscal.ai'
      : (resolvedDataScope === 'Charts fpm' ? 'API FPM' : 'API FMP');
    const map: Record<string, string> = {};
    const ids = [
      'price-line',
      'revenue',
      'gross-profit',
      'operating-income',
      'pretax-income',
      'net-income',
      'eps-diluted',
      'cash-equivalents',
      'total-assets',
      'short-term-debt',
      'long-term-debt',
      'total-liabilities',
      'shareholders-equity',
      'operating-cash-flow',
      'free-cash-flow',
      'dividends',
      'buybacks',
      'pe',
      'p-ocf-ratio',
      'p-fcf-ratio',
      'gross-profit-margin',
      'operating-margin',
      'net-margin',
      'operating-cf-margin',
      'free-cf-margin',
      'operating-cf-to-ni',
      'free-cf-to-ni',
      'roic',
      'flat-eps-ttm',
      'fair-price',
      'entry-point-1',
      'entry-point-2',
      'flat-ocf-ttm',
      'fair-price-ocf',
      'entry-point-1-ocf',
      'entry-point-2-ocf',
    ];
    ids.forEach((id) => {
      map[id] = baseLabel;
    });
    if (prefersFiscal && fiscalPricePoints.length > 0) {
      map['price-line'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.revenue) {
      map.revenue = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.grossProfit) {
      map['gross-profit'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.revenue && fiscalIncomeAvailability.grossProfit) {
      map['gross-profit-margin'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.operatingIncome) {
      map['operating-income'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.revenue && fiscalIncomeAvailability.operatingIncome) {
      map['operating-margin'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.pretaxIncome) {
      map['pretax-income'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.netIncome) {
      map['net-income'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.revenue && fiscalIncomeAvailability.netIncome) {
      map['net-margin'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.epsDiluted) {
      map['eps-diluted'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalFlatEpsTtmPoints.length > 0) {
      map['flat-eps-ttm'] = 'fiscal.ai';
      map['fair-price'] = 'fiscal.ai';
      map['entry-point-1'] = 'fiscal.ai';
      map['entry-point-2'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalBalanceAvailability.cashAndCashEquivalents) {
      map['cash-equivalents'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalBalanceAvailability.totalAssets) {
      map['total-assets'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalBalanceAvailability.shortTermDebt) {
      map['short-term-debt'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalBalanceAvailability.longTermDebt) {
      map['long-term-debt'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalBalanceAvailability.totalLiabilities) {
      map['total-liabilities'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalBalanceAvailability.totalStockholdersEquity) {
      map['shareholders-equity'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalCashAvailability.operatingCashFlow) {
      map['operating-cash-flow'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.revenue && fiscalCashAvailability.operatingCashFlow) {
      map['operating-cf-margin'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.netIncome && fiscalCashAvailability.operatingCashFlow) {
      map['operating-cf-to-ni'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalCashAvailability.freeCashFlow) {
      map['free-cash-flow'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.revenue && fiscalCashAvailability.freeCashFlow) {
      map['free-cf-margin'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalIncomeAvailability.netIncome && fiscalCashAvailability.freeCashFlow) {
      map['free-cf-to-ni'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalCashAvailability.dividends) {
      map['dividends'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalCashAvailability.buybacks) {
      map['buybacks'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalPeAvailable) {
      map['pe'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalOcfPerShareRollingTtm.length > 0) {
      map['p-ocf-ratio'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalOcfAvailable) {
      map['flat-ocf-ttm'] = 'fiscal.ai';
      map['fair-price-ocf'] = 'fiscal.ai';
      map['entry-point-1-ocf'] = 'fiscal.ai';
      map['entry-point-2-ocf'] = 'fiscal.ai';
    }
    if (prefersFiscal && fiscalFcfPerShareRollingTtm.length > 0) {
      map['p-fcf-ratio'] = 'fiscal.ai';
    }
    if (prefersFiscal && roicSource === 'fiscal') {
      map.roic = 'fiscal.ai';
    }
    return map;
  }, [isAdmin, chartsUseFiscalSource, resolvedDataScope, fiscalIncomeAvailability, fiscalBalanceAvailability, fiscalCashAvailability, fiscalPeAvailable, fiscalOcfPerShareRollingTtm.length, fiscalFcfPerShareRollingTtm.length, fiscalOcfAvailable, roicSource, fiscalFlatEpsTtmPoints.length, fiscalPricePoints.length]);

  useEffect(() => {
    return () => {
      if (jumpPulseTimeoutRef.current != null) {
        window.clearTimeout(jumpPulseTimeoutRef.current);
      }
    };
  }, []);

  const triggerJumpPulse = (key: string) => {
    setJumpPulseKey(key);
    if (jumpPulseTimeoutRef.current != null) {
      window.clearTimeout(jumpPulseTimeoutRef.current);
    }
    jumpPulseTimeoutRef.current = window.setTimeout(() => {
      setJumpPulseKey(null);
    }, 220);
  };

  
  // Load data on mount or when symbol changes
  useEffect(() => {
    let cancelled = false;
    const requestId = ++loadRequestIdRef.current;
    const MAX_LOAD_RETRIES = 3;
    const MAX_NO_AUTH_RETRIES = 2;

    loadRetryCountRef.current[selectedStock] = 0;

    setDataLoaded(false);
    setDataLoadError(null);
    setRoicData([]);
    setRoicPoints([]);
    setRoicSource(null);
    setKeyMetricsSeries({ dates: [], pfcf: [], pocf: [], pocfSourceField: [], ocfPerShare: [], fcfPerShare: [] });
    setIncomeTtmSeries([]);
    setCashFlowTtmSeries([]);
    setFiscalIncomeAnnual([]);
    setFiscalIncomeQuarter([]);
    setFiscalBalanceAnnual([]);
    setFiscalBalanceQuarter([]);
    setFiscalCashAnnual([]);
    setFiscalCashQuarter([]);
    setFiscalPricePoints([]);
    setFmpPricePoints([]);
    setQuarterlyFilingDates(new Map());
    setFmpFiscalYearEndKeys([]);
    setElements(prev => prev.map(element => ({ ...element, data: [] })));
    setElementsReadySymbol(null);
    ratioSeriesLoadedSymbolRef.current = null;
    loadedSymbolRef.current = null;

    const runLoad = async () => {
      const result = await loadStockData(selectedStock, authToken, authExpiresAtUtc);
      if (cancelled) return;
      if (requestId !== loadRequestIdRef.current) return;

      const retries = loadRetryCountRef.current[selectedStock] ?? 0;

      const scheduleRetry = (reason?: 'no-auth' | 'no-prices' | 'api-failed') => {
        const delayMs = reason === 'no-auth'
          ? (350 + retries * 450)
          : (retries < 2 ? (900 + retries * 600) : 3000);
        loadRetryCountRef.current[selectedStock] = retries + 1;
        window.setTimeout(() => {
          if (cancelled) return;
          if (requestId !== loadRequestIdRef.current) return;
          void runLoad();
        }, delayMs);
      };

      const showTerminalLoadError = (reason?: 'no-auth' | 'no-prices' | 'api-failed') => {
        if (reason === 'no-auth') {
          console.warn('Charts load: missing auth token for backend proxy.');
          return;
        }
        if (reason === 'no-prices') {
          console.warn(`Charts load: no price points returned for ${selectedStock}.`);
          return;
        }
        console.warn(`Charts load failed for ${selectedStock}.`);
      };

      if (!result.ok) {
        if (result.reason === 'no-auth') {
          if (retries >= MAX_NO_AUTH_RETRIES) {
            setDataLoaded(true);
            loadedSymbolRef.current = selectedStock;
            setDataLoadError(null);
            showTerminalLoadError(result.reason);
            return;
          }
          scheduleRetry(result.reason);
          setDataLoaded(false);
          setDataLoadError(null);
          return;
        }

        if (retries >= MAX_LOAD_RETRIES) {
          setDataLoaded(true);
          loadedSymbolRef.current = selectedStock;
          setDataLoadError(null);
          showTerminalLoadError(result.reason);
          return;
        }
        scheduleRetry(result.reason);
        setDataLoaded(false);
        // Keep retrying silently; transient API/auth race conditions usually recover.
        setDataLoadError(null);
        return;
      }

      const loadedSymbol = mockStockData.find((stock) => stock.symbol === selectedStock);
      const hasPrices = (loadedSymbol?.data?.length ?? 0) > 0;
      if (!hasPrices) {
        if (retries >= MAX_LOAD_RETRIES) {
          setDataLoaded(true);
          loadedSymbolRef.current = selectedStock;
          setDataLoadError(null);
          console.warn(`Charts load: no cached prices for ${selectedStock} after retries.`);
          return;
        }
        scheduleRetry();
        return;
      }

      loadedSymbolRef.current = selectedStock;
      setDataLoaded(true);
    };
    void runLoad();

    return () => {
      cancelled = true;
    };
  }, [selectedStock, hasFmpApiKey, authLoadKick]);
  
  // Load ROIC + valuation ratios from Key Metrics API
  useEffect(() => {
    if (!dataLoaded) return;
    let cancelled = false;
    const requestId = ++roicRequestIdRef.current;

    const isStale = () => cancelled || requestId !== roicRequestIdRef.current;
    
    const loadROICData = async () => {
      try {
        const period = selectedPeriodType === 'Annual Data' ? 'annual' : 'quarter';
        const limit = 30; // Get enough historical data
        let fiscalRoicPoints: Array<{ dateKey: string; value: number; periodType: 'annual' | 'quarter' | 'ltm' | 'other' }> = [];
        let fiscalRoicRows: Array<{ date: string; periodType: string; value?: number }> = [];
        if (prefersFiscal) {
          const fiscalRows = await fetchFiscalRoicRatios(selectedStock);
          if (isStale()) return;
          fiscalRoicRows = fiscalRows;

          const targetPeriod: 'annual' | 'quarter' | 'ltm' = selectedPeriodType === 'Annual Data'
            ? 'annual'
            : selectedPeriodType === 'TTM'
              ? 'ltm'
              : 'quarter';
          const filteredFiscal = fiscalRows
            .filter((row) => row.periodType === targetPeriod)
            .map((row) => ({ dateKey: toUTCDateKey(row.date), value: Number(row.value), periodType: row.periodType }))
            .filter((row) => row.dateKey && Number.isFinite(row.value));

          fiscalRoicPoints = filteredFiscal
            .map((row) => {
              let normalized = row.value;
              if (Math.abs(normalized) < 1) {
                normalized *= 100;
              }
              return { dateKey: row.dateKey, value: normalized, periodType: row.periodType };
            })
            .filter((row) => row.value !== 0 && Number.isFinite(row.value));
        }

        const roicMetrics = await fetchKeyMetrics(selectedStock, period, limit);
        if (isStale()) return;

        const chronMetrics = [...(roicMetrics ?? [])]
          .map((km: any) => ({
            dateKey: toUTCDateKey(new Date(km.date)),
            raw: Number(km.returnOnInvestedCapital ?? km.roic),
            investedCapital: Number(km.investedCapital),
          }))
          .filter((row) => row.dateKey && Number.isFinite(row.raw))
          .sort((a, b) => a.dateKey.localeCompare(b.dateKey));

        const fmpRoicPoints = selectedPeriodType === 'TTM'
          ? chronMetrics.slice(3).map((row, index) => {
              const window = chronMetrics.slice(index, index + 4);
              const validWindow = window.filter((item) => Number.isFinite(item.investedCapital) && item.investedCapital > 0);
              const investedCapitalTotal = validWindow.reduce((sum, item) => sum + item.investedCapital, 0);
              const investedCapitalAverage = investedCapitalTotal > 0
                ? investedCapitalTotal / 4
                : 0;
              const nopatWeightedRoic = validWindow.reduce((sum, item) => sum + item.raw * item.investedCapital, 0);
              const ttmRoic = investedCapitalAverage > 0 ? nopatWeightedRoic / investedCapitalAverage : 0;
              return { dateKey: row.dateKey, value: ttmRoic < 1 ? ttmRoic * 100 : ttmRoic };
            }).filter((row) => row.value !== 0 && Number.isFinite(row.value))
          : chronMetrics.map((row) => {
              let normalized = row.raw;
              if (Math.abs(normalized) < 1) {
                normalized *= 100;
              }
              return {
                dateKey: row.dateKey,
                value: normalized,
              };
            }).filter((row) => row.value !== 0 && Number.isFinite(row.value));

        // Keep OCF/FCF ratio anchors stable across Annual/Quarterly switches.
        // These sources are quarter-based and should only refresh when symbol changes.
        if (ratioSeriesLoadedSymbolRef.current !== selectedStock) {
          const ratioMetrics = await fetchKeyMetrics(selectedStock, 'quarter', 120);
          if (isStale()) return;


          // Fetch quarterly statements to get filing/accepted dates per quarter.
          const filingResults = config.useRealAPI
            ? await Promise.allSettled([
                fetchQuarterlyCashFlowStatements(selectedStock, 120),
                fetchIncomeStatement(selectedStock, 'quarter', 120),
                prefersFiscal ? fetchFiscalCompanyFilings(selectedStock) : Promise.resolve([]),
              ])
            : [];
          const quarterlyCFS = filingResults[0]?.status === 'fulfilled' ? filingResults[0].value : [];
          const quarterlyIncome = filingResults[1]?.status === 'fulfilled' ? filingResults[1].value : [];
          const fiscalFilings = filingResults[2]?.status === 'fulfilled' ? filingResults[2].value : [];
          if (isStale()) return;
          setFmpIncomeQuarter(quarterlyIncome);
          // Build period-end-date -> filingDate map so OCF/FCF anchors activate on earnings date.
          const filingDateMap = new Map<string, string>();
          const parseDate = (value?: string | null): number | null => {
            if (!value) return null;
            const dt = new Date(value);
            const ms = dt.getTime();
            return Number.isFinite(ms) ? ms : null;
          };
          const setFilingDate = (periodEndRaw?: string | null, acceptedRaw?: string | null, filingRaw?: string | null) => {
            if (!periodEndRaw) return;
            const key = periodEndRaw.slice(0, 10);
            if (!key) return;
            // FPM at-reported requirement: prefer filingDate over acceptedDate.
            const preferred = resolvedDataScope === 'Charts fpm'
              ? (filingRaw || acceptedRaw || '')
              : (acceptedRaw || filingRaw || '');
            if (!preferred) return;
            const existing = filingDateMap.get(key);
            if (!existing) {
              filingDateMap.set(key, preferred);
              return;
            }
            const existingMs = parseDate(existing);
            const nextMs = parseDate(preferred);
            if (existingMs != null && nextMs != null && nextMs < existingMs) {
              filingDateMap.set(key, preferred);
            }
          };
          for (const row of fiscalFilings) {
            const reportDate = String((row as any)?.reportDate || (row as any)?.report_date || '').trim();
            const filingDate = String((row as any)?.filingDate || (row as any)?.filing_date || '').trim();
            if (!reportDate || !filingDate) continue;
            const key = reportDate.slice(0, 10);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) continue;
            const existing = filingDateMap.get(key);
            if (!existing || parseDate(filingDate) != null && parseDate(existing) != null && parseDate(filingDate)! < parseDate(existing)!) {
              filingDateMap.set(key, filingDate);
            }
          }
          for (const row of quarterlyCFS) {
            setFilingDate(row.date, (row as any).acceptedDate, row.filingDate);
          }
          for (const row of quarterlyIncome) {
            setFilingDate((row as any)?.date, (row as any)?.acceptedDate, (row as any)?.filingDate);
          }

          setQuarterlyFilingDates(filingDateMap);

          const fiscalYearEndKeys = new Set<string>();
          const isYearEndPeriod = (raw?: string | null) => {
            const p = String(raw || '').trim().toUpperCase();
            return p === 'Q4' || p === 'FY';
          };
          for (const row of quarterlyCFS) {
            if (!isYearEndPeriod((row as any)?.period)) continue;
            const key = String((row as any)?.date || '').slice(0, 10);
            if (key) fiscalYearEndKeys.add(key);
          }
          for (const row of quarterlyIncome) {
            if (!isYearEndPeriod((row as any)?.period)) continue;
            const key = String((row as any)?.date || '').slice(0, 10);
            if (key) fiscalYearEndKeys.add(key);
          }
          setFmpFiscalYearEndKeys(Array.from(fiscalYearEndKeys).sort());

          const ratioMetricsSource = ratioMetrics ?? [];
          if (ratioMetricsSource.length > 0) {
            const reversedRatioMetrics = [...ratioMetricsSource].reverse();

            const ratioDates = reversedRatioMetrics.map((km) => new Date(km.date));
            const pickFiniteNumber = (source: any, keys: string[]): number | null => {
              for (const key of keys) {
                const raw = source?.[key];
                if (raw === null || raw === undefined || raw === '') continue;
                const v = typeof raw === 'number' ? raw : Number(raw);
                if (Number.isFinite(v)) return v;
              }
              return null;
            };

            const pickFiniteNumberWithKey = (source: any, keys: string[]): { value: number; key: string } | null => {
              for (const key of keys) {
                const raw = source?.[key];
                if (raw === null || raw === undefined || raw === '') continue;
                const v = typeof raw === 'number' ? raw : Number(raw);
                if (Number.isFinite(v)) return { value: v, key };
              }
              return null;
            };

            const pfcfValues = reversedRatioMetrics.map((km) => {
              const direct = pickFiniteNumber(km as any, [
                'pfcfRatioTTM',
                'pfcfRatio',
                'pfcf',
                'priceToFreeCashFlowRatio',
                'priceToFreeCashFlow',
                'priceToFreeCashFlows',
              ]);
              return typeof direct === 'number' && Number.isFinite(direct) ? direct : null;
            });

            const pocfPicked = reversedRatioMetrics.map((km) => {
              const direct = pickFiniteNumberWithKey(km as any, [
                'pocfRatioTTM',
                'pocfratio',
                'pocfRatio',
                'pocf',
                'priceToOperatingCashFlowRatio',
                'priceToOperatingCashFlow',
                'priceToOperatingCashFlows',
                'priceCashFlowRatio',
                'priceToCashFlowRatio',
              ]);
              if (direct) return direct;

              const yieldValue = Number((km as any)?.operatingCashFlowYield);
              if (Number.isFinite(yieldValue) && yieldValue > 0) {
                return { value: 1 / yieldValue, key: 'operatingCashFlowYield(inverted)' };
              }
              return null;
            });
            const pocfValues = pocfPicked.map((item) => item?.value ?? null);
            const pocfSourceFields = pocfPicked.map((item) => item?.key ?? null);

            const ocfPerShareValues = reversedRatioMetrics.map((km) => {
              const direct = pickFiniteNumber(km as any, [
                'operatingCashFlowPerShareTTM',
                'operatingCashFlowPerShare',
              ]);
              return typeof direct === 'number' && Number.isFinite(direct) ? direct : null;
            });

            const fcfPerShareValues = reversedRatioMetrics.map((km) => {
              const direct = pickFiniteNumber(km as any, [
                'freeCashFlowPerShareTTM',
                'freeCashFlowPerShare',
                'fcfPerShare',
              ]);
              return typeof direct === 'number' && Number.isFinite(direct) ? direct : null;
            });

            const hasRatioValues =
              pfcfValues.some((v) => typeof v === 'number' && Number.isFinite(v)) ||
              pocfValues.some((v) => typeof v === 'number' && Number.isFinite(v));
            if (isStale()) return;
            setKeyMetricsSeries({ dates: ratioDates, pfcf: pfcfValues, pocf: pocfValues, pocfSourceField: pocfSourceFields, ocfPerShare: ocfPerShareValues, fcfPerShare: fcfPerShareValues });
          } else {
            if (isStale()) return;
            setKeyMetricsSeries({ dates: [], pfcf: [], pocf: [], pocfSourceField: [], ocfPerShare: [], fcfPerShare: [] });
          }


          ratioSeriesLoadedSymbolRef.current = selectedStock;
        }

        if (fiscalRoicPoints.length > 0) {
          const fmpByDateKey = new Map(fmpRoicPoints.map((point) => [point.dateKey, point.value]));
          const merged = fiscalRoicPoints.map((point) => {
            // TTM in Charts must reflect Fiscal LTM API values only (no local/FMP override).
            if (selectedPeriodType === 'TTM') return point;
            const fmpValue = fmpByDateKey.get(point.dateKey);
            // Keep fiscal as the source-of-truth when it has a real value.
            // Use FMP only as a fallback for missing/invalid fiscal points.
            const fiscalValue = typeof point.value === 'number' && Number.isFinite(point.value) ? point.value : null;
            if (fiscalValue == null && typeof fmpValue === 'number' && Number.isFinite(fmpValue)) {
              return { ...point, value: fmpValue };
            }
            return point;
          });
          const mergedDateKeys = new Set(merged.map((point) => point.dateKey));
          if (selectedPeriodType !== 'TTM') {
            for (const point of fmpRoicPoints) {
              // FMP fills only missing Annual/Quarterly dates. TTM must remain
              // strictly Fiscal LTM data and must not mix in quarterly points.
              if (!mergedDateKeys.has(point.dateKey)) {
                merged.push({
                  ...point,
                  periodType: selectedPeriodType === 'Annual Data' ? 'annual' : 'quarter',
                });
              }
            }
          }
          merged.sort((a, b) => a.dateKey.localeCompare(b.dateKey));
          setRoicPoints(merged);
          setRoicData(merged.map((point) => point.value));
          setRoicSource('fiscal');
        } else if (fmpRoicPoints.length > 0) {
          if (isStale()) return;
          setRoicPoints(fmpRoicPoints);
          setRoicData(fmpRoicPoints.map((point) => point.value));
          setRoicSource('fmp');
        } else {
          if (isStale()) return;
          setRoicPoints([]);
          setRoicData([]);
          setRoicSource(null);
        }
      } catch (error) {
        console.error('❌ Error loading ROIC data:', error);
        if (isStale()) return;
        setRoicData([]);
        setRoicSource(null);
        if (ratioSeriesLoadedSymbolRef.current !== selectedStock) {
          setKeyMetricsSeries({ dates: [], pfcf: [], pocf: [], pocfSourceField: [], ocfPerShare: [], fcfPerShare: [] });
        }
      }
    };
    
    loadROICData();
    return () => {
      cancelled = true;
    };
  }, [selectedStock, selectedPeriodType, dataLoaded, prefersFiscal]);

  const aaplData = useMemo(() => {
    if (!dataLoaded) return null;
    const data = mockStockData.find(stock => stock.symbol === selectedStock);
    const dedupeByDate = (series: Array<{ date: Date; value: number }>) => {
      const map = new Map<string, { date: Date; value: number }>();
      for (const point of series) {
        const key = point.date.toISOString().slice(0, 10);
        const existing = map.get(key);
        if (!existing || point.value < existing.value) {
          map.set(key, point);
        }
      }
      return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    };

    const fmpSeries = dedupeByDate(calculateFlatEPSTTM(selectedStock, analysisDateModeEps));
    return data;
  }, [dataLoaded, selectedStock]);

  const pfcfSeries = useMemo(() => {
    if (!keyMetricsSeries.dates.length) return [] as { date: Date; value: number | null }[];
    return keyMetricsSeries.dates.map((date, index) => ({
      date,
      value: keyMetricsSeries.pfcf[index] ?? null,
    }));
  }, [keyMetricsSeries]);

  const pocfSeries = useMemo(() => {
    if (!keyMetricsSeries.dates.length) return [] as { date: Date; value: number | null; sourceField: string | null }[];
    return keyMetricsSeries.dates.map((date, index) => ({
      date,
      value: keyMetricsSeries.pocf[index] ?? null,
      sourceField: keyMetricsSeries.pocfSourceField[index] ?? null,
    }));
  }, [keyMetricsSeries]);

  const ocfPerShareSeries = useMemo(() => {
    if (!keyMetricsSeries.dates.length) return [] as { date: Date; value: number | null }[];
    return keyMetricsSeries.dates.map((date, index) => ({
      date,
      value: keyMetricsSeries.ocfPerShare[index] ?? null,
    }));
  }, [keyMetricsSeries]);

  const fcfPerShareSeries = useMemo(() => {
    if (!keyMetricsSeries.dates.length) return [] as { date: Date; value: number | null }[];
    return keyMetricsSeries.dates.map((date, index) => ({
      date,
      value: keyMetricsSeries.fcfPerShare[index] ?? null,
    }));
  }, [keyMetricsSeries]);

  const allPriceDataWithDates = useMemo(() => {
    if (!aaplData) return [];
    if (aaplData.data.length > 0) {
    }
    return aaplData.data;
  }, [aaplData]);

  const dailyPricePoints = useMemo(() => {
    if (!allPriceDataWithDates.length) return [] as { date: Date; close: number }[];
    return allPriceDataWithDates
      .map((point: any) => ({
        date: new Date(point.timestamp),
        close: typeof point.close === 'number' && Number.isFinite(point.close)
          ? point.close
          : (typeof point.price === 'number' && Number.isFinite(point.price) ? point.price : NaN)
      }))
      .filter(p => Number.isFinite(p.close))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [allPriceDataWithDates]);

  useEffect(() => {
    let cancelled = false;

    // The primary chart load already fetches and caches historical prices.
    // Wait for it before loading this secondary price representation so the
    // first Charts navigation does not issue duplicate requests in parallel.
    if (!dataLoaded || !authToken) {
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      try {
        const rows = await fetchHistoricalPrices(selectedStock);

        if (cancelled) return;

        const points = rows
          .map((row) => {
            const date = new Date(row.date ?? '');
            const closeRaw = (row as any).close ?? (row as any).price;
            const close = typeof closeRaw === 'number' ? closeRaw : Number(closeRaw);
            return { date, close };
          })
          .filter((point) => Number.isFinite(point.date.getTime()) && Number.isFinite(point.close))
          .sort((a, b) => a.date.getTime() - b.date.getTime());
        setFmpPricePoints(points);
      } catch {
        if (cancelled) return;
        setFmpPricePoints([]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedStock, dataLoaded, authToken]);

  useEffect(() => {
    let cancelled = false;

    if (!prefersFiscal) {
      setFiscalPricePoints([]);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      try {
        const rows = await fetchFiscalStockPrices(selectedStock);
        if (cancelled) return;

        const points = rows
          .map((row) => {
            const date = new Date(row.date ?? '');
            const closeRaw = (row as any).close_price ?? (row as any).close ?? (row as any).price;
            const close = typeof closeRaw === 'number' ? closeRaw : Number(closeRaw);
            return { date, close };
          })
          .filter((point) => Number.isFinite(point.date.getTime()) && Number.isFinite(point.close))
          .sort((a, b) => a.date.getTime() - b.date.getTime());

        setFiscalPricePoints(points);
      } catch (error) {
        if (cancelled) return;
        setFiscalPricePoints([]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [prefersFiscal, selectedStock]);

  const buildDailyRatioSeries = useMemo(() => {
    const pricePoints = dailyPricePoints;
    if (!pricePoints.length) return (series: Array<{ date: Date; value: number | null }>) => [] as { date: Date; value: number }[];

    const priceDates = pricePoints.map(p => p.date.getTime());

    const findPriceAtOrBefore = (target: Date): number | null => {
      const t = target.getTime();
      let lo = 0;
      let hi = priceDates.length - 1;
      let bestIdx = -1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const v = priceDates[mid];
        if (v <= t) {
          bestIdx = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      if (bestIdx < 0) return null;
      return pricePoints[bestIdx].close;
    };

    return (series: Array<{ date: Date; value: number | null }>) => {
      if (!series.length) return [] as { date: Date; value: number }[];

      const perSharePoints = series
        .map(point => {
          const ratio = point.value;
          if (typeof ratio !== 'number' || !Number.isFinite(ratio) || ratio === 0) return null;
          const price = findPriceAtOrBefore(point.date);
          if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) return null;
          return { date: point.date, perShare: price / ratio };
        })
        .filter((p): p is { date: Date; perShare: number } => !!p)
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      if (!perSharePoints.length) return [] as { date: Date; value: number }[];

      const result: Array<{ date: Date; value: number }> = [];
      let idx = 0;
      let lastPerShare: number | null = null;

      for (const pricePoint of pricePoints) {
        while (idx < perSharePoints.length && perSharePoints[idx].date.getTime() <= pricePoint.date.getTime()) {
          lastPerShare = perSharePoints[idx].perShare;
          idx++;
        }

        if (typeof lastPerShare === 'number' && Number.isFinite(lastPerShare)) {
          if (lastPerShare <= 0) {
            result.push({ date: pricePoint.date, value: 0 });
            continue;
          }
          const ratio = pricePoint.close / lastPerShare;
          result.push({ date: pricePoint.date, value: Number.isFinite(ratio) && ratio > 0 ? ratio : 0 });
        }
      }

      return result;
    };
  }, [dailyPricePoints]);

  const spliceSeriesByStartDate = useCallback((
    seriesBefore: Array<{ date: Date; value: number }>,
    seriesAfter: Array<{ date: Date; value: number }>,
    startDate: Date | null
  ) => {
    if (!startDate) return seriesAfter.length > 0 ? seriesAfter : seriesBefore;
    const startTime = startDate.getTime();
    const before = seriesBefore.filter((point) => point.date.getTime() < startTime);
    const after = seriesAfter.filter((point) => point.date.getTime() >= startTime);
    return [...before, ...after].sort((a, b) => a.date.getTime() - b.date.getTime());
  }, []);

  // Load Income Statement TTM series (AS-IS). These are used both by TTM charts and by ratio charts like P/FCF.
  useEffect(() => {
    if (!dataLoaded) return;
    if (!config.useRealAPI) {
      setIncomeTtmSeries([]);
      return;
    }

    let cancelled = false;

    const loadTtmSeries = async () => {
      try {
        // Large enough to reach back to ~2011 for most symbols.
        const series = await fetchIncomeStatementTTMSeries(selectedStock, 120);
        if (cancelled) return;

        const from2011 = new Date('2011-01-01T00:00:00.000Z').getTime();
        const filtered = series.filter((row) => {
          const t = new Date(row.date).getTime();
          return Number.isFinite(t) && t >= from2011;
        });

        setIncomeTtmSeries(filtered);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Failed to load income-statement-ttm series:', error);
        setIncomeTtmSeries([]);
      }
    };

    loadTtmSeries();
    return () => {
      cancelled = true;
    };
  }, [selectedStock, dataLoaded]);

  useEffect(() => {
    let cancelled = false;

    if (!prefersFiscal) {
      setFiscalIncomeAnnual([]);
      setFiscalIncomeQuarter([]);
      setFmpIncomeQuarter([]);
      return () => {
        cancelled = true;
      };
    }

    const run = async (attempt = 1) => {
      try {
        if (selectedStock === 'SPGI') {
          // eslint-disable-next-line no-console
          console.log('SPGI fiscal income fetch start');
        }
        const result = await fetchFiscalStandardizedIncomeStatement(selectedStock);
        if (cancelled) return;
        if (result.annual.length === 0 && result.quarter.length === 0 && attempt < 3) {
          const sample = result.quarter
            .slice(0, 6)
            .map((row) => ({
              date: row.date,
              periodEndDate: row.periodEndDate,
              filingDate: row.filingDate,
            }));
          // eslint-disable-next-line no-console
          console.log('SPGI fiscal income result:', {
            error: result.error,
            annualCount: result.annual.length,
            quarterCount: result.quarter.length,
            sample,
          });
        }
      } catch (error) {
        if (cancelled) return;
        if (attempt < 3) {
          window.setTimeout(() => { if (!cancelled) run(attempt + 1); }, 1500 * attempt);
          return;
        }
        setFiscalIncomeAnnual([]);
        setFiscalIncomeQuarter([]);
        if (selectedStock === 'SPGI') {
          // eslint-disable-next-line no-console
          console.log('SPGI fiscal income error:', error);
        }
        // eslint-disable-next-line no-console
        console.error('❌ Failed to load fiscal income statement:', error);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [prefersFiscal, selectedStock]);

  useEffect(() => {
    let cancelled = false;

    if (!prefersFiscal) {
      setFiscalBalanceAnnual([]);
      setFiscalBalanceQuarter([]);
      return () => {
        cancelled = true;
      };
    }

    const run = async (attempt = 1) => {
      try {
        const result = await fetchFiscalStandardizedBalanceSheet(selectedStock);
        if (cancelled) return;
        if (result.annual.length === 0 && result.quarter.length === 0 && attempt < 3) {
          window.setTimeout(() => { if (!cancelled) run(attempt + 1); }, 1500 * attempt);
          return;
        }
        setFiscalBalanceAnnual(result.annual);
        setFiscalBalanceQuarter(result.quarter);
      } catch (error) {
        if (cancelled) return;
        if (attempt < 3) {
          window.setTimeout(() => { if (!cancelled) run(attempt + 1); }, 1500 * attempt);
          return;
        }
        setFiscalBalanceAnnual([]);
        setFiscalBalanceQuarter([]);
        // eslint-disable-next-line no-console
        console.error('❌ Failed to load fiscal balance sheet:', error);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [prefersFiscal, selectedStock]);

  useEffect(() => {
    let cancelled = false;

    if (!prefersFiscal) {
      setFiscalCashAnnual([]);
      setFiscalCashQuarter([]);
      return () => {
        cancelled = true;
      };
    }

    const run = async (attempt = 1) => {
      try {
        const result = await fetchFiscalStandardizedCashFlowStatement(selectedStock);
        if (cancelled) return;
        if (result.annual.length === 0 && result.quarter.length === 0 && attempt < 3) {
          window.setTimeout(() => { if (!cancelled) run(attempt + 1); }, 1500 * attempt);
          return;
        }
        setFiscalCashAnnual(result.annual);
        setFiscalCashQuarter(result.quarter);
      } catch (error) {
        if (cancelled) return;
        if (attempt < 3) {
          window.setTimeout(() => { if (!cancelled) run(attempt + 1); }, 1500 * attempt);
          return;
        }
        setFiscalCashAnnual([]);
        setFiscalCashQuarter([]);
        // eslint-disable-next-line no-console
        console.error('❌ Failed to load fiscal cash flow statement:', error);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [prefersFiscal, selectedStock]);

  // Load Cash Flow Statement TTM series (AS-IS). These are used both by TTM charts and by ratio charts like P/FCF.
  useEffect(() => {
    if (!dataLoaded) return;
    if (!config.useRealAPI) {
      setCashFlowTtmSeries([]);
      return;
    }

    let cancelled = false;

    const loadTtmSeries = async () => {
      try {
        const series = await fetchCashFlowStatementTTMSeries(selectedStock, 120);
        if (cancelled) return;

        const from2011 = new Date('2011-01-01T00:00:00.000Z').getTime();
        const filtered = series.filter((row) => {
          const t = new Date(row.date).getTime();
          return Number.isFinite(t) && t >= from2011;
        });

        setCashFlowTtmSeries(filtered);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Failed to load cash-flow-statement-ttm series:', error);
        setCashFlowTtmSeries([]);
      }
    };

    loadTtmSeries();
    return () => {
      cancelled = true;
    };
  }, [selectedStock, dataLoaded]);
  
  const financialData = useMemo(() => {
    if (!dataLoaded) {
      return {
        revenue: [],
        netIncome: [],
        grossProfit: [],
        operatingIncome: [],
        pretaxIncome: [],
        eps: [],
        cash: [],
        totalAssets: [],
        shortTermDebt: [],
        longTermDebt: [],
        totalLiabilities: [],
        totalStockholdersEquity: [],
        operatingCashFlow: [],
        roic: [],
        years: [],
        periods: []
      };
    }

    const periodCategory: 'Annual Data' | 'Quarterly Data' = selectedPeriodType === 'Annual Data' ? 'Annual Data' : 'Quarterly Data';

    // Income statement base series (Annual/Quarter). For TTM:
    // - When using real API: use income-statement-ttm AS-IS (no quarterly summation)
    // - Otherwise: derive TTM from quarterly JSON values
    const baseIncomeCategory: 'Annual Data' | 'Quarterly Data' = selectedPeriodType === 'TTM' ? 'Quarterly Data' : periodCategory;
    const revenueBase = getFinancialMetricData('revenue', baseIncomeCategory, selectedStock);
    const netIncomeBase = getFinancialMetricData('netIncome', baseIncomeCategory, selectedStock);
    const grossProfitBase = getFinancialMetricData('grossProfit', baseIncomeCategory, selectedStock);
    const operatingIncomeBase = getFinancialMetricData('operatingIncome', baseIncomeCategory, selectedStock);
    const pretaxIncomeBase = getFinancialMetricData('pretaxIncome', baseIncomeCategory, selectedStock);
    const epsBase = getFinancialMetricData('eps', baseIncomeCategory, selectedStock);

    const periodsBase = getFinancialDataPeriods(baseIncomeCategory, selectedStock);

    const rollingTTMSum = (values: number[]): number[] => {
      if (!values || values.length < 4) return [];
      const out: number[] = [];
      for (let i = 3; i < values.length; i++) {
        out.push((values[i - 3] || 0) + (values[i - 2] || 0) + (values[i - 1] || 0) + (values[i] || 0));
      }
      return out;
    };

    const useFiscalIncome = prefersFiscal;
    const annualOrQuarterRows = selectedPeriodType === 'Annual Data' ? fiscalIncomeAnnual : fiscalIncomeQuarter;
    const balanceRows = selectedPeriodType === 'Annual Data' ? fiscalBalanceAnnual : fiscalBalanceQuarter;

    const useApiTtmSeries = selectedPeriodType === 'TTM' && config.useRealAPI && incomeTtmSeries.length > 0;
    const useApiCashFlowTtmSeries = selectedPeriodType === 'TTM' && config.useRealAPI && cashFlowTtmSeries.length > 0;

    const fiscalTtmPeriods = (() => {
      if (selectedPeriodType !== 'TTM' || !useFiscalIncome || fiscalIncomeQuarter.length < 4) return [] as Date[];
      const sorted = [...fiscalIncomeQuarter]
        .map((row) => String(row?.date || '').trim())
        .filter((key) => key.length > 0)
        .sort((a, b) => Date.parse(a) - Date.parse(b));
      if (sorted.length < 4) return [] as Date[];
      return sorted.slice(3).map((key) => dateKeyToUTCDate(key, false));
    })();

    // In fiscal TTM mode, keep the X-axis anchored to fiscal periods so latest fiscal
    // quarters (for example 03/2026, 06/2026) are not truncated by older FMP TTM tails.
    const periods = selectedPeriodType === 'TTM'
      ? (fiscalTtmPeriods.length > 0
          ? fiscalTtmPeriods
          : (useApiTtmSeries
              ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false))
              : periodsBase.slice(3)))
      : periodsBase;

    const buildFiscalMetricByDateKey = (metric: keyof FiscalIncomeRow) => {
      if (!useFiscalIncome) return new Map<string, number>();
      if (!annualOrQuarterRows.length) return new Map<string, number>();
      const map = new Map<string, number>();
      annualOrQuarterRows.forEach((row) => {
        const key = String(row.date || '').trim();
        const raw = metric === 'epsDiluted'
          ? (row.epsDiluted ?? row.eps)
          : (row as FiscalIncomeRow)[metric];
        const value = Number(raw);
        if (!key || !Number.isFinite(value)) return;
        const normalized = metric === 'epsDiluted' ? value : (value / 1_000_000);
        map.set(key, normalized);
      });
      return map;
    };

    const buildFiscalMetricTtmByDateKey = (metric: keyof FiscalIncomeRow) => {
      if (!useFiscalIncome) return new Map<string, number>();
      if (!fiscalIncomeQuarter.length) return new Map<string, number>();
      const rows = fiscalIncomeQuarter
        .map((row) => {
          const raw = metric === 'epsDiluted'
            ? (row.epsDiluted ?? row.eps)
            : (row as FiscalIncomeRow)[metric];
          return { date: String(row.date || '').trim(), value: Number(raw) };
        })
        .filter((row) => row.date && Number.isFinite(row.value))
        .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));

      const map = new Map<string, number>();
      for (let i = 3; i < rows.length; i += 1) {
        const sum = rows[i - 3].value + rows[i - 2].value + rows[i - 1].value + rows[i].value;
        if (Number.isFinite(sum)) {
          const normalized = metric === 'epsDiluted' ? sum : (sum / 1_000_000);
          map.set(rows[i].date, normalized);
        }
      }
      return map;
    };

    const buildSeriesByDateKey = (values: Array<number | null>, seriesPeriods: Date[]) => {
      const map = new Map<string, number>();
      seriesPeriods.forEach((d, i) => {
        const v = values[i];
        if (typeof v === 'number' && Number.isFinite(v)) map.set(toUTCDateKey(d), v);
      });
      return periods.map((d) => {
        const value = map.get(toUTCDateKey(d));
        return Number.isFinite(value) ? (value as number) : null;
      });
    };

    const getFiscalMetricSeries = (metric: keyof FiscalIncomeRow) => {
      if (!useFiscalIncome) return null;
      const map = selectedPeriodType === 'TTM'
        ? buildFiscalMetricTtmByDateKey(metric)
        : buildFiscalMetricByDateKey(metric);
      if (map.size === 0) return null;
      return periods.map((d) => {
        const key = toUTCDateKey(d);
        const value = map.get(key);
        return Number.isFinite(value) ? (value as number) : null;
      });
    };

    const getFmpMetricSeries = (baseValues: Array<number | null>, ttmValues: Array<number | null>, ttmPeriods: Date[]) => {
      if (selectedPeriodType === 'TTM') {
        return buildSeriesByDateKey(ttmValues, ttmPeriods);
      }
      return buildSeriesByDateKey(baseValues, periodsBase);
    };

    const mergeFiscalWithFmp = (
      fiscalSeries: Array<number | null> | null,
      fmpSeries: Array<number | null>,
      options?: { suppressLastFiscalZero?: boolean; useFmpWhenFiscalMissing?: boolean }
    ) => {
      if (!prefersFiscal) {
        return fmpSeries.map((value) => (typeof value === 'number' && Number.isFinite(value) ? value : null));
      }
      if (!fiscalSeries) {
        return fmpSeries.map((value) => (typeof value === 'number' && Number.isFinite(value) ? value : null));
      }
      const startYear = 2012;
      const endYear = 2016;
      const lastIndex = periods.length - 1;
      const suppressLastFiscalZero = options?.suppressLastFiscalZero ?? false;
      const useFmpWhenFiscalMissing = options?.useFmpWhenFiscalMissing ?? false;

      return periods.map((d, i) => {
        const year = d.getUTCFullYear();
        const useFmp = year >= startYear && year <= endYear;
        if (useFmp) {
          const fmpValue = fmpSeries[i];
          return typeof fmpValue === 'number' && Number.isFinite(fmpValue) ? fmpValue : null;
        }

        const fiscalValue = fiscalSeries?.[i] ?? null;
        if (suppressLastFiscalZero && i === lastIndex && fiscalValue === 0) {
          return null;
        }
        if (typeof fiscalValue === 'number' && Number.isFinite(fiscalValue)) {
          return fiscalValue;
        }

        if (useFmpWhenFiscalMissing) {
          const fmpValue = fmpSeries[i];
          return typeof fmpValue === 'number' && Number.isFinite(fmpValue) ? fmpValue : null;
        }

        return null;
      });
    };

    const buildFiscalBalanceByDateKey = (metric: keyof BalanceSheet) => {
      if (!prefersFiscal) return new Map<string, number>();
      if (!balanceRows.length) return new Map<string, number>();
      const map = new Map<string, number>();
      balanceRows.forEach((row) => {
        const key = String(row.date || '').trim();
        const value = Number((row as BalanceSheet)[metric]);
        if (!key || !Number.isFinite(value)) return;
        const normalized = Math.abs(value) < 1_000_000 ? value * 1_000_000 : value;
        map.set(key, normalized / 1_000_000);
      });
      return map;
    };

    const buildFiscalCashByDateKey = (
      metric: 'operatingCashFlow' | 'freeCashFlow' | 'dividends' | 'buybacks'
    ) => {
      if (!prefersFiscal) return new Map<string, number>();
      const rows = selectedPeriodType === 'Annual Data' ? fiscalCashAnnual : fiscalCashQuarter;
      if (!rows.length) return new Map<string, number>();

      const map = new Map<string, number>();
      rows.forEach((row) => {
        const key = String(row.date || '').trim();
        if (!key) return;

        const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
        const capex = Number(row.capitalExpenditure ?? row.investmentsInPropertyPlantAndEquipment);
        const dividends = Number(row.dividendsPaid ?? row.commonDividendsPaid);
        const buybacks = Number(row.commonStockRepurchased);

        const value = (() => {
          switch (metric) {
            case 'operatingCashFlow':
              return ocf;
            case 'freeCashFlow':
              if (!Number.isFinite(ocf) || !Number.isFinite(capex)) return NaN;
              return ocf + (capex > 0 ? -capex : capex);
            case 'dividends':
              return dividends;
            case 'buybacks':
              return buybacks;
          }
        })();

        if (!Number.isFinite(value)) return;
        map.set(key, value / 1_000_000);
      });

      return map;
    };

    const buildFiscalCashTtmByDateKey = (
      metric: 'operatingCashFlow' | 'freeCashFlow' | 'dividends' | 'buybacks'
    ) => {
      if (!prefersFiscal) return new Map<string, number>();
      if (!fiscalCashQuarter.length) return new Map<string, number>();

      const rows = fiscalCashQuarter
        .map((row) => {
          const dateKey = String(row.date || '').trim();
          const ocf = Number(row.operatingCashFlow ?? row.netCashProvidedByOperatingActivities);
          const capexRaw = Number(row.capitalExpenditure ?? row.investmentsInPropertyPlantAndEquipment);
          const capex = capexRaw > 0 ? -capexRaw : capexRaw;
          const dividends = Number(row.dividendsPaid ?? row.commonDividendsPaid);
          const buybacks = Number(row.commonStockRepurchased);
          return { dateKey, ocf, capex, dividends, buybacks };
        })
        .filter((row) => row.dateKey)
        .sort((a, b) => Date.parse(a.dateKey) - Date.parse(b.dateKey));

      if (rows.length < 4) return new Map<string, number>();

      const map = new Map<string, number>();
      for (let i = 3; i < rows.length; i += 1) {
        let ocfSum = 0;
        let fcfSum = 0;
        let divSum = 0;
        let buybackSum = 0;

        for (let j = i - 3; j <= i; j += 1) {
          if (Number.isFinite(rows[j].ocf)) ocfSum += rows[j].ocf;
          if (Number.isFinite(rows[j].ocf) && Number.isFinite(rows[j].capex)) {
            fcfSum += rows[j].ocf + rows[j].capex;
          } else if (Number.isFinite(rows[j].ocf)) {
            fcfSum += rows[j].ocf;
          }
          if (Number.isFinite(rows[j].dividends)) divSum += rows[j].dividends;
          if (Number.isFinite(rows[j].buybacks)) buybackSum += rows[j].buybacks;
        }

        const value = (() => {
          switch (metric) {
            case 'operatingCashFlow':
              return ocfSum;
            case 'freeCashFlow':
              return fcfSum;
            case 'dividends':
              return divSum;
            case 'buybacks':
              return buybackSum;
          }
        })();

        if (!Number.isFinite(value)) continue;
        map.set(rows[i].dateKey, value / 1_000_000);
      }

      return map;
    };

    const getFiscalBalanceSeries = (metric: keyof BalanceSheet) => {
      if (!prefersFiscal) return null;
      const map = buildFiscalBalanceByDateKey(metric);
      if (map.size === 0) return null;
      return periods.map((d) => {
        const key = toUTCDateKey(d);
        const value = map.get(key);
        return Number.isFinite(value) ? (value as number) : 0;
      });
    };

    const getFiscalCashSeries = (metric: 'operatingCashFlow' | 'freeCashFlow' | 'dividends' | 'buybacks') => {
      if (!prefersFiscal) return null;
      const map = selectedPeriodType === 'TTM'
        ? buildFiscalCashTtmByDateKey(metric)
        : buildFiscalCashByDateKey(metric);
      if (map.size === 0) return null;
      return periods.map((d) => {
        const key = toUTCDateKey(d);
        const value = map.get(key);
        return Number.isFinite(value) ? (value as number) : 0;
      });
    };

    const revenueFmp = getFmpMetricSeries(
      revenueBase,
      useApiTtmSeries ? incomeTtmSeries.map((s) => (typeof s.revenue === 'number' ? s.revenue / 1_000_000 : 0)) : rollingTTMSum(revenueBase),
      useApiTtmSeries ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false)) : periodsBase.slice(3)
    );
    const netIncomeFmp = getFmpMetricSeries(
      netIncomeBase,
      useApiTtmSeries ? incomeTtmSeries.map((s) => (typeof s.netIncome === 'number' ? s.netIncome / 1_000_000 : 0)) : rollingTTMSum(netIncomeBase),
      useApiTtmSeries ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false)) : periodsBase.slice(3)
    );
    const grossProfitFmp = getFmpMetricSeries(
      grossProfitBase,
      useApiTtmSeries ? incomeTtmSeries.map((s) => (typeof s.grossProfit === 'number' ? s.grossProfit / 1_000_000 : 0)) : rollingTTMSum(grossProfitBase),
      useApiTtmSeries ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false)) : periodsBase.slice(3)
    );
    const operatingIncomeFmp = getFmpMetricSeries(
      operatingIncomeBase,
      useApiTtmSeries ? incomeTtmSeries.map((s) => (typeof s.operatingIncome === 'number' ? s.operatingIncome / 1_000_000 : 0)) : rollingTTMSum(operatingIncomeBase),
      useApiTtmSeries ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false)) : periodsBase.slice(3)
    );
    const pretaxIncomeFmp = getFmpMetricSeries(
      pretaxIncomeBase,
      useApiTtmSeries ? incomeTtmSeries.map((s) => (typeof s.incomeBeforeTax === 'number' ? s.incomeBeforeTax / 1_000_000 : 0)) : rollingTTMSum(pretaxIncomeBase),
      useApiTtmSeries ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false)) : periodsBase.slice(3)
    );
    const epsFmp = getFmpMetricSeries(
      epsBase,
      useApiTtmSeries ? incomeTtmSeries.map((s) => (typeof s.epsDiluted === 'number' ? s.epsDiluted : 0)) : rollingTTMSum(epsBase),
      useApiTtmSeries ? incomeTtmSeries.map((s) => dateKeyToUTCDate(s.date, false)) : periodsBase.slice(3)
    );

    const revenueData = mergeFiscalWithFmp(getFiscalMetricSeries('revenue'), revenueFmp, { useFmpWhenFiscalMissing: true });
    const netIncomeData = mergeFiscalWithFmp(getFiscalMetricSeries('netIncome'), netIncomeFmp, { useFmpWhenFiscalMissing: true });
    const grossProfitData = mergeFiscalWithFmp(getFiscalMetricSeries('grossProfit'), grossProfitFmp, { useFmpWhenFiscalMissing: true });
    const operatingIncomeData = mergeFiscalWithFmp(getFiscalMetricSeries('operatingIncome'), operatingIncomeFmp, { useFmpWhenFiscalMissing: true });
    const pretaxIncomeData = mergeFiscalWithFmp(getFiscalMetricSeries('incomeBeforeTax'), pretaxIncomeFmp, { useFmpWhenFiscalMissing: true });
    const epsData = mergeFiscalWithFmp(getFiscalMetricSeries('epsDiluted'), epsFmp, { useFmpWhenFiscalMissing: true });

    // When in TTM mode with real API, align cash-flow series to the same period keys as income-statement-ttm.
    const periodDateKeys = periods.map((d) => toUTCDateKey(d));
    const cashFlowByDateKey = useApiCashFlowTtmSeries
      ? new Map(cashFlowTtmSeries.map((row) => [toUTCDateKey(row.date), row]))
      : new Map<string, CashFlowStatement>();
    const cashFmp = buildSeriesByDateKey(
      getBalanceSheetMetricData('cashAndCashEquivalents', periodCategory, selectedStock),
      periodsBase
    );
    const totalAssetsFmp = buildSeriesByDateKey(
      getBalanceSheetMetricData('totalAssets', periodCategory, selectedStock),
      periodsBase
    );
    const shortTermDebtFmp = buildSeriesByDateKey(
      getBalanceSheetMetricData('shortTermDebt', periodCategory, selectedStock),
      periodsBase
    );
    const longTermDebtFmp = buildSeriesByDateKey(
      getBalanceSheetMetricData('longTermDebt', periodCategory, selectedStock),
      periodsBase
    );
    const totalLiabilitiesFmp = buildSeriesByDateKey(
      getBalanceSheetMetricData('totalLiabilities', periodCategory, selectedStock),
      periodsBase
    );
    const totalStockholdersEquityFmp = buildSeriesByDateKey(
      getBalanceSheetMetricData('totalStockholdersEquity', periodCategory, selectedStock),
      periodsBase
    );

    const operatingCashFlowFmpBase = useApiCashFlowTtmSeries
      ? periodDateKeys.map((k) => {
          const row = cashFlowByDateKey.get(k);
          const v = (row as any)?.operatingCashFlow;
          return typeof v === 'number' ? v / 1_000_000 : 0;
        })
      : getCashFlowMetricData('operatingCashFlow', periodCategory, selectedStock);
    const freeCashFlowFmpBase = useApiCashFlowTtmSeries
      ? periodDateKeys.map((k) => {
          const row = cashFlowByDateKey.get(k);
          const v = (row as any)?.freeCashFlow;
          return typeof v === 'number' ? v / 1_000_000 : 0;
        })
      : getCashFlowMetricData('freeCashFlow', periodCategory, selectedStock);
    const dividendsFmpBase = useApiCashFlowTtmSeries
      ? periodDateKeys.map((k) => {
          const row = cashFlowByDateKey.get(k);
          const v = (row as any)?.dividendsPaid ?? (row as any)?.commonDividendsPaid ?? (row as any)?.netDividendsPaid;
          return typeof v === 'number' ? v / 1_000_000 : 0;
        })
      : getCashFlowMetricData('dividendsPaid', periodCategory, selectedStock);
    const buybacksFmpBase = useApiCashFlowTtmSeries
      ? periodDateKeys.map((k) => {
          const row = cashFlowByDateKey.get(k);
          const v = (row as any)?.commonStockRepurchased ?? (row as any)?.stockRepurchased;
          return typeof v === 'number' ? v / 1_000_000 : 0;
        })
      : getCashFlowMetricData('stockRepurchased', periodCategory, selectedStock);

    const operatingCashFlowFmp = buildSeriesByDateKey(
      operatingCashFlowFmpBase,
      useApiCashFlowTtmSeries ? periodDateKeys.map((k) => dateKeyToUTCDate(k, false)) : periodsBase
    );
    const freeCashFlowFmp = buildSeriesByDateKey(
      freeCashFlowFmpBase,
      useApiCashFlowTtmSeries ? periodDateKeys.map((k) => dateKeyToUTCDate(k, false)) : periodsBase
    );
    const dividendsFmp = buildSeriesByDateKey(
      dividendsFmpBase,
      useApiCashFlowTtmSeries ? periodDateKeys.map((k) => dateKeyToUTCDate(k, false)) : periodsBase
    );
    const buybacksFmp = buildSeriesByDateKey(
      buybacksFmpBase,
      useApiCashFlowTtmSeries ? periodDateKeys.map((k) => dateKeyToUTCDate(k, false)) : periodsBase
    );

    const fiscalRevenueSeries = getFiscalMetricSeries('revenue');
    const fiscalFreeCashFlowSeries = getFiscalCashSeries('freeCashFlow');

    const cashData = mergeFiscalWithFmp(getFiscalBalanceSeries('cashAndCashEquivalents'), cashFmp, { useFmpWhenFiscalMissing: true });
    const totalAssetsData = mergeFiscalWithFmp(getFiscalBalanceSeries('totalAssets'), totalAssetsFmp, { useFmpWhenFiscalMissing: true });
    const shortTermDebtData = mergeFiscalWithFmp(getFiscalBalanceSeries('shortTermDebt'), shortTermDebtFmp, { useFmpWhenFiscalMissing: true });
    const longTermDebtData = mergeFiscalWithFmp(getFiscalBalanceSeries('longTermDebt'), longTermDebtFmp, { useFmpWhenFiscalMissing: true });
    const totalLiabilitiesData = mergeFiscalWithFmp(getFiscalBalanceSeries('totalLiabilities'), totalLiabilitiesFmp, { useFmpWhenFiscalMissing: true });
    const totalStockholdersEquityData = mergeFiscalWithFmp(getFiscalBalanceSeries('totalStockholdersEquity'), totalStockholdersEquityFmp, { useFmpWhenFiscalMissing: true });
    const operatingCashFlowData = mergeFiscalWithFmp(getFiscalCashSeries('operatingCashFlow'), operatingCashFlowFmp, { useFmpWhenFiscalMissing: true });
    const freeCashFlowData = mergeFiscalWithFmp(getFiscalCashSeries('freeCashFlow'), freeCashFlowFmp, { useFmpWhenFiscalMissing: true });
    const dividendsData = mergeFiscalWithFmp(getFiscalCashSeries('dividends'), dividendsFmp, { suppressLastFiscalZero: true, useFmpWhenFiscalMissing: true });
    const buybacksData = mergeFiscalWithFmp(getFiscalCashSeries('buybacks'), buybacksFmp, { suppressLastFiscalZero: true, useFmpWhenFiscalMissing: true });
    const grossProfitMarginData = revenueData.map((rev, index) => {
      const gp = grossProfitData[index];
      if (typeof rev === 'number' && Number.isFinite(rev) && rev !== 0 && typeof gp === 'number' && Number.isFinite(gp)) {
        return (gp / rev) * 100;
      }
      return 0;
    });
    const operatingMarginData = revenueData.map((rev, index) => {
      const op = operatingIncomeData[index];
      if (typeof rev === 'number' && Number.isFinite(rev) && rev !== 0 && typeof op === 'number' && Number.isFinite(op)) {
        return (op / rev) * 100;
      }
      return 0;
    });
    const netMarginData = revenueData.map((rev, index) => {
      const ni = netIncomeData[index];
      if (typeof rev === 'number' && Number.isFinite(rev) && rev !== 0 && typeof ni === 'number' && Number.isFinite(ni)) {
        return (ni / rev) * 100;
      }
      return 0;
    });
    const operatingCashFlowMarginData = revenueData.map((rev, index) => {
      const ocf = operatingCashFlowData[index];
      if (typeof rev === 'number' && Number.isFinite(rev) && rev !== 0 && typeof ocf === 'number' && Number.isFinite(ocf)) {
        return (ocf / rev) * 100;
      }
      return 0;
    });
    const fmpFreeCashFlowMarginSeries = periods.map((_, index) => {
      const fmpRev = revenueFmp[index];
      const fmpFcf = freeCashFlowFmp[index];
      return (fmpRev && fmpRev !== 0 && fmpFcf) ? (fmpFcf / fmpRev) * 100 : 0;
    });

    const fiscalFreeCashFlowMarginSeries = periods.map((_, index) => {
      if (!fiscalRevenueSeries || !fiscalFreeCashFlowSeries) return 0;
      const fiscalRev = fiscalRevenueSeries[index];
      const fiscalFcf = fiscalFreeCashFlowSeries[index];
      return (fiscalRev && fiscalRev !== 0 && fiscalFcf) ? (fiscalFcf / fiscalRev) * 100 : 0;
    });

    const firstFiscalMarginIndex = (fiscalRevenueSeries && fiscalFreeCashFlowSeries)
      ? periods.findIndex((_, index) => {
          const fiscalRev = fiscalRevenueSeries[index];
          const fiscalFcf = fiscalFreeCashFlowSeries[index];
          return Number.isFinite(fiscalRev) && fiscalRev !== 0 && Number.isFinite(fiscalFcf);
        })
      : -1;

    const freeCashFlowMarginData = periods.map((_, index) => {
      if (!prefersFiscal || firstFiscalMarginIndex < 0) {
        return fmpFreeCashFlowMarginSeries[index] ?? 0;
      }
      if (index < firstFiscalMarginIndex) {
        return fmpFreeCashFlowMarginSeries[index] ?? 0;
      }
      const fiscalValue = fiscalFreeCashFlowMarginSeries[index];
      return Number.isFinite(fiscalValue) ? (fiscalValue as number) : 0;
    });
    const ocfToNetIncomeData = netIncomeData.map((netIncome, index) => {
      const ocf = operatingCashFlowData[index];
      const ratio = (netIncome && netIncome !== 0 && ocf) ? (ocf / netIncome) * 100 : 0;
      return ratio;
    });
    const fcfToNetIncomeData = netIncomeData.map((netIncome, index) => {
      const fcf = freeCashFlowData[index];
      const ratio = (netIncome && netIncome !== 0 && fcf) ? (fcf / netIncome) * 100 : 0;
      return ratio;
    });
    const roicByDateKey = new Map(roicPoints.map((point) => [point.dateKey, point.value]));
    const roicByYear = new Map<string, { dateKey: string; value: number }>();
    roicPoints.forEach((point) => {
      const year = point.dateKey.slice(0, 4);
      if (!year) return;
      const existing = roicByYear.get(year);
      if (!existing || point.dateKey > existing.dateKey) {
        // Keep the latest point in each year so Annual view matches year-end Growth values.
        roicByYear.set(year, { dateKey: point.dateKey, value: point.value });
      }
    });
    return {
      revenue: revenueData,
      netIncome: netIncomeData,
      grossProfit: grossProfitData,
      operatingIncome: operatingIncomeData,
      pretaxIncome: pretaxIncomeData,
      eps: epsData,
      cash: cashData,
      totalAssets: totalAssetsData,
      shortTermDebt: shortTermDebtData,
      longTermDebt: longTermDebtData,
      totalLiabilities: totalLiabilitiesData,
      totalStockholdersEquity: totalStockholdersEquityData,
      operatingCashFlow: operatingCashFlowData,
      freeCashFlow: freeCashFlowData,
      dividends: dividendsData,
      buybacks: buybacksData,
      grossProfitMargin: grossProfitMarginData,
      operatingMargin: operatingMarginData,
      netMargin: netMarginData,
      operatingCashFlowMargin: operatingCashFlowMarginData,
      freeCashFlowMargin: freeCashFlowMarginData,
      ocfToNetIncome: ocfToNetIncomeData,
      fcfToNetIncome: fcfToNetIncomeData,
      roic: periods.map((date) => {
        const dateKey = toUTCDateKey(date);
        const exact = roicByDateKey.get(dateKey);
        if (typeof exact === 'number' && Number.isFinite(exact)) return exact;

        if (selectedPeriodType !== 'Annual Data') return 0;
        const yearFallback = roicByYear.get(String(date.getUTCFullYear()))?.value;
        return typeof yearFallback === 'number' && Number.isFinite(yearFallback) ? yearFallback : 0;
      }),
      years: periods.map(d => d.getFullYear()),
      periods: periods
    };
  }, [cashFlowTtmSeries, dataLoaded, incomeTtmSeries, roicData, roicPoints, selectedPeriodType, selectedStock, fiscalIncomeAnnual, fiscalIncomeQuarter, fiscalBalanceAnnual, fiscalBalanceQuarter, fiscalCashAnnual, fiscalCashQuarter, prefersFiscal]);

  const epsAnchorDates = useMemo(() => {
    if (!financialData.periods.length) return [] as Date[];
    if (analysisDateModeEps !== 'at-reported') return financialData.periods;

    const map = new Map<string, string>();
    const rows = selectedPeriodType === 'Annual Data' ? fiscalIncomeAnnual : fiscalIncomeQuarter;

    if (prefersFiscal && rows.length > 0) {
      rows.forEach((row) => {
        const periodEnd = row.periodEndDate || row.date;
        if (!periodEnd) return;
        const key = periodEnd.slice(0, 10);
        if (!key) return;
        const filing = row.filingDate || quarterlyFilingDates.get(key);
        if (filing) map.set(key, filing);
      });
    }

    // Symbols missing from Fiscal (for example OTC listings) still need the
    // FMP filing/accepted date for At Reported EPS anchors.
    fmpIncomeQuarter.forEach((row) => {
      const key = String(row.date || '').slice(0, 10);
      if (key && !map.has(key)) {
        const periodEnd = new Date(`${key}T00:00:00.000Z`);
        const reportDate = resolveFmpAtReportedDate(selectedStock, periodEnd, row.acceptedDate, row.filingDate);
        if (!Number.isNaN(reportDate.getTime())) map.set(key, reportDate.toISOString());
      }
    });

    financialData.periods.forEach((date) => {
      const key = toUTCDateKey(date);
      if (map.has(key)) return;
      const filing = quarterlyFilingDates.get(key);
      if (filing) map.set(key, filing);
    });

    return financialData.periods.map((date) => {
      const key = toUTCDateKey(date);
      const raw = map.get(key);
      if (raw) {
        const dt = new Date(raw);
        if (!Number.isNaN(dt.getTime())) return shiftAtReportedDate(dt, analysisDateModeEps);
      }
      return shiftAtReportedDate(date, analysisDateModeEps);
    });
  }, [analysisDateModeEps, financialData.periods, selectedPeriodType, selectedStock, prefersFiscal, fiscalIncomeAnnual, fiscalIncomeQuarter, fmpIncomeQuarter, quarterlyFilingDates]);

  const ocfPerShareDerivedSeries = useMemo(() => {
    if (!financialData.periods.length) return [] as { date: Date; value: number }[];

    return financialData.periods
      .map((date, index) => {
        const ocfRaw = financialData.operatingCashFlow[index];
        const netIncomeRaw = financialData.netIncome[index];
        const epsRaw = financialData.eps[index];
        const ocf = typeof ocfRaw === 'number' ? ocfRaw : Number.NaN;
        const netIncome = typeof netIncomeRaw === 'number' ? netIncomeRaw : Number.NaN;
        const eps = typeof epsRaw === 'number' ? epsRaw : Number.NaN;

        if (!Number.isFinite(ocf) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) {
          return null;
        }

        const sharesDerived = netIncome / eps;
        if (!Number.isFinite(sharesDerived) || sharesDerived <= 0) return null;

        const ocfPerShare = ocf / sharesDerived;
        if (!Number.isFinite(ocfPerShare)) return null;

        return { date, value: ocfPerShare };
      })
      .filter((p): p is { date: Date; value: number } => !!p);
  }, [financialData]);

  const fcfPerShareDerivedSeries = useMemo(() => {
    if (!financialData.periods.length) return [] as { date: Date; value: number }[];

    return financialData.periods
      .map((date, index) => {
        const fcfRaw = financialData.freeCashFlow?.[index];
        const netIncomeRaw = financialData.netIncome[index];
        const epsRaw = financialData.eps[index];
        const fcf = typeof fcfRaw === 'number' ? fcfRaw : Number.NaN;
        const netIncome = typeof netIncomeRaw === 'number' ? netIncomeRaw : Number.NaN;
        const eps = typeof epsRaw === 'number' ? epsRaw : Number.NaN;

        if (!Number.isFinite(fcf) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) {
          return null;
        }

        const sharesDerived = netIncome / eps;
        if (!Number.isFinite(sharesDerived) || sharesDerived <= 0) return null;

        const fcfPerShare = fcf / sharesDerived;
        if (!Number.isFinite(fcfPerShare)) return null;

        return { date, value: fcfPerShare };
      })
      .filter((p): p is { date: Date; value: number } => !!p);
  }, [financialData]);

  const pfcfDailySeries = useMemo(() => buildDailyRatioSeries(pfcfSeries), [buildDailyRatioSeries, pfcfSeries]);
  const pocfDailySeries = useMemo(() => {
    const direct = buildDailyRatioSeries(pocfSeries);
    if (direct.length > 0) return direct;

    // Fallback: derive daily P/OCF from historical price and OCF/share anchors.
    if (!dailyPricePoints.length) return [] as { date: Date; value: number }[];

    const ocfPerShareAnchors = ocfPerShareSeries.length > 0 ? ocfPerShareSeries : ocfPerShareDerivedSeries;
    if (!ocfPerShareAnchors.length) return [] as { date: Date; value: number }[];

    const perSharePoints = ocfPerShareAnchors
      .map((point) => {
        const perShare = point.value;
        if (typeof perShare !== 'number' || !Number.isFinite(perShare)) return null;
        return { date: point.date, perShare };
      })
      .filter((p): p is { date: Date; perShare: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (!perSharePoints.length) return [] as { date: Date; value: number }[];

    const result: Array<{ date: Date; value: number }> = [];
    let idx = 0;
    let lastPerShare: number | null = null;

    for (const pricePoint of dailyPricePoints) {
      while (idx < perSharePoints.length && perSharePoints[idx].date.getTime() <= pricePoint.date.getTime()) {
        lastPerShare = perSharePoints[idx].perShare;
        idx++;
      }

      if (typeof lastPerShare === 'number' && Number.isFinite(lastPerShare)) {
        if (lastPerShare <= 0) {
          result.push({ date: pricePoint.date, value: 0 });
          continue;
        }
        const ratio = pricePoint.close / lastPerShare;
        result.push({ date: pricePoint.date, value: Number.isFinite(ratio) && ratio > 0 ? ratio : 0 });
      }
    }

    return result;
  }, [buildDailyRatioSeries, pocfSeries, dailyPricePoints, ocfPerShareSeries, ocfPerShareDerivedSeries]);

  const fiscalPeTtmPoints = useMemo(() => {
    if (!prefersFiscal) return [] as Array<{ date: Date; eps: number }>;
    if (!fiscalIncomeQuarter.length) return [] as Array<{ date: Date; eps: number }>;

    const rows = fiscalIncomeQuarter
      .map((row) => ({
        date: shiftAtReportedDate(getFiscalAnchorDate(row, analysisDateModeEps), analysisDateModeEps),
        eps: Number(row.eps ?? row.epsDiluted),
      }))
      .filter((row) => Number.isFinite(row.date.getTime()) && Number.isFinite(row.eps))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (rows.length < 4) return [] as Array<{ date: Date; eps: number }>;

    const out: Array<{ date: Date; eps: number }> = [];
    for (let i = 3; i < rows.length; i += 1) {
      const sum = rows[i - 3].eps + rows[i - 2].eps + rows[i - 1].eps + rows[i].eps;
      if (!Number.isFinite(sum)) continue;
      out.push({ date: rows[i].date, eps: sum });
    }

    return out;
  }, [analysisDateModeEps, prefersFiscal, fiscalIncomeQuarter, quarterlyFilingDates]);

  // Calculate PE data once
  const peData = useMemo(() => {
    if (!dataLoaded) return [];

    const pricePointsForPe =
      prefersFiscal && fiscalPricePoints.length > 0
        ? fiscalPricePoints
        : dailyPricePoints;

    if (prefersFiscal && fiscalPeTtmPoints.length > 0 && pricePointsForPe.length > 0) {
      const peSeries: Array<{ date: Date; value: number }> = [];
      let idx = 0;
      let latestEps: number | null = null;

      for (const pricePoint of pricePointsForPe) {
        const priceDate = pricePoint.date;
        while (idx < fiscalPeTtmPoints.length && fiscalPeTtmPoints[idx].date.getTime() <= priceDate.getTime()) {
          latestEps = fiscalPeTtmPoints[idx].eps;
          idx += 1;
        }
        if (latestEps == null) continue;

        const peRaw = latestEps > 0 ? (pricePoint.close / latestEps) : 0;
        const pe = Number.isFinite(peRaw) && peRaw > 0 ? peRaw : 0;
        peSeries.push({ date: priceDate, value: pe });
      }

      const fiscalStart = fiscalPeTtmPoints[0].date.getTime();
      const fmpSeries = calculatePE(selectedStock);
      const blended = [
        ...fmpSeries.filter((point) => point.date.getTime() < fiscalStart),
        ...peSeries,
      ];

      return blended;
    }

    return calculatePE(selectedStock);
  }, [dataLoaded, dailyPricePoints, fiscalPeTtmPoints, fiscalPricePoints, prefersFiscal, selectedStock]);

  // Calculate Flat EPS TTM data once
  const flatEPSTTMData = useMemo<Array<{ date: Date; value: number }>>(() => {
    if (!dataLoaded) return [];
    const normalizeToLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const localDateKey = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const dedupeByDate = (series: Array<{ date: Date; value: number }>) => {
      const map = new Map<string, { date: Date; value: number }>();
      for (const point of series) {
        const normalized = normalizeToLocalDay(point.date);
        const key = localDateKey(normalized);
        const existing = map.get(key);
        if (!existing || point.date.getTime() > existing.date.getTime()) {
          map.set(key, { date: normalized, value: point.value });
        }
      }
      return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    };

    const fmpSeries = dedupeByDate(calculateFlatEPSTTM(selectedStock, analysisDateModeEps));

    if (analysisDateModeEps === 'at-reported') {
      if (!fiscalFlatEpsTtmPoints.length) return fmpSeries;
      // Dedupe by UTC day and keep the original UTC-midnight dates so the
      // anchors stay exactly on the Fiscal earningsDate (no local-day shift).
      const utcDateKey = (d: Date) => d.toISOString().slice(0, 10);
      const map = new Map<string, { date: Date; value: number }>();
      for (const point of fiscalFlatEpsTtmPoints) {
        const key = utcDateKey(point.date);
        const existing = map.get(key);
        if (!existing || point.date.getTime() > existing.date.getTime()) {
          map.set(key, { date: point.date, value: point.value });
        }
      }
      const fiscalSeries = Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
      return fiscalSeries.length > 0 ? fiscalSeries : fmpSeries;
    }
    if (prefersFiscal && fiscalFlatEpsTtmPoints.length > 0) {
      const fiscalStart = fiscalFlatEpsTtmPoints[0].date.getTime();
      const blended = dedupeByDate([
        ...fmpSeries.filter((point) => point.date.getTime() < fiscalStart),
        ...fiscalFlatEpsTtmPoints.map((point) => ({
          date: normalizeToLocalDay(point.date),
          value: point.value,
        })),
      ]);
      return blended;
    }
    return fmpSeries;
  }, [dataLoaded, selectedStock, analysisDateModeEps, prefersFiscal, fiscalFlatEpsTtmPoints]);


  // In at-reported mode, show the quarterly EPS plus the EFFECTIVE Flat EPS (ttm)
  // at each earningsDate. The newly-reported quarter takes effect EXACTLY on its
  // earningsDate, so at the report date itself the NEW quarter's TTM is what is
  // drawn (e.g. 30-03 shows 13.11). This log matches the graph exactly.
  useEffect(() => {
    if (!chartsDebug || !dataLoaded) return;
    if (analysisDateModeEps !== 'at-reported') return;
    // Map each earningDate -> "FY{year} Q{quarter}".
    const quarterByDate = new Map<string, string>();
    fiscalIncomeQuarter.forEach((row) => {
      const d = String(row.earningDate || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
      const fy = (row as any).fiscalYear;
      const fq = (row as any).fiscalQuarter;
      if (fy != null && fq != null) quarterByDate.set(d, `FY${fy} Q${fq}`);
    });
    const labelFor = (d: string) => quarterByDate.get(d) || '';
    const q5 = [...fiscalIncomeQuarter]
      .map((row) => ({
        d: String(row.earningDate || '').slice(0, 10),
        eps: Number(row.eps ?? row.epsDiluted),
      }))
      .filter((r) => /^\d{4}-\d{2}-\d{2}$/.test(r.d) && Number.isFinite(r.eps))
      .sort((a, b) => a.d.localeCompare(b.d))
      .slice(-5)
      .map((r) => `${r.d}[${labelFor(r.d)}]:${r.eps.toFixed(2)}`);
    const last3 = flatEPSTTMData
      .slice(-3)
      .map((p, idx, arr) => {
        const d = p.date.toISOString().slice(0, 10);
        const effective = p.value;
        return `${d}[${labelFor(d)}]=${effective.toFixed(2)}`;
      });
    setQuarterEndDebugText(`q.eps= ${q5.join(' ')} || ttm@reportDate= ${last3.join(' | ')}`);
  }, [chartsDebug, dataLoaded, analysisDateModeEps, flatEPSTTMData, fiscalIncomeQuarter]);


  const ttmOcfPerShareSeries = useMemo(() => {
    if (!dataLoaded || !cashFlowTtmSeries.length || !incomeTtmSeries.length) {
      return [] as Array<{ date: Date; value: number }>;
    }

    const incomeByDateKey = new Map(incomeTtmSeries.map((row) => [toUTCDateKey(row.date), row]));

    return cashFlowTtmSeries
      .map((cashRow) => {
        const dateKey = toUTCDateKey(cashRow.date);
        const incomeRow = incomeByDateKey.get(dateKey);
        const operatingCashFlow = Number((cashRow as any)?.operatingCashFlow ?? (cashRow as any)?.netCashProvidedByOperatingActivities);
        const netIncome = Number(incomeRow?.netIncome);
        const epsDiluted = Number(incomeRow?.epsDiluted ?? incomeRow?.eps);

        if (!Number.isFinite(operatingCashFlow) || !Number.isFinite(netIncome) || !Number.isFinite(epsDiluted) || epsDiluted === 0) {
          return null;
        }

        const derivedShares = netIncome / epsDiluted;
        if (!Number.isFinite(derivedShares) || derivedShares <= 0) return null;

        const ocfPerShare = operatingCashFlow / derivedShares;
        if (!Number.isFinite(ocfPerShare)) return null;

        const filingDateStr = cashRow.filingDate || quarterlyFilingDates.get(dateKey);
        const anchorDate =
          analysisDateModeOcf === 'at-reported' && filingDateStr
            ? new Date(filingDateStr)
            : dateKeyToUTCDate(dateKey, false);

        return { date: anchorDate, value: ocfPerShare };
      })
      .filter((point): point is { date: Date; value: number } => !!point)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [analysisDateModeOcf, cashFlowTtmSeries, dataLoaded, incomeTtmSeries, quarterlyFilingDates]);

  const buildOcfPerShareSeries = useCallback((
    category: 'Annual Data' | 'Quarterly Data',
    rollingWindow: number,
    sourceField: string,
  ) => {
    const periods = getFinancialDataPeriods(category, selectedStock);
    const ocfData = getCashFlowMetricData('operatingCashFlow', category, selectedStock);
    const netIncomeData = getFinancialMetricData('netIncome', category, selectedStock);
    const epsData = getFinancialMetricData('eps', category, selectedStock);

    return periods
      .map((date, index) => {
        if (index < rollingWindow - 1) {
          return { date, value: null as number | null, sourceField };
        }

        let ocf = 0;
        let netIncome = 0;
        let eps = 0;
        for (let i = index - (rollingWindow - 1); i <= index; i++) {
          ocf += Number(ocfData[i] ?? 0);
          netIncome += Number(netIncomeData[i] ?? 0);
          eps += Number(epsData[i] ?? 0);
        }

        if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
          return { date, value: null as number | null, sourceField };
        }

        if (!Number.isFinite(ocf) || !Number.isFinite(netIncome) || !Number.isFinite(eps) || eps === 0) {
          return { date, value: null as number | null, sourceField };
        }

        const impliedShares = netIncome / eps;
        if (!Number.isFinite(impliedShares) || impliedShares <= 0) {
          return { date, value: null as number | null, sourceField };
        }

        const ocfPerShare = ocf / impliedShares;
        const periodKey = date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate =
          category === 'Quarterly Data' && analysisDateModeOcf === 'at-reported' && filingDateStr
            ? new Date(filingDateStr)
            : date;

        return {
          date: anchorDate,
          value: Number.isFinite(ocfPerShare) ? ocfPerShare : null,
          sourceField,
        };
      })
      .filter((point) => point.date instanceof Date && Number.isFinite(point.date.getTime()));
  }, [analysisDateModeOcf, quarterlyFilingDates, selectedStock]);

  const countFiniteSeries = (arr: Array<{ date: Date; value: number | null; sourceField?: string | null }>) =>
    arr.reduce((acc, p) => acc + ((p.value !== null && Number.isFinite(p.value)) ? 1 : 0), 0);

  // Calculate Flat OCF TTM (per share) data once
  const flatOCFTTMData = useMemo(() => {
    if (!dataLoaded) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    const quarterlyRollingTtmSeries = buildOcfPerShareSeries('Quarterly Data', 4, 'quarterlyRollingTtmDerived');
    let seriesToUse: Array<{ date: Date; value: number | null; sourceField?: string | null }> | null = null;
    if (fiscalOcfPerShareRollingTtm.length > 0) {
      const fmpTtmSeries = ttmOcfPerShareSeries.length > 0
        ? ttmOcfPerShareSeries.map((point) => ({
            date: point.date,
            value: point.value,
            sourceField: 'ttmStatementsDerived',
          }))
        : (countFiniteSeries(quarterlyRollingTtmSeries) > 0 ? quarterlyRollingTtmSeries : []);

      if (prefersFiscal && fmpTtmSeries.length > 0) {
        const fiscalStart = fiscalOcfPerShareRollingTtm[0].date.getTime();
        const blended = [
          ...fmpTtmSeries.filter((point) => point.date.getTime() < fiscalStart),
          ...fiscalOcfPerShareRollingTtm,
        ];
        seriesToUse = blended;
      } else {
        seriesToUse = fiscalOcfPerShareRollingTtm;
      }
    } else if (ttmOcfPerShareSeries.length > 0) {
      seriesToUse = ttmOcfPerShareSeries.map((point) => ({
        date: point.date,
        value: point.value,
        sourceField: 'ttmStatementsDerived',
      }));
    } else if (countFiniteSeries(quarterlyRollingTtmSeries) > 0) {
      seriesToUse = quarterlyRollingTtmSeries;
    }

    if (!seriesToUse) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    // Flat OCF (ttm) should use the best available per-date OCF/share values as-is.
    // Re-rolling by 4 points here can collapse visibility (especially in Annual mode).
    const finiteSeries = seriesToUse
      .filter((p): p is { date: Date; value: number; sourceField?: string | null } => p.value !== null && Number.isFinite(p.value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (finiteSeries.length === 0) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;


    const capDate = dailyPricePoints.length > 0 ? dailyPricePoints[dailyPricePoints.length - 1].date : null;
    return finiteSeries.map((p) => {
      const clampedDate = capDate && p.date.getTime() > capDate.getTime() ? capDate : p.date;
      return { date: clampedDate, value: p.value, sourceField: p.sourceField ?? null };
    });
  }, [dataLoaded, dailyPricePoints, quarterlyFilingDates, selectedStock, analysisDateModeOcf, fiscalOcfPerShareRollingTtm, ttmOcfPerShareSeries, buildOcfPerShareSeries, prefersFiscal]);

  const flatOCFTTMDataForPocf = useMemo(() => {
    if (!dataLoaded) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    const quarterlyRollingTtmSeries = buildOcfPerShareSeries('Quarterly Data', 4, 'quarterlyRollingTtmDerived');

    let seriesToUse: Array<{ date: Date; value: number | null; sourceField?: string | null }> | null = null;
    if (fiscalOcfPerShareRollingTtm.length > 0) {
      seriesToUse = fiscalOcfPerShareRollingTtm;
    } else if (ttmOcfPerShareSeries.length > 0) {
      seriesToUse = ttmOcfPerShareSeries.map((point) => ({
        date: point.date,
        value: point.value,
        sourceField: 'ttmStatementsDerived',
      }));
    } else if (countFiniteSeries(quarterlyRollingTtmSeries) > 0) {
      seriesToUse = quarterlyRollingTtmSeries;
    }

    if (!seriesToUse) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    const finiteSeries = seriesToUse
      .filter((p): p is { date: Date; value: number; sourceField?: string | null } => p.value !== null && Number.isFinite(p.value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (finiteSeries.length === 0) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    const capDate = dailyPricePoints.length > 0 ? dailyPricePoints[dailyPricePoints.length - 1].date : null;
    return finiteSeries.map((p) => {
      const clampedDate = capDate && p.date.getTime() > capDate.getTime() ? capDate : p.date;
      return { date: clampedDate, value: p.value, sourceField: p.sourceField ?? null };
    });
  }, [dataLoaded, dailyPricePoints, fiscalOcfPerShareRollingTtm, ttmOcfPerShareSeries, buildOcfPerShareSeries]);

  const fmpOnlyFlatOCFTTMDataForPocf = useMemo(() => {
    if (!dataLoaded) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;
    const quarterlyRollingTtmSeries = buildOcfPerShareSeries('Quarterly Data', 4, 'quarterlyRollingTtmDerived');

    let seriesToUse: Array<{ date: Date; value: number | null; sourceField?: string | null }> | null = null;
    if (ttmOcfPerShareSeries.length > 0) {
      seriesToUse = ttmOcfPerShareSeries.map((point) => ({
        date: point.date,
        value: point.value,
        sourceField: 'ttmStatementsDerived',
      }));
    } else if (countFiniteSeries(quarterlyRollingTtmSeries) > 0) {
      seriesToUse = quarterlyRollingTtmSeries;
    }

    if (!seriesToUse) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    const finiteSeries = seriesToUse
      .filter((p): p is { date: Date; value: number; sourceField?: string | null } => p.value !== null && Number.isFinite(p.value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (finiteSeries.length === 0) return [] as Array<{ date: Date; value: number; sourceField?: string | null }>;

    const capDate = dailyPricePoints.length > 0 ? dailyPricePoints[dailyPricePoints.length - 1].date : null;
    return finiteSeries.map((p) => {
      const clampedDate = capDate && p.date.getTime() > capDate.getTime() ? capDate : p.date;
      return { date: clampedDate, value: p.value, sourceField: p.sourceField ?? null };
    });
  }, [dataLoaded, dailyPricePoints, ttmOcfPerShareSeries, buildOcfPerShareSeries]);

  const ttmFcfPerShareSeries = useMemo(() => {
    if (!dataLoaded || !cashFlowTtmSeries.length || !incomeTtmSeries.length) {
      return [] as Array<{ date: Date; value: number }>;
    }

    const incomeByDateKey = new Map(incomeTtmSeries.map((row) => [toUTCDateKey(row.date), row]));

    return cashFlowTtmSeries
      .map((cashRow) => {
        const dateKey = toUTCDateKey(cashRow.date);
        const incomeRow = incomeByDateKey.get(dateKey);
        const freeCashFlow = Number(cashRow.freeCashFlow);
        const netIncome = Number(incomeRow?.netIncome);
        const epsDiluted = Number(incomeRow?.epsDiluted ?? incomeRow?.eps);

        if (!Number.isFinite(freeCashFlow) || !Number.isFinite(netIncome) || !Number.isFinite(epsDiluted) || epsDiluted === 0) {
          return null;
        }

        const derivedShares = netIncome / epsDiluted;
        if (!Number.isFinite(derivedShares) || derivedShares <= 0) return null;

        const fcfPerShare = freeCashFlow / derivedShares;
        if (!Number.isFinite(fcfPerShare)) return null;
        // Keep negative FCF/share as-is — downstream renders P/FCF=0 for those periods (like P/E)

        const filingDateStr = cashRow.filingDate || quarterlyFilingDates.get(dateKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : dateKeyToUTCDate(dateKey, false);
        return { date: anchorDate, value: fcfPerShare };
      })
      .filter((point): point is { date: Date; value: number } => !!point)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [dataLoaded, cashFlowTtmSeries, incomeTtmSeries, quarterlyFilingDates]);

  // Mirrors flatOCFTTMData but for Free Cash Flow.
  // Primary: derive TTM FCF/share from true TTM cash-flow and income-statement series.
  const flatFCFTTMData = useMemo(() => {
    if (!dataLoaded) return [] as Array<{ date: Date; value: number }>;

    const pricePointsFcf = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    const priceTimesFcf = pricePointsFcf.map((p) => p.date.getTime());
    const findPriceAtOrBeforeFcf = (target: Date): number | null => {
      const t = target.getTime();
      let lo = 0; let hi = priceTimesFcf.length - 1; let best = -1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (priceTimesFcf[mid] <= t) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
      }
      return best >= 0 ? pricePointsFcf[best].close : null;
    };

    // 1st priority: derive FCF/share from fiscal quarterly data.
    if (fiscalFcfPerShareRollingTtm.length > 0) return fiscalFcfPerShareRollingTtm;

    // 2nd priority: derive FCF/share from true TTM statements.
    if (ttmFcfPerShareSeries.length > 0) return ttmFcfPerShareSeries;

    // 3rd priority: reverse-engineer TTM FCF/share from pfcfRatio when the API provides it directly.
    const fromPfcfSeries = pfcfSeries
      .map((point) => {
        const ratio = Number(point.value);
        if (!(point.date instanceof Date) || !Number.isFinite(point.date.getTime()) || !Number.isFinite(ratio) || ratio <= 0) return null;
        const price = findPriceAtOrBeforeFcf(point.date);
        if (!Number.isFinite(price) || (price ?? 0) <= 0) return null;
        const fcfPerShare = (price as number) / ratio;
        if (!Number.isFinite(fcfPerShare) || fcfPerShare <= 0) return null;
        const periodKey = point.date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : point.date;
        return { date: anchorDate, value: fcfPerShare };
      })
      .filter((p): p is { date: Date; value: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (fromPfcfSeries.length > 0) return fromPfcfSeries;

    // 4th priority: fcfPerShare from key metrics.
    const fromFcfPerShareSeries = fcfPerShareSeries
      .map((point) => {
        const v = Number(point.value);
        if (!(point.date instanceof Date) || !Number.isFinite(point.date.getTime()) || !Number.isFinite(v)) return null;
        // Allow negative — downstream renders P/FCF=0 for those periods (like P/E)
        const periodKey = point.date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : point.date;
        return { date: anchorDate, value: v };
      })
      .filter((p): p is { date: Date; value: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (fromFcfPerShareSeries.length > 0) return fromFcfPerShareSeries;

    // 5th priority: derived from already-loaded financial statements.
    return fcfPerShareDerivedSeries
      .map((point) => {
        if (!Number.isFinite(point.value)) return null;
        // Allow negative — downstream renders P/FCF=0 (like P/E)
        const periodKey = point.date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : point.date;
        return { date: anchorDate, value: point.value };
      })
      .filter((p): p is { date: Date; value: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [dataLoaded, fiscalFcfPerShareRollingTtm, ttmFcfPerShareSeries, pfcfSeries, fcfPerShareSeries, fcfPerShareDerivedSeries, dailyPricePoints, quarterlyFilingDates]);

  const fmpOnlyFlatFCFTTMData = useMemo(() => {
    if (!dataLoaded) return [] as Array<{ date: Date; value: number }>;

    const pricePointsFcf = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    const priceTimesFcf = pricePointsFcf.map((p) => p.date.getTime());
    const findPriceAtOrBeforeFcf = (target: Date): number | null => {
      const t = target.getTime();
      let lo = 0; let hi = priceTimesFcf.length - 1; let best = -1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (priceTimesFcf[mid] <= t) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
      }
      return best >= 0 ? pricePointsFcf[best].close : null;
    };

    if (ttmFcfPerShareSeries.length > 0) return ttmFcfPerShareSeries;

    const fromPfcfSeries = pfcfSeries
      .map((point) => {
        const ratio = Number(point.value);
        if (!(point.date instanceof Date) || !Number.isFinite(point.date.getTime()) || !Number.isFinite(ratio) || ratio <= 0) return null;
        const price = findPriceAtOrBeforeFcf(point.date);
        if (!Number.isFinite(price) || (price ?? 0) <= 0) return null;
        const fcfPerShare = (price as number) / ratio;
        if (!Number.isFinite(fcfPerShare) || fcfPerShare <= 0) return null;
        const periodKey = point.date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : point.date;
        return { date: anchorDate, value: fcfPerShare };
      })
      .filter((p): p is { date: Date; value: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (fromPfcfSeries.length > 0) return fromPfcfSeries;

    const fromFcfPerShareSeries = fcfPerShareSeries
      .map((point) => {
        const v = Number(point.value);
        if (!(point.date instanceof Date) || !Number.isFinite(point.date.getTime()) || !Number.isFinite(v)) return null;
        const periodKey = point.date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : point.date;
        return { date: anchorDate, value: v };
      })
      .filter((p): p is { date: Date; value: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (fromFcfPerShareSeries.length > 0) return fromFcfPerShareSeries;

    return fcfPerShareDerivedSeries
      .map((point) => {
        if (!Number.isFinite(point.value)) return null;
        const periodKey = point.date.toISOString().slice(0, 10);
        const filingDateStr = quarterlyFilingDates.get(periodKey);
        const anchorDate = filingDateStr ? new Date(filingDateStr) : point.date;
        return { date: anchorDate, value: point.value };
      })
      .filter((p): p is { date: Date; value: number } => !!p)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [dataLoaded, ttmFcfPerShareSeries, pfcfSeries, fcfPerShareSeries, fcfPerShareDerivedSeries, dailyPricePoints, quarterlyFilingDates]);

  const bestPocfSeries = useMemo(() => {
    const pricePoints = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    const priceTimes = pricePoints.map((p) => p.date.getTime());

    const findPriceAtOrBefore = (target: Date): number | null => {
      const t = target.getTime();
      let lo = 0;
      let hi = priceTimes.length - 1;
      let best = -1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const tm = priceTimes[mid];
        if (tm <= t) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      if (best < 0) return null;
      return pricePoints[best].close;
    };

    const derivedDaily = flatOCFTTMDataForPocf
      .map((point) => {
        const price = findPriceAtOrBefore(point.date);
        if (!Number.isFinite(price) || (price ?? 0) <= 0 || !Number.isFinite(point.value)) {
          return null;
        }
        if (point.value <= 0) {
          return { date: point.date, value: 0 };
        }
        const ratio = (price as number) / point.value;
        if (!Number.isFinite(ratio) || ratio <= 0) return { date: point.date, value: 0 };
        return { date: point.date, value: ratio };
      })
      .filter((point): point is { date: Date; value: number } => !!point)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (derivedDaily.length > 0) return derivedDaily;

    return pocfSeries
      .filter((point): point is { date: Date; value: number; sourceField: string | null } => (
        point.date instanceof Date &&
        Number.isFinite(point.date.getTime()) &&
        typeof point.value === 'number' &&
        Number.isFinite(point.value)
      ))
      .map((point) => ({ date: point.date, value: point.value > 0 ? point.value : 0 }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [pocfSeries, flatOCFTTMDataForPocf, dailyPricePoints]);

  const fmpPocfSeries = useMemo(() => {
    const out: Array<{ date: Date; value: number }> = [];
    for (const point of pocfSeries) {
      if (!(point.date instanceof Date)) continue;
      if (!Number.isFinite(point.date.getTime())) continue;
      if (typeof point.value !== 'number' || !Number.isFinite(point.value)) continue;
      out.push({ date: point.date, value: point.value > 0 ? point.value : 0 });
    }
    return out.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [pocfSeries]);

  const bestPocfDailySeries = useMemo(() => {
    const pricePoints = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (!pricePoints.length) return [] as Array<{ date: Date; value: number }>;

    const ocfPerShareAnchors = flatOCFTTMDataForPocf
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (ocfPerShareAnchors.length > 0) {
      const result: Array<{ date: Date; value: number }> = [];
      let idx = 0;
      let lastOcfPerShare: number | null = null;
      let lastAnchorDate: Date | null = null;
      let lastAnchorSourceField: string | null = null;
      for (const pricePoint of pricePoints) {
        while (idx < ocfPerShareAnchors.length && ocfPerShareAnchors[idx].date.getTime() <= pricePoint.date.getTime()) {
          const nextPerShare = ocfPerShareAnchors[idx].value;
          lastOcfPerShare = nextPerShare;
          lastAnchorDate = ocfPerShareAnchors[idx].date;
          lastAnchorSourceField = ocfPerShareAnchors[idx].sourceField ?? null;
          idx++;
        }

        if (typeof lastOcfPerShare === 'number' && Number.isFinite(lastOcfPerShare)) {
          if (lastOcfPerShare <= 0) {
            result.push({ date: pricePoint.date, value: 0 });
          } else {
            const ratio = pricePoint.close / lastOcfPerShare;
            result.push({ date: pricePoint.date, value: Number.isFinite(ratio) && ratio > 0 ? ratio : 0 });

          }
        }
      }

      if (result.length > 0) {
        return result;
      }
    }

    return buildDailyRatioSeries(bestPocfSeries);
  }, [dailyPricePoints, flatOCFTTMDataForPocf, buildDailyRatioSeries, bestPocfSeries, selectedStock]);

  const bestPfcfSeries = useMemo(() => {
    return pfcfSeries
      .filter((point): point is { date: Date; value: number } => (
        point.date instanceof Date &&
        Number.isFinite(point.date.getTime()) &&
        typeof point.value === 'number' &&
        Number.isFinite(point.value) &&
        point.value > 0
      ))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [pfcfSeries]);

  const buildPfcfDailySeries = useCallback((
    anchors: Array<{ date: Date; value: number }>,
    fallbackSeries: Array<{ date: Date; value: number }>
  ) => {
    const pricePoints = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (!pricePoints.length) return [] as Array<{ date: Date; value: number }>;

    if (anchors.length > 0) {
      const result: Array<{ date: Date; value: number }> = [];
      let idx = 0;
      let lastFcfPerShare: number | null = null;

      for (const pricePoint of pricePoints) {
        while (idx < anchors.length && anchors[idx].date.getTime() <= pricePoint.date.getTime()) {
          lastFcfPerShare = anchors[idx].value;
          idx++;
        }
        if (typeof lastFcfPerShare === 'number' && Number.isFinite(lastFcfPerShare)) {
          if (lastFcfPerShare <= 0) {
            result.push({ date: pricePoint.date, value: 0 });
          } else {
            const ratio = pricePoint.close / lastFcfPerShare;
            result.push({ date: pricePoint.date, value: Number.isFinite(ratio) && ratio > 0 ? ratio : 0 });
          }
        }
      }

      if (result.length > 0) return result;
    }

    return buildDailyRatioSeries(fallbackSeries);
  }, [dailyPricePoints, buildDailyRatioSeries]);

  const fmpPfcfSeries = useMemo(() => {
    const out: Array<{ date: Date; value: number }> = [];
    for (const point of pfcfSeries) {
      if (!(point.date instanceof Date)) continue;
      if (!Number.isFinite(point.date.getTime())) continue;
      if (typeof point.value !== 'number' || !Number.isFinite(point.value) || point.value <= 0) continue;
      out.push({ date: point.date, value: point.value });
    }
    return out.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [pfcfSeries]);

  const bestPfcfDailySeries = useMemo(() => {
    const pricePoints = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (!pricePoints.length) return [] as Array<{ date: Date; value: number }>;

    const anchors = flatFCFTTMData;

    if (anchors.length > 0) {
      const result: Array<{ date: Date; value: number }> = [];
      let idx = 0;
      let lastFcfPerShare: number | null = null;

      for (const pricePoint of pricePoints) {
        while (idx < anchors.length && anchors[idx].date.getTime() <= pricePoint.date.getTime()) {
          lastFcfPerShare = anchors[idx].value;
          idx++;
        }
        if (typeof lastFcfPerShare === 'number' && Number.isFinite(lastFcfPerShare)) {
          if (lastFcfPerShare <= 0) {
            result.push({ date: pricePoint.date, value: 0 });
          } else {
            const ratio = pricePoint.close / lastFcfPerShare;
            result.push({ date: pricePoint.date, value: Number.isFinite(ratio) && ratio > 0 ? ratio : 0 });
          }
        }
      }

      if (result.length > 0) return result;
    }

    return buildDailyRatioSeries(bestPfcfSeries);
  }, [dailyPricePoints, flatFCFTTMData, buildDailyRatioSeries, bestPfcfSeries]);

  const fmpPocfDailySeries = useMemo(() => {
    const pricePoints = dailyPricePoints
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.close) && p.close > 0)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (!pricePoints.length) return [] as Array<{ date: Date; value: number }>;

    const ocfPerShareAnchors = fmpOnlyFlatOCFTTMDataForPocf
      .filter((p) => p.date instanceof Date && Number.isFinite(p.date.getTime()) && Number.isFinite(p.value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (ocfPerShareAnchors.length > 0) {
      const result: Array<{ date: Date; value: number }> = [];
      let idx = 0;
      let lastOcfPerShare: number | null = null;
      for (const pricePoint of pricePoints) {
        while (idx < ocfPerShareAnchors.length && ocfPerShareAnchors[idx].date.getTime() <= pricePoint.date.getTime()) {
          lastOcfPerShare = ocfPerShareAnchors[idx].value;
          idx++;
        }
        if (typeof lastOcfPerShare === 'number' && Number.isFinite(lastOcfPerShare)) {
          if (lastOcfPerShare <= 0) {
            result.push({ date: pricePoint.date, value: 0 });
          } else {
            const ratio = pricePoint.close / lastOcfPerShare;
            result.push({ date: pricePoint.date, value: Number.isFinite(ratio) && ratio > 0 ? ratio : 0 });
          }
        }
      }
      if (result.length > 0) return result;
    }

    return buildDailyRatioSeries(fmpPocfSeries);
  }, [dailyPricePoints, fmpOnlyFlatOCFTTMDataForPocf, buildDailyRatioSeries, fmpPocfSeries]);

  const fmpPfcfDailySeries = useMemo(() => {
    return buildPfcfDailySeries(fmpOnlyFlatFCFTTMData, fmpPfcfSeries);
  }, [buildPfcfDailySeries, fmpOnlyFlatFCFTTMData, fmpPfcfSeries]);

  const fiscalPocfStartDate = useMemo(() => {
    if (!prefersFiscal) return null;
    if (fiscalOcfPerShareRollingTtm.length === 0) return null;
    return fiscalOcfPerShareRollingTtm[0]?.date ?? null;
  }, [prefersFiscal, fiscalOcfPerShareRollingTtm]);

  const fiscalPfcfStartDate = useMemo(() => {
    if (!prefersFiscal) return null;
    if (fiscalFcfPerShareRollingTtm.length === 0) return null;
    return fiscalFcfPerShareRollingTtm[0]?.date ?? null;
  }, [prefersFiscal, fiscalFcfPerShareRollingTtm]);

  const blendedPocfDailySeries = useMemo(() => {
    if (!prefersFiscal) return bestPocfDailySeries;
    return spliceSeriesByStartDate(fmpPocfDailySeries, bestPocfDailySeries, fiscalPocfStartDate);
  }, [prefersFiscal, fmpPocfDailySeries, bestPocfDailySeries, fiscalPocfStartDate, spliceSeriesByStartDate]);

  const blendedPfcfDailySeries = useMemo(() => {
    if (!prefersFiscal) return bestPfcfDailySeries;
    return spliceSeriesByStartDate(fmpPfcfDailySeries, bestPfcfDailySeries, fiscalPfcfStartDate);
  }, [prefersFiscal, fmpPfcfDailySeries, bestPfcfDailySeries, fiscalPfcfStartDate, spliceSeriesByStartDate]);
  const [normalizedPEInput, setNormalizedPEInput] = useState<string>('0');
  const [normalizedPE, setNormalizedPE] = useState<number>(0);

  const [normalizedPOCFInput, setNormalizedPOCFInput] = useState<string>('0');
  const [normalizedPOCF, setNormalizedPOCF] = useState<number>(0);

  const DEFAULT_ENTRY_EXIT_RATIO = 0.05;
  const clampEntryExitRatio = useCallback((value: number | null | undefined): number => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_ENTRY_EXIT_RATIO;
    return Math.min(0.95, Math.max(DEFAULT_ENTRY_EXIT_RATIO, value));
  }, []);
  const [entryMultipleRatioEps, setEntryMultipleRatioEps] = useState<number>(DEFAULT_ENTRY_EXIT_RATIO);
  const [exitMultipleRatioEps, setExitMultipleRatioEps] = useState<number>(DEFAULT_ENTRY_EXIT_RATIO);
  const [entryMultipleRatioOcf, setEntryMultipleRatioOcf] = useState<number>(DEFAULT_ENTRY_EXIT_RATIO);
  const [exitMultipleRatioOcf, setExitMultipleRatioOcf] = useState<number>(DEFAULT_ENTRY_EXIT_RATIO);

  // When Normalized PE is 0, Fair/Entry/Exit should be disabled and not selectable.
  // Ensure any previously enabled toggles are turned off to keep UI consistent.
  useEffect(() => {
    if (normalizedPE > 0) return;

    const lockedIds = ['fair-price', 'entry-point-1', 'entry-point-2'] as const;

    setElementVisibility(prev => {
      let changed = false;
      const next: Record<string, boolean> = { ...prev };
      for (const id of lockedIds) {
        if (next[id]) {
          next[id] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setElements(prev => {
      let changed = false;
      const next = prev.map(el => {
        if (lockedIds.includes(el.id as any) && el.visible) {
          changed = true;
          return { ...el, visible: false, orderIndex: undefined };
        }
        return el;
      });
      return changed ? next : prev;
    });
  }, [normalizedPE]);

  // When Normalized P/OCF is 0, Fair/Entry/Exit (OCF) should be disabled and not selectable.
  useEffect(() => {
    if (normalizedPOCF > 0) return;

    const lockedIds = ['fair-price-ocf', 'entry-point-1-ocf', 'entry-point-2-ocf'] as const;

    setElementVisibility(prev => {
      let changed = false;
      const next: Record<string, boolean> = { ...prev };
      for (const id of lockedIds) {
        if (next[id]) {
          next[id] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setElements(prev => {
      let changed = false;
      const next = prev.map(el => {
        if (lockedIds.includes(el.id as any) && el.visible) {
          changed = true;
          return { ...el, visible: false, orderIndex: undefined };
        }
        return el;
      });
      return changed ? next : prev;
    });
  }, [normalizedPOCF]);

  const handleNormalizedPEChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setNormalizedPEInput(next);
    const parsed = Number.parseFloat(next.replace(/,/g, ''));
    setNormalizedPE(Number.isFinite(parsed) ? parsed : 0);
    updateChartsStateRef({ normalizedPEInput: next });
    saveChartsStateLocalImmediate({ normalizedPEInput: next });
    saveChartsStateImmediate({ normalizedPEInput: next });
  };

  const handleNormalizedPOCFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setNormalizedPOCFInput(next);
    const parsed = Number.parseFloat(next.replace(/,/g, ''));
    setNormalizedPOCF(Number.isFinite(parsed) ? parsed : 0);
    updateChartsStateRef({ normalizedPOCFInput: next });
    saveChartsStateLocalImmediate({ normalizedPOCFInput: next });
    saveChartsStateImmediate({ normalizedPOCFInput: next });
  };

  // Calculate Fair Price Flat EPS (ttm) data once
  const fairPriceFlatEPSTTMData = useMemo(() => {
    if (!dataLoaded) return [];
    const data = calculateFairPriceFlatEPSTTM(selectedStock, analysisDateModeEps);
    return data;
  }, [dataLoaded, selectedStock, analysisDateModeEps]);

  // Calculate Fair Price data once
  const fairPriceData = useMemo(() => {
    if (!dataLoaded) return [];
    if (flatEPSTTMData.length === 0) return [];

    // Requirement: Fair Price = EPS (TTM) × Normalized PE (no fallback).
    // When Normalized PE is 0, Fair Price should not be shown.
    if (!(normalizedPE > 0)) return [];

    const peMultiple = normalizedPE;

    return flatEPSTTMData.map(point => ({
      date: point.date,
      value: point.value * peMultiple,
    }));
  }, [dataLoaded, flatEPSTTMData, normalizedPE]);

  const fairPriceOcfData = useMemo(() => {
    if (!dataLoaded) return [];
    if (flatOCFTTMData.length === 0) return [];
    if (!(normalizedPOCF > 0)) return [];

    const pocfMultiple = normalizedPOCF;
    return flatOCFTTMData.map(point => ({
      date: point.date,
      value: point.value * pocfMultiple,
    }));
  }, [dataLoaded, flatOCFTTMData, normalizedPOCF]);

  // Calculate minimum allowed date from available data
  const minAllowedDate = useMemo(() => {
    // Charts page requirement: keep the visible timeline starting from 2011.
    // Even if we fetch older history for other screens, the Charts X-axis should
    // start at 2011 (and show the "2011" tick label).
    return new Date('2011-01-01T00:00:00.000Z');
  }, []);

  // Calculate maximum allowed date.
  // IMPORTANT: drive presets (e.g., 5D) by the last available PRICE date.
  // Some financial-derived series can extend into future periods, which would
  // cause 5D to select a future range with no price points (appears as zeros).
  const maxAllowedDate = useMemo(() => {
    const today = new Date();
    const lastPriceDate = allPriceDataWithDates.length > 0
      ? new Date(allPriceDataWithDates[allPriceDataWithDates.length - 1].timestamp)
      : (fmpPricePoints.length > 0 ? fmpPricePoints[fmpPricePoints.length - 1].date : today);
    return lastPriceDate;
  }, [allPriceDataWithDates, fmpPricePoints]);

  const [selectedRange, setSelectedRange] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<string>(minAllowedDate.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(maxAllowedDate.toISOString().split('T')[0]);
  const latestRangeRef = useRef({ startKey: startDate, endKey: endDate });
  const visibleRangeChangeSourceRef = useRef<'date-range-container' | 'prev-next-quarter-year' | 'other'>('other');
  const returnToMaxOnNextQuarterRef = useRef<string | null>(null);
  const safeStartKey = startDate || toUTCDateKey(minAllowedDate);
  const safeEndKey = endDate || toUTCDateKey(maxAllowedDate);
  const normalizedPEOverlayValue = React.useMemo(() => {
    const parsed = Number.parseFloat((normalizedPEInput || '').replace(/,/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [normalizedPEInput]);
  const normalizedPOCFOverlayValue = React.useMemo(() => {
    const parsed = Number.parseFloat((normalizedPOCFInput || '').replace(/,/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [normalizedPOCFInput]);

  useEffect(() => {
    if (!onVisibleRangeChange) return;
    if (!startDate || !endDate) return;
    onVisibleRangeChange(
      dateKeyToUTCDate(startDate, false),
      dateKeyToUTCDate(endDate, true),
      visibleRangeChangeSourceRef.current
    );
    visibleRangeChangeSourceRef.current = 'other';
  }, [startDate, endDate, onVisibleRangeChange]);

  useEffect(() => {
    latestRangeRef.current = { startKey: startDate, endKey: endDate };
  }, [startDate, endDate]);

  useEffect(() => {
    onPeriodTypeChange?.(selectedPeriodType);
  }, [selectedPeriodType, onPeriodTypeChange]);

  // Keep the selected range clamped to the actual available data boundaries.
  // maxAllowedDate can change after data loads (initially it may be "today" when prices are empty).
  // Without this, UI elements (e.g., tooltip date / custom end-date input) can show a future date
  // even though the chart is effectively capped at the last available price date.
  useEffect(() => {
    const minKey = minAllowedDate.toISOString().slice(0, 10);
    const maxKey = maxAllowedDate.toISOString().slice(0, 10);

    const clampKey = (key: string, minK: string, maxK: string): string => {
      if (!key) return minK;
      if (key < minK) return minK;
      if (key > maxK) return maxK;
      return key;
    };

    const nextStart = clampKey(startDate, minKey, maxKey);
    const nextEnd = clampKey(endDate, minKey, maxKey);

    // Ensure start <= end after clamping.
    const safeStart = nextStart > nextEnd ? minKey : nextStart;
    const safeEnd = nextStart > nextEnd ? nextEnd : nextEnd;

    if (safeStart !== startDate) setStartDate(safeStart);
    if (safeEnd !== endDate) setEndDate(safeEnd);
  }, [minAllowedDate, maxAllowedDate, startDate, endDate]);
  
  // Tooltip data state
  const [tooltipData, setTooltipData] = useState<AdvancedChartTooltipData | null>(null);

  // Filter price data with dates
  const filteredPriceDataWithDates = useMemo(() => {
    // Compare by UTC date keys (YYYY-MM-DD) to avoid timezone-related off-by-one bugs.
    const startKey = safeStartKey;
    const endKey = safeEndKey;
    
    // Find indices of first and last points in range
    const firstInRangeIdx = allPriceDataWithDates.findIndex(point => {
      const pointKey = toUTCDateKey(point.timestamp);
      return pointKey >= startKey;
    });
    
    const lastInRangeIdx = allPriceDataWithDates.findIndex(point => {
      const pointKey = toUTCDateKey(point.timestamp);
      return pointKey > endKey;
    });
    
    // Include one point before start for continuity (if exists)
    const startIdx = Math.max(0, firstInRangeIdx - 1);
    // Include one point after end for continuity (if exists), or use length if not found
    const endIdx = lastInRangeIdx === -1 ? allPriceDataWithDates.length : lastInRangeIdx + 1;
    
    const filtered = allPriceDataWithDates.slice(startIdx, endIdx);
    
    return filtered;
  }, [allPriceDataWithDates, startDate, endDate]);

  const peAttractiveness = useMemo(() => {
    if (!Array.isArray(peData) || peData.length === 0) return null;

    // Dynamic behavior: follow the currently selected chart range.
    const rangeStartMs = dateKeyToUTCDate(safeStartKey, false).getTime();
    const rangeEndMs = dateKeyToUTCDate(safeEndKey, true).getTime();

    const inRange = peData
      .filter((p) => Number.isFinite(p.date?.getTime()) && p.date.getTime() >= rangeStartMs && p.date.getTime() <= rangeEndMs)
      .map((p) => Number(p.value))
      .filter((v) => Number.isFinite(v) && v > 0);

    const source = inRange.length >= 8
      ? inRange
      : peData
          .map((p) => Number(p.value))
          .filter((v) => Number.isFinite(v) && v > 0);

    if (source.length < 8) return null;

    const sorted = [...source].sort((a, b) => a - b);
    const medianPe = quantileSorted(sorted, 0.5);
    return {
      medianPe,
    };
  }, [peData, safeStartKey, safeEndKey]);

  const pocfAttractiveness = useMemo(() => {
    if (!Array.isArray(blendedPocfDailySeries) || blendedPocfDailySeries.length === 0) return null;

    const rangeStartMs = dateKeyToUTCDate(safeStartKey, false).getTime();
    const rangeEndMs = dateKeyToUTCDate(safeEndKey, true).getTime();
    const inRange = blendedPocfDailySeries
      .filter((p) => Number.isFinite(p.date?.getTime()) && p.date.getTime() >= rangeStartMs && p.date.getTime() <= rangeEndMs)
      .map((p) => Number(p.value))
      .filter((v) => Number.isFinite(v) && v > 0);

    const source = inRange.length > 0
      ? inRange
      : blendedPocfDailySeries
          .map((p) => Number(p.value))
          .filter((v) => Number.isFinite(v) && v > 0);

    if (source.length === 0) return null;
    const sorted = [...source].sort((a, b) => a - b);
    return { medianPocf: quantileSorted(sorted, 0.5) };
  }, [blendedPocfDailySeries, safeStartKey, safeEndKey]);

  // Empty labels array - not needed anymore with time scale
  const currentLabels: string[] = [];

  // Store visibility state separately to preserve across stock changes
  const [elementVisibility, setElementVisibility] = useState<Record<string, boolean>>(
    defaultElementVisibility
  );


  const resetVisibilityDefaults = React.useCallback(() => {
    const defaults = getDefaultElementVisibility(prefsScope);
    setElementVisibility(defaults);
    setElements(prev => prev.map(element => {
      const visible = defaults[element.id] ?? false;
      return {
        ...element,
        visible,
        orderIndex: element.id === 'price-line' && visible
          ? 1
          : element.id === 'revenue' && visible
            ? 2
            : undefined,
      };
    }));
    setOrderCounter(defaults.revenue ? 3 : 2);
  }, [prefsScope]);

  const resetChartsToDefaults = React.useCallback(() => {
    const minKey = minAllowedDate.toISOString().slice(0, 10);
    const maxKey = maxAllowedDate.toISOString().slice(0, 10);
    setSelectedPeriodType('Annual Data');
    setSelectedRange('ALL');
    setStartDate(minKey);
    setEndDate(maxKey);
    setAnalysisDateModeEps('as-reported');
    setAnalysisDateModeOcf('as-reported');
    resetVisibilityDefaults();
    setNormalizedPEInput('0');
    setNormalizedPE(0);
    setNormalizedPOCFInput('0');
    setNormalizedPOCF(0);
    setEntryMultipleRatioEps(DEFAULT_ENTRY_EXIT_RATIO);
    setExitMultipleRatioEps(DEFAULT_ENTRY_EXIT_RATIO);
    setEntryMultipleRatioOcf(DEFAULT_ENTRY_EXIT_RATIO);
    setExitMultipleRatioOcf(DEFAULT_ENTRY_EXIT_RATIO);
  }, [maxAllowedDate, minAllowedDate, resetVisibilityDefaults]);

  const normalizedSymbol = (selectedStock ?? '').trim().toUpperCase();
  const symbolGuardRef = useRef(normalizedSymbol);
  const didSymbolChangeThisRender = symbolGuardRef.current !== normalizedSymbol;
  if (didSymbolChangeThisRender) {
    symbolGuardRef.current = normalizedSymbol;
  }

  useLayoutEffect(() => {
    setIsSwitchingSymbol(true);
    setChartMasked(true);
    pendingChartRevealRef.current = normalizedSymbol;
    setVisibilitySuppressed(true);
  }, [normalizedSymbol]);

  useEffect(() => {
    if (dataLoaded && loadedSymbolRef.current === normalizedSymbol) {
      setIsSwitchingSymbol(false);
    }
  }, [dataLoaded, normalizedSymbol]);

  const resolveUsernameFromToken = (token: string | null | undefined): string => {
    if (!token) return '';
    const parts = token.split('.');
    if (parts.length < 2) return '';
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const payload = JSON.parse(json) as Record<string, unknown>;
      const raw =
        payload.unique_name ??
        payload.preferred_username ??
        payload.username ??
        payload.name ??
        payload.email ??
        '';
      return String(raw || '').trim().toLowerCase();
    } catch {
      return '';
    }
  };

  const resolveUserIdFromToken = (token: string | null | undefined): number | null => {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const payload = JSON.parse(json) as Record<string, unknown>;
      const raw = payload.userId ?? payload.userid ?? payload.id ?? payload.sub ?? payload.nameid;
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (typeof raw === 'string') {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    } catch {
      return null;
    }
  };

  const resolvedUsername = normalizedUsername || resolveUsernameFromToken(authToken);
  const resolvedUserId =
    (typeof authUserId === 'number' && Number.isFinite(authUserId) && authUserId > 0)
      ? authUserId
      : resolveUserIdFromToken(authToken);
  const chartsUserIdentityKey = React.useMemo(() => {
    if (resolvedUsername) return `user:${resolvedUsername}`;
    if (typeof resolvedUserId === 'number' && Number.isFinite(resolvedUserId) && resolvedUserId > 0) {
      return `uid:${resolvedUserId}`;
    }
    return 'anon';
  }, [resolvedUsername, resolvedUserId]);
  const resolvedUserKey = React.useMemo(() => {
    return chartsUserIdentityKey;
  }, [chartsUserIdentityKey]);
  const storageSymbol = normalizedSymbol || 'UNKNOWN';

  // --- Charts auto-save (localStorage) ---
  // Persist key user selections (visible graphs, period type, date range) so a refresh keeps the same view.
  const storageKey = React.useMemo(
    () => `advanced-chart:prefs:${prefsScope}:v${ADVANCED_CHART_PREFS_STORAGE_VERSION}:${resolvedUserKey}:${storageSymbol}`,
    [prefsScope, resolvedUserKey, storageSymbol]
  );
  const storagePurgeKey = React.useMemo(
    () => `advanced-chart:purge:${storageKey}`,
    [storageKey]
  );

  const visibilityStorageKey = React.useMemo(
    () => {
      const scope = visibilityScope || prefsScope;
      return `advanced-chart:prefs:${scope}:v${ADVANCED_CHART_PREFS_STORAGE_VERSION}:${resolvedUserKey}:${storageSymbol}`;
    },
    [visibilityScope, prefsScope, resolvedUserKey, storageSymbol]
  );

  // Keep the top-right Normalized panel visibility global per user+scope,
  // so it survives symbol switches, tab switches, and logout/login cycles.
  const normalizedFieldsVisibilityStorageKey = React.useMemo(
    () => {
      const scope = visibilityScope || prefsScope;
      return `advanced-chart:normalized-fields:${scope}:v${ADVANCED_CHART_PREFS_STORAGE_VERSION}:${resolvedUserKey}`;
    },
    [visibilityScope, prefsScope, resolvedUserKey]
  );

  const didHydrateRef = useRef(false);
  const lastHydrationSourceRef = useRef<'none' | 'local' | 'server'>('none');

  const [chartsHydrationAttempt, setChartsHydrationAttempt] = useState(0);
  const chartsHydrationRetryTimerRef = useRef<number | undefined>(undefined);
  const chartsHydrationRetryCountRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(normalizedFieldsVisibilityStorageKey);
      if (raw === '1' || raw === '0') {
        setShowNoTooltipNormalizedFields(raw === '1');
        return;
      }
    } catch {
      // ignore storage errors (private mode/quota)
    }

    setShowNoTooltipNormalizedFields(true);
  }, [normalizedFieldsVisibilityStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        normalizedFieldsVisibilityStorageKey,
        showNoTooltipNormalizedFields ? '1' : '0'
      );
    } catch {
      // ignore storage errors (private mode/quota)
    }
  }, [normalizedFieldsVisibilityStorageKey, showNoTooltipNormalizedFields]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(storagePurgeKey) !== '1') return;

    window.localStorage.removeItem(storagePurgeKey);
    window.localStorage.removeItem(storageKey);
    window.localStorage.removeItem(visibilityStorageKey);

    lastLocalPayloadRef.current = null;
    lastSavedChartsPayloadRef.current = null;
    suppressChartsSaveRef.current = true;
    didHydrateRef.current = false;
    lastHydrationSourceRef.current = 'none';
    didInitChartsKeyRef.current = null;

    resetChartsToDefaults();
  }, [dataLoaded, resetChartsToDefaults, storageKey, storagePurgeKey, visibilityStorageKey]);

  // Reset hydration when symbol changes.
  useEffect(() => {
    didHydrateRef.current = false;
    lastHydrationSourceRef.current = 'none';
    didInitChartsKeyRef.current = null;
    setChartsHydrated(!isChartsScope);

    if (chartsHydrationRetryTimerRef.current) {
      window.clearTimeout(chartsHydrationRetryTimerRef.current);
      chartsHydrationRetryTimerRef.current = undefined;
    }
  }, [isChartsScope, selectedStock]);

  // Reset hydration when the authenticated user changes.
  useEffect(() => {
    didHydrateRef.current = false;
    lastHydrationSourceRef.current = 'none';
    didInitChartsKeyRef.current = null;
    setChartsHydrated(!isChartsScope);

    if (chartsHydrationRetryTimerRef.current) {
      window.clearTimeout(chartsHydrationRetryTimerRef.current);
      chartsHydrationRetryTimerRef.current = undefined;
    }
  }, [isChartsScope, resolvedUserKey]);

  // Persist user chart state to the server for main Charts and Back Testing scopes.
  const canPersistCharts =
    (prefsScope === 'Charts' || prefsScope === 'Back Testing') &&
    !!authToken &&
    normalizedSymbol.length > 0 &&
    (resolvedUserId != null || resolvedUsername.length > 0);

  const isBackTestingWithChartsBase =
    prefsScope === 'Back Testing' && (visibilityScope || prefsScope) !== prefsScope;

  // Keep BE persistence isolated by tab scope so Back Testing state never overwrites Charts state.
  const persistedServerStockSymbol = React.useMemo(() => {
    if (!normalizedSymbol) return '';
    return prefsScope === 'Back Testing'
      ? `${normalizedSymbol}::BACK_TESTING`
      : normalizedSymbol;
  }, [normalizedSymbol, prefsScope]);


  const didInitChartsKeyRef = useRef<string | null>(null);
  const suppressChartsSaveRef = useRef(false);
  const chartsSaveTimerRef = useRef<number | undefined>(undefined);
  const lastSavedChartsPayloadRef = useRef<string | null>(null);
  const lastLocalPayloadRef = useRef<string | null>(null);
  const baseScopeElementVisibilityRef = useRef<Record<string, boolean>>({});
  const chartsStateRef = useRef({
    selectedPeriodType: 'Annual Data' as 'Annual Data' | 'Quarterly Data' | 'TTM',
    selectedRange: 'ALL',
    startDate: '',
    endDate: '',
    analysisDateModeEps: 'as-reported' as FinancialDateMode,
    analysisDateModeOcf: 'as-reported' as FinancialDateMode,
    elementVisibility: {} as Record<string, boolean>,
    normalizedPEInput: '0',
    normalizedPOCFInput: '0',
    entryMultipleRatioEps: DEFAULT_ENTRY_EXIT_RATIO,
    exitMultipleRatioEps: DEFAULT_ENTRY_EXIT_RATIO,
    entryMultipleRatioOcf: DEFAULT_ENTRY_EXIT_RATIO,
    exitMultipleRatioOcf: DEFAULT_ENTRY_EXIT_RATIO,
  });

  useEffect(() => {
    chartsStateRef.current = {
      selectedPeriodType,
      selectedRange,
      startDate,
      endDate,
      analysisDateModeEps,
      analysisDateModeOcf,
      elementVisibility,
      normalizedPEInput,
      normalizedPOCFInput,
      entryMultipleRatioEps,
      exitMultipleRatioEps,
      entryMultipleRatioOcf,
      exitMultipleRatioOcf,
    };
  }, [
    selectedPeriodType,
    selectedRange,
    startDate,
    endDate,
    analysisDateModeEps,
    analysisDateModeOcf,
    elementVisibility,
    normalizedPEInput,
    normalizedPOCFInput,
    entryMultipleRatioEps,
    exitMultipleRatioEps,
    entryMultipleRatioOcf,
    exitMultipleRatioOcf,
  ]);

  const updateChartsStateRef = (partial: Partial<typeof chartsStateRef.current>) => {
    chartsStateRef.current = { ...chartsStateRef.current, ...partial };
  };

  const buildPersistableChartsState = (
    base: typeof chartsStateRef.current,
    overrides?: Partial<typeof chartsStateRef.current>
  ) => {
    const merged = {
      v: ADVANCED_CHART_PREFS_STORAGE_VERSION,
      selectedPeriodType: base.selectedPeriodType,
      selectedRange: base.selectedRange,
      startDate: base.startDate,
      endDate: base.endDate,
      analysisDateModeEps: base.analysisDateModeEps,
      analysisDateModeOcf: base.analysisDateModeOcf,
      // Backward compatibility for older clients/state readers.
      analysisDateMode: base.analysisDateModeEps,
      elementVisibility: clampRestrictedVisibility(base.elementVisibility),
      normalizedPEInput: base.normalizedPEInput,
      normalizedPOCFInput: base.normalizedPOCFInput,
      entryMultipleRatioEps: base.entryMultipleRatioEps,
      exitMultipleRatioEps: base.exitMultipleRatioEps,
      entryMultipleRatioOcf: base.entryMultipleRatioOcf,
      exitMultipleRatioOcf: base.exitMultipleRatioOcf,
      // Backward compatibility for legacy ratio fields.
      entryMultipleRatio: base.entryMultipleRatioEps,
      exitMultipleRatio: base.exitMultipleRatioEps,
      ...overrides,
    } as any;

    if (merged.elementVisibility && typeof merged.elementVisibility === 'object') {
      merged.elementVisibility = clampRestrictedVisibility(merged.elementVisibility as Record<string, boolean>);
    }

    if (isBackTestingWithChartsBase) {
      const baseVis = baseScopeElementVisibilityRef.current || {};
      const currentVis = (merged.elementVisibility && typeof merged.elementVisibility === 'object')
        ? (merged.elementVisibility as Record<string, unknown>)
        : {};
      const visOverrides: Record<string, boolean> = {};
      const keys = new Set([...Object.keys(baseVis), ...Object.keys(currentVis)]);
      keys.forEach((k) => {
        const current = typeof currentVis[k] === 'boolean' ? (currentVis[k] as boolean) : false;
        const baseValue = typeof baseVis[k] === 'boolean' ? baseVis[k] : false;
        if (current !== baseValue) visOverrides[k] = current;
      });
      merged.elementVisibility = visOverrides;
      merged.elementVisibilityMode = 'overrides';
    }

    return merged;
  };

  const saveChartsStateLocalImmediate = (overrides?: Partial<typeof chartsStateRef.current>) => {
    const base = chartsStateRef.current;
    const state = buildPersistableChartsState(base, overrides);

    const payloadJson = JSON.stringify(state);
    lastLocalPayloadRef.current = payloadJson;
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, payloadJson);
      }
    } catch {
      // ignore storage errors (private mode/quota)
    }
  };

  const saveChartsStateImmediate = (overrides?: Partial<typeof chartsStateRef.current>) => {
    if (!dataLoaded) return;
    if (!canPersistCharts) return;

    const token = authToken as string;
    const userKey = chartsUserIdentityKey;
    const chartsKey = `${userKey}:${persistedServerStockSymbol}`;

    if (didInitChartsKeyRef.current && didInitChartsKeyRef.current !== chartsKey) return;
    if (!didInitChartsKeyRef.current && lastHydrationSourceRef.current !== 'none') {
      didInitChartsKeyRef.current = chartsKey;
    }

    const base = chartsStateRef.current;
    const state = buildPersistableChartsState(base, overrides);

    const payloadKey = JSON.stringify({ userId: userKey, stockSymbol: persistedServerStockSymbol, state });
    if (payloadKey === lastSavedChartsPayloadRef.current) return;

    void (async () => {
      try {
        await adminApi.upsertMyResearchChartsState(token, {
          stockSymbol: persistedServerStockSymbol,
          state,
        });
        lastSavedChartsPayloadRef.current = payloadKey;
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
      }
    })();
  };

  useEffect(() => {
    if (!dataLoaded) return;
    const token = authToken as string;
    const userKey = chartsUserIdentityKey;

    const chartsKey = canPersistCharts ? `${userKey}:${persistedServerStockSymbol}` : null;

    // If we hydrated from local storage before userId was available, re-hydrate
    // once userId arrives so server state takes precedence.
    const shouldRehydrateFromServer =
      canPersistCharts &&
      chartsKey &&
      lastHydrationSourceRef.current === 'local' &&
      didInitChartsKeyRef.current !== chartsKey;

    if (didHydrateRef.current && !shouldRehydrateFromServer) return;

    let cancelled = false;

    const safeParse = (raw: string | null): any | null => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const buildLocalStorageKey = (scope: string, userKey: string, symbol: string) => (
      `advanced-chart:prefs:${scope}:v${ADVANCED_CHART_PREFS_STORAGE_VERSION}:${userKey}:${symbol}`
    );

    const getUserKeyCandidates = (): string[] => {
      const keys: string[] = [];
      const pushUnique = (value: string | null | undefined) => {
        if (!value) return;
        if (!keys.includes(value)) keys.push(value);
      };

      pushUnique(chartsUserIdentityKey);
      if (typeof resolvedUserId === 'number' && Number.isFinite(resolvedUserId) && resolvedUserId > 0) {
        pushUnique(`uid:${resolvedUserId}`);
      }
      if (resolvedUsername) {
        pushUnique(`user:${resolvedUsername}`);
      }
      pushUnique('anon');
      return keys;
    };

    const readLocalSavedWithFallback = (scope: string): any | null => {
      if (typeof window === 'undefined') return null;
      const candidates = getUserKeyCandidates();
      for (const userKey of candidates) {
        const key = buildLocalStorageKey(scope, userKey, storageSymbol);
        const parsed = safeParse(window.localStorage.getItem(key));
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      }
      return null;
    };

    const mergeSavedState = (baseSaved: any | null, scopedSaved: any | null): any | null => {
      if (!baseSaved && !scopedSaved) return null;
      if (!baseSaved) return scopedSaved;
      if (!scopedSaved) return baseSaved;

      const baseVis = baseSaved?.elementVisibility && typeof baseSaved.elementVisibility === 'object'
        ? (baseSaved.elementVisibility as Record<string, unknown>)
        : {};

      baseScopeElementVisibilityRef.current = Object.entries(baseVis).reduce((acc, [k, v]) => {
        if (typeof v === 'boolean') acc[k] = v;
        return acc;
      }, {} as Record<string, boolean>);

      let scopedVis: Record<string, unknown> = {};
      if (scopedSaved?.elementVisibility && typeof scopedSaved.elementVisibility === 'object') {
        if (scopedSaved?.elementVisibilityMode === 'overrides') {
          scopedVis = scopedSaved.elementVisibility as Record<string, unknown>;
        } else if (isBackTestingWithChartsBase) {
          // Legacy Back Testing payloads were full snapshots; treat only `true` as override so Charts can flow through.
          const raw = scopedSaved.elementVisibility as Record<string, unknown>;
          for (const [k, v] of Object.entries(raw)) {
            if (v === true) scopedVis[k] = true;
          }
        } else {
          scopedVis = scopedSaved.elementVisibility as Record<string, unknown>;
        }
      }

      const merged: any = {
        ...baseSaved,
        ...scopedSaved,
        elementVisibility: {
          ...baseVis,
          ...scopedVis,
        },
      };

      if (isBackTestingWithChartsBase) {
        merged.elementVisibilityMode = 'overrides';
      }

      return merged;
    };

    const clampKey = (key: string, minKey: string, maxKey: string): string => {
      if (!key) return minKey;
      if (key < minKey) return minKey;
      if (key > maxKey) return maxKey;
      return key;
    };

    const parseNormalizedInput = (value: unknown): { input: string; numeric: number } | null => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return { input: String(value), numeric: value };
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const parsed = parseFloat(trimmed.replace(/,/g, ''));
        if (!Number.isFinite(parsed)) return null;
        return { input: value, numeric: parsed };
      }
      return null;
    };

    const minKey = minAllowedDate.toISOString().slice(0, 10);
    const maxKey = maxAllowedDate.toISOString().slice(0, 10);

    const applySaved = (saved: any | null) => {
      if (!saved || typeof saved !== 'object') {
        // No saved state for this symbol: hard reset chart preferences so values
        // from a previous symbol (e.g. Normalized PE / P-OCF) never carry over.
        // This keeps a new symbol at defaults (price-only, normalized inputs = 0).
        resetChartsToDefaults();
        return;
      }

      const savedUnifiedAnalysisMode =
        saved.analysisDateModeEps ??
        saved.analysisDateModeOcf ??
        saved.analysisDateMode;
      if (savedUnifiedAnalysisMode === 'as-reported' || savedUnifiedAnalysisMode === 'at-reported') {
        setAnalysisDateModeEps(savedUnifiedAnalysisMode);
        setAnalysisDateModeOcf(savedUnifiedAnalysisMode);
      }

      const savedPeriod = saved.selectedPeriodType;
      if (savedPeriod === 'Annual Data' || savedPeriod === 'Quarterly Data' || savedPeriod === 'TTM') {
        setSelectedPeriodType(savedPeriod);
      }

      const savedRange = typeof saved.selectedRange === 'string' ? saved.selectedRange : 'ALL';
      const savedStart = typeof saved.startDate === 'string' ? saved.startDate : minKey;

      const clampedStart = clampKey(savedStart, minKey, maxKey);

      setSelectedRange(savedRange);
      setStartDate(clampedStart);
      setEndDate(maxKey);

      const vis = saved.elementVisibility;
      if (vis && typeof vis === 'object') {
        setElementVisibility((prev) => {
          const next: Record<string, boolean> = { ...prev };
          for (const [key, value] of Object.entries(vis as Record<string, unknown>)) {
            if (typeof value === 'boolean') next[key] = value;
          }
          if (visibilityScope && visibilityScope !== prefsScope && prefsScope !== 'Back Testing') {
            next['price-line'] = true;
          }
          return clampRestrictedVisibility(next);
        });
      } else {
        resetVisibilityDefaults();
      }

      const normalizedPeParsed = parseNormalizedInput((saved as any).normalizedPEInput);
      if (normalizedPeParsed) {
        setNormalizedPEInput(normalizedPeParsed.input);
        setNormalizedPE(normalizedPeParsed.numeric);
      } else {
        setNormalizedPEInput('0');
        setNormalizedPE(0);
      }

      const normalizedPocfParsed = parseNormalizedInput((saved as any).normalizedPOCFInput);
      if (normalizedPocfParsed) {
        setNormalizedPOCFInput(normalizedPocfParsed.input);
        setNormalizedPOCF(normalizedPocfParsed.numeric);
      } else {
        setNormalizedPOCFInput('0');
        setNormalizedPOCF(0);
      }

      const savedEntryEps =
        typeof saved.entryMultipleRatioEps === 'number' && Number.isFinite(saved.entryMultipleRatioEps)
          ? saved.entryMultipleRatioEps
          : typeof saved.entryMultipleRatio === 'number' && Number.isFinite(saved.entryMultipleRatio)
            ? saved.entryMultipleRatio
            : null;
      if (savedEntryEps != null) setEntryMultipleRatioEps(clampEntryExitRatio(savedEntryEps));
      else setEntryMultipleRatioEps(DEFAULT_ENTRY_EXIT_RATIO);

      const savedExitEps =
        typeof saved.exitMultipleRatioEps === 'number' && Number.isFinite(saved.exitMultipleRatioEps)
          ? saved.exitMultipleRatioEps
          : typeof saved.exitMultipleRatio === 'number' && Number.isFinite(saved.exitMultipleRatio)
            ? saved.exitMultipleRatio
            : null;
      if (savedExitEps != null) setExitMultipleRatioEps(clampEntryExitRatio(savedExitEps));
      else setExitMultipleRatioEps(DEFAULT_ENTRY_EXIT_RATIO);

      const savedEntryOcf =
        typeof saved.entryMultipleRatioOcf === 'number' && Number.isFinite(saved.entryMultipleRatioOcf)
          ? saved.entryMultipleRatioOcf
          : typeof saved.entryMultipleRatio === 'number' && Number.isFinite(saved.entryMultipleRatio)
            ? saved.entryMultipleRatio
            : null;
      if (savedEntryOcf != null) setEntryMultipleRatioOcf(clampEntryExitRatio(savedEntryOcf));
      else setEntryMultipleRatioOcf(DEFAULT_ENTRY_EXIT_RATIO);

      const savedExitOcf =
        typeof saved.exitMultipleRatioOcf === 'number' && Number.isFinite(saved.exitMultipleRatioOcf)
          ? saved.exitMultipleRatioOcf
          : typeof saved.exitMultipleRatio === 'number' && Number.isFinite(saved.exitMultipleRatio)
            ? saved.exitMultipleRatio
            : null;
      if (savedExitOcf != null) setExitMultipleRatioOcf(clampEntryExitRatio(savedExitOcf));
      else setExitMultipleRatioOcf(DEFAULT_ENTRY_EXIT_RATIO);
    };

    (async () => {
      let serverCheckedSuccessfully = false;
      let baseScopeSaved: any | null = null;
      const localSaved = safeParse(typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null);

      if ((visibilityScope || prefsScope) !== prefsScope) {
        baseScopeSaved = readLocalSavedWithFallback(visibilityScope || prefsScope);
        if (!baseScopeSaved && authToken && normalizedSymbol.length > 0) {
          try {
            const baseRes = await adminApi.getMyResearchChartsState(authToken, normalizedSymbol);
            if (cancelled) return;
            if (baseRes && (baseRes as any).state) {
              baseScopeSaved = (baseRes as any).state;
            }
          } catch (e: any) {
            if (cancelled) return;
            if (isUnauthorized(e)) {
              onUnauthorized?.();
              return;
            }
          }
        }
      }

      // Prefer server state if connected.
      if (canPersistCharts && chartsKey && didInitChartsKeyRef.current !== chartsKey) {
        try {
          const res = await adminApi.getMyResearchChartsState(token, persistedServerStockSymbol);
          if (cancelled) return;

          serverCheckedSuccessfully = true;

          // If the server has a saved state (updatedAtUtc not null), use it.
          if (res && (res as any).updatedAtUtc) {
            suppressChartsSaveRef.current = true;
            // Preserve locally saved fields that older server payloads do not yet
            // contain, while keeping server values authoritative when present.
            const serverSaved = mergeSavedState(baseScopeSaved, (res as any).state);
            const mergedForHydration = mergeSavedState(localSaved, serverSaved) || {};
            if (localSaved && typeof localSaved === 'object') {
              if (typeof (localSaved as any).normalizedPEInput === 'string') {
                (mergedForHydration as any).normalizedPEInput = (localSaved as any).normalizedPEInput;
              }
              if (typeof (localSaved as any).normalizedPOCFInput === 'string') {
                (mergedForHydration as any).normalizedPOCFInput = (localSaved as any).normalizedPOCFInput;
              }
            }
            applySaved(mergedForHydration);
            didInitChartsKeyRef.current = chartsKey;
            lastHydrationSourceRef.current = 'server';
            didHydrateRef.current = true;
            setChartsHydrated(true);

            // Reset retry counter on success.
            chartsHydrationRetryCountRef.current[chartsKey] = 0;
            if (chartsHydrationRetryTimerRef.current) {
              window.clearTimeout(chartsHydrationRetryTimerRef.current);
              chartsHydrationRetryTimerRef.current = undefined;
            }
            return;
          }
        } catch (e: any) {
          if (cancelled) return;
          if (isUnauthorized(e)) {
            onUnauthorized?.();
            return;
          }

          // Fall back to localStorage on errors, but do NOT mark hydration as initialized.
          // Otherwise we can get stuck on stale local state and never re-fetch from DB.
          const retriesSoFar = chartsKey ? (chartsHydrationRetryCountRef.current[chartsKey] ?? 0) : 0;
          if (retriesSoFar === 0) {
            onNotify?.(`${normalizedSymbol} • Charts • Failed loading (using local)`, 'error');
          }
        }
      }

      const saved = readLocalSavedWithFallback(prefsScope);
      const mergedSaved = mergeSavedState(baseScopeSaved, saved);
      applySaved(mergedSaved);
      lastHydrationSourceRef.current = mergedSaved ? 'local' : lastHydrationSourceRef.current;
      didHydrateRef.current = true;
      setChartsHydrated(true);

      if (canPersistCharts && chartsKey) {
        if (serverCheckedSuccessfully) {
          // Server responded (even if it had no saved state). Safe to enable server saves.
          didInitChartsKeyRef.current = chartsKey;
          chartsHydrationRetryCountRef.current[chartsKey] = 0;
          return;
        }

        // Server fetch failed: retry a few times so DB state eventually wins.
        const maxRetries = 3;
        const retriesSoFar = chartsHydrationRetryCountRef.current[chartsKey] ?? 0;
        if (retriesSoFar < maxRetries) {
          chartsHydrationRetryCountRef.current[chartsKey] = retriesSoFar + 1;
          if (chartsHydrationRetryTimerRef.current) {
            window.clearTimeout(chartsHydrationRetryTimerRef.current);
          }
          chartsHydrationRetryTimerRef.current = window.setTimeout(() => {
            setChartsHydrationAttempt((x) => x + 1);
          }, 900 + retriesSoFar * 600);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dataLoaded,
    storageKey,
    minAllowedDate,
    maxAllowedDate,
    authToken,
    authUserId,
    authUsername,
    canPersistCharts,
    isBackTestingWithChartsBase,
    normalizedSymbol,
    persistedServerStockSymbol,
    resolvedUsername,
    resolvedUserId,
    prefsScope,
    visibilityScope,
    visibilityStorageKey,
    chartsHydrationAttempt,
    onNotify,
    onUnauthorized,
    resetChartsToDefaults,
    resetVisibilityDefaults,
  ]);

  useEffect(() => {
    if (!dataLoaded) return;
    if ((visibilityScope || prefsScope) === prefsScope) return;
    if (prefsScope === 'Back Testing') return;

    const safeParse = (raw: string | null): any | null => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    let cancelled = false;

    const applySavedState = (saved: any | null) => {
      if (!saved || typeof saved !== 'object') return;

      const parseNormalizedInput = (value: unknown): { input: string; numeric: number } | null => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          return { input: String(value), numeric: value };
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return null;
          const parsed = parseFloat(trimmed.replace(/,/g, ''));
          if (!Number.isFinite(parsed)) return null;
          return { input: value, numeric: parsed };
        }
        return null;
      };

      const minKey = minAllowedDate.toISOString().slice(0, 10);
      const maxKey = maxAllowedDate.toISOString().slice(0, 10);
      const clampKey = (key: string, minK: string, maxK: string): string => {
        if (!key) return minK;
        if (key < minK) return minK;
        if (key > maxK) return maxK;
        return key;
      };

      const savedPeriod = saved?.selectedPeriodType;
      if (savedPeriod === 'Annual Data' || savedPeriod === 'Quarterly Data' || savedPeriod === 'TTM') {
        setSelectedPeriodType(savedPeriod);
      }

      const savedUnifiedAnalysisMode =
        saved?.analysisDateModeEps ??
        saved?.analysisDateModeOcf ??
        saved?.analysisDateMode;
      if (savedUnifiedAnalysisMode === 'as-reported' || savedUnifiedAnalysisMode === 'at-reported') {
        setAnalysisDateModeEps(savedUnifiedAnalysisMode);
        setAnalysisDateModeOcf(savedUnifiedAnalysisMode);
      }

      const savedRange = typeof saved?.selectedRange === 'string' ? saved.selectedRange : null;
      const savedStart = typeof saved?.startDate === 'string' ? saved.startDate : null;
      if (savedRange && savedStart) {
        const clampedStart = clampKey(savedStart, minKey, maxKey);
        setSelectedRange(savedRange);
        setStartDate(clampedStart);
        setEndDate(maxKey);
      }

      const vis = saved?.elementVisibility;
      if (vis && typeof vis === 'object') {
        setElementVisibility((prev) => {
          const next: Record<string, boolean> = { ...prev };
          for (const [key, value] of Object.entries(vis as Record<string, unknown>)) {
            if (typeof value === 'boolean') next[key] = value;
          }
          if (visibilityScope && visibilityScope !== prefsScope) {
            next['price-line'] = true;
          }
          return clampRestrictedVisibility(next);
        });
      } else {
        resetVisibilityDefaults();
      }

      const normalizedPeParsed = parseNormalizedInput(saved?.normalizedPEInput);
      if (normalizedPeParsed) {
        setNormalizedPEInput(normalizedPeParsed.input);
        setNormalizedPE(normalizedPeParsed.numeric);
      } else {
        setNormalizedPEInput('0');
        setNormalizedPE(0);
      }

      const normalizedPocfParsed = parseNormalizedInput(saved?.normalizedPOCFInput);
      if (normalizedPocfParsed) {
        setNormalizedPOCFInput(normalizedPocfParsed.input);
        setNormalizedPOCF(normalizedPocfParsed.numeric);
      } else {
        setNormalizedPOCFInput('0');
        setNormalizedPOCF(0);
      }

      const savedEntryEps =
        typeof saved?.entryMultipleRatioEps === 'number' && Number.isFinite(saved.entryMultipleRatioEps)
          ? saved.entryMultipleRatioEps
          : typeof saved?.entryMultipleRatio === 'number' && Number.isFinite(saved.entryMultipleRatio)
            ? saved.entryMultipleRatio
            : null;
      if (savedEntryEps != null) setEntryMultipleRatioEps(clampEntryExitRatio(savedEntryEps));
      else setEntryMultipleRatioEps(DEFAULT_ENTRY_EXIT_RATIO);

      const savedExitEps =
        typeof saved?.exitMultipleRatioEps === 'number' && Number.isFinite(saved.exitMultipleRatioEps)
          ? saved.exitMultipleRatioEps
          : typeof saved?.exitMultipleRatio === 'number' && Number.isFinite(saved.exitMultipleRatio)
            ? saved.exitMultipleRatio
            : null;
      if (savedExitEps != null) setExitMultipleRatioEps(clampEntryExitRatio(savedExitEps));
      else setExitMultipleRatioEps(DEFAULT_ENTRY_EXIT_RATIO);

      const savedEntryOcf =
        typeof saved?.entryMultipleRatioOcf === 'number' && Number.isFinite(saved.entryMultipleRatioOcf)
          ? saved.entryMultipleRatioOcf
          : typeof saved?.entryMultipleRatio === 'number' && Number.isFinite(saved.entryMultipleRatio)
            ? saved.entryMultipleRatio
            : null;
      if (savedEntryOcf != null) setEntryMultipleRatioOcf(clampEntryExitRatio(savedEntryOcf));
      else setEntryMultipleRatioOcf(DEFAULT_ENTRY_EXIT_RATIO);

      const savedExitOcf =
        typeof saved?.exitMultipleRatioOcf === 'number' && Number.isFinite(saved.exitMultipleRatioOcf)
          ? saved.exitMultipleRatioOcf
          : typeof saved?.exitMultipleRatio === 'number' && Number.isFinite(saved.exitMultipleRatio)
            ? saved.exitMultipleRatio
            : null;
      if (savedExitOcf != null) setExitMultipleRatioOcf(clampEntryExitRatio(savedExitOcf));
      else setExitMultipleRatioOcf(DEFAULT_ENTRY_EXIT_RATIO);
    };

    const saved = safeParse(typeof window !== 'undefined' ? window.localStorage.getItem(visibilityStorageKey) : null);
    if (saved) {
      applySavedState(saved);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      if (!authToken || normalizedSymbol.length === 0) return;
      if (resolvedUserId == null && resolvedUsername.length === 0) return;

      try {
        const res = await adminApi.getMyResearchChartsState(authToken, normalizedSymbol);
        if (cancelled) return;
        if (res && (res as any).state) {
          applySavedState((res as any).state);
        }
      } catch (e: any) {
        if (cancelled) return;
        if (isUnauthorized(e)) {
          onUnauthorized?.();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dataLoaded,
    prefsScope,
    visibilityScope,
    visibilityStorageKey,
    minAllowedDate,
    maxAllowedDate,
    authToken,
    normalizedSymbol,
    resolvedUserId,
    resolvedUsername,
    onUnauthorized,
    resetVisibilityDefaults,
  ]);

  const applyUnifiedAnalysisDateMode = (mode: FinancialDateMode) => {
    // Debug: track As/At toggle events from UI.
    // eslint-disable-next-line no-console
    console.log('Apply analysis date mode', { selectedStock, mode, prefsScope });
    updateChartsStateRef({ analysisDateModeEps: mode, analysisDateModeOcf: mode });
    setAnalysisDateModeEps(mode);
    setAnalysisDateModeOcf(mode);
    onAnalysisDateModeChange?.(mode);
    saveChartsStateLocalImmediate({ analysisDateModeEps: mode, analysisDateModeOcf: mode });
    saveChartsStateImmediate({ analysisDateModeEps: mode, analysisDateModeOcf: mode });
  };

  useEffect(() => {
    const mode: FinancialDateMode =
      analysisDateModeEps === 'at-reported' || analysisDateModeOcf === 'at-reported'
        ? 'at-reported'
        : 'as-reported';
    onAnalysisDateModeChange?.(mode);
  }, [analysisDateModeEps, analysisDateModeOcf, onAnalysisDateModeChange]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (!didHydrateRef.current) return;

    const payload = buildPersistableChartsState(chartsStateRef.current);

    const payloadJson = JSON.stringify(payload);
    lastLocalPayloadRef.current = payloadJson;

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, payloadJson);
      }
    } catch {
      // ignore storage errors (private mode/quota)
    }
  }, [
    dataLoaded,
    storageKey,
    selectedPeriodType,
    selectedRange,
    startDate,
    endDate,
    analysisDateModeEps,
    analysisDateModeOcf,
    elementVisibility,
    normalizedPEInput,
    normalizedPOCFInput,
    entryMultipleRatioEps,
    exitMultipleRatioEps,
    entryMultipleRatioOcf,
    exitMultipleRatioOcf,
  ]);

  useEffect(() => {
    return () => {
      if (!lastLocalPayloadRef.current) return;
      try {
        if (typeof window !== 'undefined') {
          const shouldPurge = window.localStorage.getItem(storagePurgeKey) === '1';
          if (shouldPurge) {
            window.localStorage.removeItem(storagePurgeKey);
            return;
          }
          window.localStorage.setItem(storageKey, lastLocalPayloadRef.current);
        }
      } catch {
        // ignore storage errors (private mode/quota)
      }
    };
  }, [storageKey, storagePurgeKey]);

  // Debounced save to server (research-charts/my/state)
  useEffect(() => {
    if (!dataLoaded) return;
    if (!canPersistCharts) return;
    if (!didHydrateRef.current) return;

    const token = authToken as string;
    const userKey = chartsUserIdentityKey;
    const chartsKey = `${userKey}:${persistedServerStockSymbol}`;
    if (didInitChartsKeyRef.current !== chartsKey) return;

    if (suppressChartsSaveRef.current) {
      suppressChartsSaveRef.current = false;
      return;
    }

    if (chartsSaveTimerRef.current) {
      window.clearTimeout(chartsSaveTimerRef.current);
    }

    const state = buildPersistableChartsState(chartsStateRef.current);

    const payloadKey = JSON.stringify({ userId: userKey, stockSymbol: persistedServerStockSymbol, state });
    if (payloadKey === lastSavedChartsPayloadRef.current) {
      return;
    }

    chartsSaveTimerRef.current = window.setTimeout(async () => {
      try {
        await adminApi.upsertMyResearchChartsState(token, {
          stockSymbol: persistedServerStockSymbol,
          state,
        });
        lastSavedChartsPayloadRef.current = payloadKey;
        onNotify?.(`${normalizedSymbol} • Charts • Saved`, 'success');
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        onNotify?.(`${normalizedSymbol} • Charts • Failed saving (kept locally)`, 'error');
      }
    }, 650);

    return () => {
      if (chartsSaveTimerRef.current) window.clearTimeout(chartsSaveTimerRef.current);
    };
  }, [
    dataLoaded,
    authToken,
    authUserId,
    authUsername,
    canPersistCharts,
    isBackTestingWithChartsBase,
    normalizedSymbol,
    persistedServerStockSymbol,
    chartsUserIdentityKey,
    resolvedUsername,
    resolvedUserId,
    selectedPeriodType,
    selectedRange,
    startDate,
    endDate,
    analysisDateModeEps,
    analysisDateModeOcf,
    elementVisibility,
    normalizedPEInput,
    normalizedPOCFInput,
    entryMultipleRatioEps,
    exitMultipleRatioEps,
    entryMultipleRatioOcf,
    exitMultipleRatioOcf,
    onUnauthorized,
    onNotify,
  ]);

  const [elements, setElements] = useState<ChartElement[]>([
    {
      id: 'price-line',
      name: 'Price',
      type: 'line',
      data: [],
      color: '#3b82f6',
      visible: true,
      adjustment: 0,
      orderIndex: 1,
    },
    {
      id: 'revenue',
      name: 'Revenue',
      type: 'line',
      data: [],
      color: '#10b981',
      visible: defaultElementVisibility.revenue,
      adjustment: 0,
      orderIndex: defaultElementVisibility.revenue ? 2 : undefined,
    },
    {
      id: 'gross-profit',
      name: 'Gross Profit',
      type: 'line',
      data: [],
      color: '#8b5cf6',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'operating-income',
      name: 'Operating Income',
      type: 'line',
      data: [],
      color: '#ea580c',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'pretax-income',
      name: 'Pretax Income',
      type: 'line',
      data: [],
      color: '#06b6d4',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'eps-diluted',
      name: 'EPS (Diluted)',
      type: 'line',
      data: [],
      color: '#10b981',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'cash-equivalents',
      name: 'Cash and Cash Equivalents',
      type: 'line',
      data: [],
      color: '#22d3ee',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'total-assets',
      name: 'Total Assets',
      type: 'line',
      data: [],
      color: '#14b8a6',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'short-term-debt',
      name: 'Short-Term Debt',
      type: 'line',
      data: [],
      color: '#a855f7',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'long-term-debt',
      name: 'Long-Term Debt',
      type: 'line',
      data: [],
      color: '#ef4444',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'total-liabilities',
      name: 'Total Liabilities',
      type: 'line',
      data: [],
      color: '#6366f1', // Indigo
      visible: false,
      adjustment: 0,
    },
    {
      id: 'shareholders-equity',
      name: "Total Shareholders' Equity",
      type: 'line',
      data: [],
      color: '#059669', // Emerald-600
      visible: false,
      adjustment: 0,
    },
    {
      id: 'operating-cash-flow',
      name: 'Operating Cash Flow',
      type: 'line',
      data: [],
      color: '#f59e0b', // Amber
      visible: false,
      adjustment: 0,
    },
    {
      id: 'free-cash-flow',
      name: 'Free Cash Flow',
      type: 'line',
      data: [],
      color: '#10b981', // Green
      visible: false,
      adjustment: 0,
    },
    {
      id: 'dividends',
      name: 'Dividends',
      type: 'line',
      data: [],
      color: '#8b5cf6', // Purple
      visible: false,
      adjustment: 0,
    },
    {
      id: 'buybacks',
      name: 'Buybacks',
      type: 'line',
      data: [],
      color: '#14b8a6', // Teal
      visible: false,
      adjustment: 0,
    },
    {
      id: 'gross-profit-margin',
      name: 'Gross Profit Margin',
      type: 'line',
      data: [],
      color: '#ec4899', // Pink
      visible: false,
      adjustment: 0,
    },
    {
      id: 'operating-margin',
      name: 'Operating Margin',
      type: 'line',
      data: [],
      color: '#8b5cf6', // Purple
      visible: false,
      adjustment: 0,
    },
    {
      id: 'net-margin',
      name: 'Net Margin',
      type: 'line',
      data: [],
      color: '#06b6d4', // Cyan
      visible: false,
      adjustment: 0,
    },
    {
      id: 'operating-cf-margin',
      name: 'Operating Cash Flow Margin',
      type: 'line',
      data: [],
      color: '#f59e0b', // Orange
      visible: false,
      adjustment: 0,
    },
    {
      id: 'free-cf-margin',
      name: 'Free Cash Flow Margin',
      type: 'line',
      data: [],
      color: '#84cc16', // Lime green
      visible: false,
      adjustment: 0,
    },
    {
      id: 'operating-cf-to-ni',
      name: 'Operating CF to Net Income',
      type: 'line',
      data: [],
      color: '#8b5cf6', // Purple/Violet
      visible: false,
      adjustment: 0,
    },
    {
      id: 'free-cf-to-ni',
      name: 'Free Cash Flow / Net Income',
      type: 'line',
      data: [],
      color: '#14b8a6', // Teal
      visible: false,
      adjustment: 0,
    },
    {
      id: 'roic',
      name: 'ROIC (Return on Invested Capital)',
      type: 'line',
      data: [],
      color: '#dc2626', // Red
      visible: false,
      adjustment: 0,
    },
    {
      id: 'flat-eps-ttm',
      name: 'Flat EPS (ttm)',
      type: 'line',
      data: [],
      color: '#f97316',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'flat-ocf-ttm',
      name: 'Flat OCF/Share (ttm)',
      type: 'line',
      data: [],
      color: '#22c55e',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'fair-price',
      name: 'Fair Price',
      type: 'line',
      data: [],
      color: '#8b5cf6', // Purple
      visible: false,
      adjustment: 0,
    },
    {
      id: 'fair-price-ocf',
      name: 'Fair Price',
      type: 'line',
      data: [],
      color: '#6366f1',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'entry-point-1',
      name: 'Entry Price',
      type: 'line',
      data: [],
      color: '#10b981',
      visible: false,
      adjustment: 0.2,
    },
    {
      id: 'entry-point-1-ocf',
      name: 'Entry Price',
      type: 'line',
      data: [],
      color: '#0ea5e9',
      visible: false,
      adjustment: 0.2,
    },
    {
      id: 'entry-point-2',
      name: 'Exit Price',
      type: 'line',
      data: [],
      color: '#ef4444',
      visible: false,
      adjustment: 0.2,
    },
    {
      id: 'entry-point-2-ocf',
      name: 'Exit Price',
      type: 'line',
      data: [],
      color: '#f97316',
      visible: false,
      adjustment: 0.2,
    },
    {
      id: 'fair-price-flat-eps-ttm',
      name: 'Fair Price Flat EPS (ttm)',
      type: 'line',
      data: [],
      color: '#06b6d4',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'pe',
      name: 'PE',
      type: 'line',
      data: [],
      color: '#000000',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'p-fcf-ratio',
      name: 'P/FCF',
      type: 'line',
      data: [],
      color: '#38bdf8',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'p-ocf-ratio',
      name: 'P/OCF',
      type: 'line',
      data: [],
      color: '#f472b6',
      visible: false,
      adjustment: 0,
    },
    {
      id: 'net-income',
      name: 'Net Income',
      type: 'line',
      data: [],
      color: '#f59e0b',
      visible: false,
      adjustment: 0,
    }
  ]);

  React.useEffect(() => {
    setElements(prev => prev.map(element => {
      // Preserve visibility from separate state
      const baseVisible = elementVisibility[element.id] ?? element.visible;
      const visible =
        isRestrictedAnalysisUser && hiddenAnalysisElementIdSet.has(element.id)
          ? false
          : baseVisible;
      switch (element.id) {
        case 'price-line':
          // Convert price data to {x, y} format with timestamps
          const start = new Date(safeStartKey);
          const end = new Date(safeEndKey);
          const pricePoints = dailyPricePoints.length > 0 ? dailyPricePoints : fmpPricePoints;
          const allPricePoints = pricePoints.map((point) => ({
            x: point.date,
            y: point.close,
          }));
          const priceDataWithTimestamps = (() => {
            if (!allPricePoints.length) return [] as Array<{ x: Date; y: number }>;
            const inRange = allPricePoints.filter((p) => p.x >= start && p.x <= end);
            if (inRange.length > 0) return inRange;
            return allPricePoints;
          })();
          // In short ranges, render as a clean line (no point markers).
          const hidePointsRanges = new Set(['5D', '1M', '3M']);
          return {
            ...element,
            data: priceDataWithTimestamps as any,
            useTimeScale: false,
            visible,
            showPoints: !hidePointsRanges.has(selectedRange),
          } as any;
        case 'revenue':
          if (financialData.revenue.length > 0 && financialData.periods.length > 0) {
            // Convert revenue data to {x, y} format with periods as dates
            const allRevenueDataWithYears = financialData.periods.map((date, idx) => ({
              x: date, // Use actual period date (last day of quarter/year)
              y: financialData.revenue[idx]
            }));

            // Filter revenue data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            // Find all points that fall within the range or just before it
            const pointsBeforeEnd = allRevenueDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allRevenueDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredRevenueData;
            
            if (pointsInRange.length === 0) {
              // No points in range - take the last 2 points before end date
              if (pointsBeforeEnd.length >= 2) {
                filteredRevenueData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allRevenueDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredRevenueData = allRevenueDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredRevenueData = allRevenueDataWithYears.slice(0, Math.min(2, allRevenueDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              // Only 1 point in range - add the point just before the start date for line continuity
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredRevenueData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              // 2 or more points in range - use them as is
              filteredRevenueData = pointsInRange;
            }
            return { ...element, data: filteredRevenueData as any, useTimeScale: true, visible };
          }
          return { ...element, data: [], useTimeScale: true, visible };
        case 'gross-profit':
          if (financialData.grossProfit && financialData.grossProfit.length > 0 && financialData.periods.length > 0) {
            // Convert gross profit data to {x, y} format with periods as dates
            const allGrossProfitDataWithYears = financialData.periods.map((date, idx) => ({
              x: date, // Use actual period date (last day of quarter/year)
              y: financialData.grossProfit[idx]
            }));

            // Filter gross profit data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            // Find all points that fall within the range or just before it
            const pointsBeforeEnd = allGrossProfitDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allGrossProfitDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredGrossProfitData;
            
            if (pointsInRange.length === 0) {
              // No points in range - take the last 2 points before end date
              if (pointsBeforeEnd.length >= 2) {
                filteredGrossProfitData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allGrossProfitDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredGrossProfitData = allGrossProfitDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredGrossProfitData = allGrossProfitDataWithYears.slice(0, Math.min(2, allGrossProfitDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              // Only 1 point in range - add the point just before the start date for line continuity
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredGrossProfitData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              // 2 or more points in range - use them as is
              filteredGrossProfitData = pointsInRange;
            }
            return { ...element, data: filteredGrossProfitData as any, useTimeScale: true, visible };
          }
          return { ...element, data: [], useTimeScale: true, visible };
        case 'operating-income':
          if (financialData.operatingIncome && financialData.operatingIncome.length > 0 && financialData.periods.length > 0) {
            // Convert operating income data to {x, y} format with periods as dates
            const allOperatingIncomeDataWithYears = financialData.periods.map((date, idx) => ({
              x: date, // Use actual period date (last day of quarter/year)
              y: financialData.operatingIncome[idx]
            }));

            // Filter operating income data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            // Find all points that fall within the range or just before it
            const pointsBeforeEnd = allOperatingIncomeDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allOperatingIncomeDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredOperatingIncomeData;
            
            if (pointsInRange.length === 0) {
              // No points in range - take the last 2 points before end date
              if (pointsBeforeEnd.length >= 2) {
                filteredOperatingIncomeData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allOperatingIncomeDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredOperatingIncomeData = allOperatingIncomeDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredOperatingIncomeData = allOperatingIncomeDataWithYears.slice(0, Math.min(2, allOperatingIncomeDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              // Only 1 point in range - add the point just before the start date for line continuity
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredOperatingIncomeData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              // 2 or more points in range - use them as is
              filteredOperatingIncomeData = pointsInRange;
            }
            return { ...element, data: filteredOperatingIncomeData as any, useTimeScale: true, visible, yAxisID: 'y1' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y1' };
        case 'pretax-income':
          if (financialData.pretaxIncome && financialData.pretaxIncome.length > 0 && financialData.periods.length > 0) {
            // Convert pretax income data to {x, y} format with periods as dates
            const allPretaxIncomeDataWithYears = financialData.periods.map((date, idx) => ({
              x: date, // Use actual period date (last day of quarter/year)
              y: financialData.pretaxIncome[idx]
            }));

            // Filter pretax income data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            // Find all points that fall within the range or just before it
            const pointsBeforeEnd = allPretaxIncomeDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allPretaxIncomeDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredPretaxIncomeData;
            
            if (pointsInRange.length === 0) {
              // No points in range - take the last 2 points before end date
              if (pointsBeforeEnd.length >= 2) {
                filteredPretaxIncomeData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allPretaxIncomeDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredPretaxIncomeData = allPretaxIncomeDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredPretaxIncomeData = allPretaxIncomeDataWithYears.slice(0, Math.min(2, allPretaxIncomeDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              // Only 1 point in range - add the point just before the start date for line continuity
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredPretaxIncomeData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              // 2 or more points in range - use them as is
              filteredPretaxIncomeData = pointsInRange;
            }
            return { ...element, data: filteredPretaxIncomeData as any, useTimeScale: true, visible };
          }
          return { ...element, data: [], useTimeScale: true, visible };
        case 'eps-diluted':
          if (financialData.eps && financialData.eps.length > 0 && financialData.periods.length > 0) {
            // EPS (Diluted) stays anchored to the fiscal period end in both date modes.
            const allEpsDataWithYears = financialData.periods.map((date, idx) => ({
              x: date,
              y: financialData.eps[idx]
            }));

            // Filter EPS data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            // Find all points that fall within the range or just before it
            const pointsBeforeEnd = allEpsDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allEpsDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredEpsData;
            
            if (pointsInRange.length === 0) {
              // No points in range - take the last 2 points before end date
              if (pointsBeforeEnd.length >= 2) {
                filteredEpsData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allEpsDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredEpsData = allEpsDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredEpsData = allEpsDataWithYears.slice(0, Math.min(2, allEpsDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              // Only 1 point in range - add the point just before the start date for line continuity
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredEpsData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              // 2 or more points in range - use them as is
              filteredEpsData = pointsInRange;
            }
            return { ...element, data: filteredEpsData as any, useTimeScale: true, visible, yAxisID: 'y3' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y3' };
        case 'cash-equivalents':
          if (financialData.cash && financialData.cash.length > 0 && financialData.periods.length > 0) {
            // Convert Cash data to {x, y} format with periods as dates
            const allCashDataWithYears = financialData.periods.map((date, idx) => ({
              x: date,
              y: financialData.cash[idx]
            }));

            // Filter Cash data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            const pointsBeforeEnd = allCashDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allCashDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredCashData;
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredCashData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allCashDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredCashData = allCashDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredCashData = allCashDataWithYears.slice(0, Math.min(2, allCashDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredCashData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredCashData = pointsInRange;
            }
            return { ...element, data: filteredCashData as any, useTimeScale: true, visible, yAxisID: 'y4' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y4' };
        case 'total-assets':
          if (financialData.totalAssets && financialData.totalAssets.length > 0 && financialData.periods.length > 0) {
            // Convert Total Assets data to {x, y} format with periods as dates
            const allTotalAssetsDataWithYears = financialData.periods.map((date, idx) => ({
              x: date,
              y: financialData.totalAssets[idx]
            }));

            // Filter Total Assets data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            const pointsBeforeEnd = allTotalAssetsDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allTotalAssetsDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredTotalAssetsData;
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredTotalAssetsData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allTotalAssetsDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredTotalAssetsData = allTotalAssetsDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredTotalAssetsData = allTotalAssetsDataWithYears.slice(0, Math.min(2, allTotalAssetsDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredTotalAssetsData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredTotalAssetsData = pointsInRange;
            }
            return { ...element, data: filteredTotalAssetsData as any, useTimeScale: true, visible, yAxisID: 'y5' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y5' };
        case 'short-term-debt':
          if (financialData.shortTermDebt && financialData.shortTermDebt.length > 0 && financialData.periods.length > 0) {
            // Convert Short-Term Debt data to {x, y} format with periods as dates
            const allShortTermDebtDataWithYears = financialData.periods.map((date, idx) => ({
              x: date,
              y: financialData.shortTermDebt[idx]
            }));

            // Filter Short-Term Debt data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            const pointsBeforeEnd = allShortTermDebtDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allShortTermDebtDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredShortTermDebtData;
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredShortTermDebtData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allShortTermDebtDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredShortTermDebtData = allShortTermDebtDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredShortTermDebtData = allShortTermDebtDataWithYears.slice(0, Math.min(2, allShortTermDebtDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredShortTermDebtData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredShortTermDebtData = pointsInRange;
            }
            return { ...element, data: filteredShortTermDebtData as any, useTimeScale: true, visible, yAxisID: 'y6' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y6' };
        case 'long-term-debt':
          if (financialData.longTermDebt && financialData.longTermDebt.length > 0 && financialData.periods && financialData.periods.length > 0) {
            // Map periods to longTermDebt values
            const allLongTermDebtDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.longTermDebt[index] || 0
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            // Filter data based on date range - ALWAYS use same logic as Total Assets
            const pointsInRange = allLongTermDebtDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allLongTermDebtDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredLongTermDebtData: any[] = [];
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredLongTermDebtData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allLongTermDebtDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredLongTermDebtData = allLongTermDebtDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredLongTermDebtData = allLongTermDebtDataWithYears.slice(0, Math.min(2, allLongTermDebtDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredLongTermDebtData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredLongTermDebtData = pointsInRange;
            }
            return { ...element, data: filteredLongTermDebtData as any, useTimeScale: true, visible, yAxisID: 'y7' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y7' };
        case 'total-liabilities':
          if (financialData.totalLiabilities && financialData.totalLiabilities.length > 0 && financialData.periods && financialData.periods.length > 0) {
            // Map periods to totalLiabilities values
            const allTotalLiabilitiesDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.totalLiabilities[index] || 0
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            // Filter data based on date range
            const pointsInRange = allTotalLiabilitiesDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allTotalLiabilitiesDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredTotalLiabilitiesData: any[] = [];
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredTotalLiabilitiesData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allTotalLiabilitiesDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredTotalLiabilitiesData = allTotalLiabilitiesDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredTotalLiabilitiesData = allTotalLiabilitiesDataWithYears.slice(0, Math.min(2, allTotalLiabilitiesDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredTotalLiabilitiesData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredTotalLiabilitiesData = pointsInRange;
            }
            return { ...element, data: filteredTotalLiabilitiesData as any, useTimeScale: true, visible, yAxisID: 'y8' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y8' };
        case 'shareholders-equity':
          if (financialData.totalStockholdersEquity && financialData.totalStockholdersEquity.length > 0) {
            const allShareholdersEquityDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.totalStockholdersEquity[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allShareholdersEquityDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allShareholdersEquityDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredShareholdersEquityData: any[] = [];
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredShareholdersEquityData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allShareholdersEquityDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredShareholdersEquityData = allShareholdersEquityDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredShareholdersEquityData = allShareholdersEquityDataWithYears.slice(0, Math.min(2, allShareholdersEquityDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredShareholdersEquityData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredShareholdersEquityData = pointsInRange;
            }
            return { ...element, data: filteredShareholdersEquityData as any, useTimeScale: true, visible, yAxisID: 'y9' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y9' };
        
        case 'operating-cash-flow':
          if (financialData.operatingCashFlow && financialData.operatingCashFlow.length > 0) {
            const allOperatingCashFlowDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.operatingCashFlow[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allOperatingCashFlowDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allOperatingCashFlowDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredOperatingCashFlowData: any[] = [];
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredOperatingCashFlowData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allOperatingCashFlowDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredOperatingCashFlowData = allOperatingCashFlowDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredOperatingCashFlowData = allOperatingCashFlowDataWithYears.slice(0, Math.min(2, allOperatingCashFlowDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredOperatingCashFlowData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredOperatingCashFlowData = pointsInRange;
            }
            return { ...element, data: filteredOperatingCashFlowData as any, useTimeScale: true, visible, yAxisID: 'y10' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y10' };
        
        case 'free-cash-flow':
          if (financialData.freeCashFlow && financialData.freeCashFlow.length > 0) {
            const allFreeCashFlowDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.freeCashFlow[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allFreeCashFlowDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allFreeCashFlowDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredFreeCashFlowData: any[] = [];
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredFreeCashFlowData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allFreeCashFlowDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredFreeCashFlowData = allFreeCashFlowDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredFreeCashFlowData = allFreeCashFlowDataWithYears.slice(0, Math.min(2, allFreeCashFlowDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredFreeCashFlowData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredFreeCashFlowData = pointsInRange;
            }
            return { ...element, data: filteredFreeCashFlowData as any, useTimeScale: true, visible, yAxisID: 'y10' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y11' };
        
        case 'dividends':
          if (financialData.dividends && financialData.dividends.length > 0) {
            const allDividendsDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.dividends[index] == null ? null : Math.abs(financialData.dividends[index] as number) // Convert to positive for display
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allDividendsDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allDividendsDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredDividendsData: any[] = [];
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredDividendsData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allDividendsDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredDividendsData = allDividendsDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredDividendsData = allDividendsDataWithYears.slice(0, Math.min(2, allDividendsDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredDividendsData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredDividendsData = pointsInRange;
            }
            return { ...element, data: filteredDividendsData as any, useTimeScale: true, visible, yAxisID: 'y12' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y12' };
        
        case 'buybacks':
          if (financialData.buybacks && financialData.buybacks.length > 0) {
            const allBuybacksDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.buybacks[index] == null ? null : Math.abs(financialData.buybacks[index] as number) // Convert to positive for display
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allBuybacksDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allBuybacksDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredBuybacksData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredBuybacksData = allBuybacksDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredBuybacksData = allBuybacksDataWithYears.slice(0, Math.min(2, allBuybacksDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredBuybacksData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredBuybacksData = pointsInRange;
            }
            return { ...element, data: filteredBuybacksData as any, useTimeScale: true, visible, yAxisID: 'y13' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y13' };
        
        case 'gross-profit-margin':
          if (financialData.grossProfitMargin && financialData.grossProfitMargin.length > 0) {
            const allGrossProfitMarginDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.grossProfitMargin[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allGrossProfitMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allGrossProfitMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredGrossProfitMarginData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredGrossProfitMarginData = allGrossProfitMarginDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredGrossProfitMarginData = allGrossProfitMarginDataWithYears.slice(0, Math.min(2, allGrossProfitMarginDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredGrossProfitMarginData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredGrossProfitMarginData = pointsInRange;
            }
            return { ...element, data: filteredGrossProfitMarginData as any, useTimeScale: true, visible, yAxisID: 'y14' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y14' };
        case 'operating-margin':
          if (financialData.operatingMargin && financialData.operatingMargin.length > 0 && financialData.periods.length > 0) {
            // Convert operating margin data to {x, y} format with periods as dates
            const allOperatingMarginDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period, // Use actual period date (last day of quarter/year)
              y: financialData.operatingMargin[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allOperatingMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allOperatingMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredOperatingMarginData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredOperatingMarginData = allOperatingMarginDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredOperatingMarginData = allOperatingMarginDataWithYears.slice(0, Math.min(2, allOperatingMarginDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredOperatingMarginData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredOperatingMarginData = pointsInRange;
            }
            return { ...element, data: filteredOperatingMarginData as any, useTimeScale: true, visible, yAxisID: 'y15' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y15' };
        case 'net-margin':
          if (financialData.netMargin && financialData.netMargin.length > 0 && financialData.periods.length > 0) {
            // Convert net margin data to {x, y} format with periods as dates
            const allNetMarginDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period, // Use actual period date (last day of quarter/year)
              y: financialData.netMargin[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allNetMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allNetMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredNetMarginData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredNetMarginData = allNetMarginDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredNetMarginData = allNetMarginDataWithYears.slice(0, Math.min(2, allNetMarginDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredNetMarginData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredNetMarginData = pointsInRange;
            }
            return { ...element, data: filteredNetMarginData as any, useTimeScale: true, visible, yAxisID: 'y16' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y16' };
        case 'operating-cf-margin':
          if (financialData.operatingCashFlowMargin && financialData.operatingCashFlowMargin.length > 0 && financialData.periods.length > 0) {
            const allOperatingCFMarginDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.operatingCashFlowMargin[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allOperatingCFMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allOperatingCFMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredOperatingCFMarginData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredOperatingCFMarginData = allOperatingCFMarginDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredOperatingCFMarginData = allOperatingCFMarginDataWithYears.slice(0, Math.min(2, allOperatingCFMarginDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredOperatingCFMarginData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredOperatingCFMarginData = pointsInRange;
            }
            return { ...element, data: filteredOperatingCFMarginData as any, useTimeScale: true, visible, yAxisID: 'y17' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y17' };
        case 'free-cf-margin':
          if (financialData.freeCashFlowMargin && financialData.freeCashFlowMargin.length > 0 && financialData.periods.length > 0) {
            const allFreeCFMarginDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.freeCashFlowMargin[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            
            const pointsInRange = allFreeCFMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allFreeCFMarginDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredFreeCFMarginData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredFreeCFMarginData = allFreeCFMarginDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredFreeCFMarginData = allFreeCFMarginDataWithYears.slice(0, Math.min(2, allFreeCFMarginDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredFreeCFMarginData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredFreeCFMarginData = pointsInRange;
            }
            return { ...element, data: filteredFreeCFMarginData as any, useTimeScale: true, visible, yAxisID: 'y18' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y18' };
        
        case 'operating-cf-to-ni':
          if (financialData.ocfToNetIncome && financialData.ocfToNetIncome.length > 0 && financialData.periods.length > 0) {
            const allOCFToNetIncomeDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.ocfToNetIncome[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            const pointsInRange = allOCFToNetIncomeDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allOCFToNetIncomeDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredOCFToNetIncomeData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredOCFToNetIncomeData = allOCFToNetIncomeDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredOCFToNetIncomeData = allOCFToNetIncomeDataWithYears.slice(0, Math.min(2, allOCFToNetIncomeDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredOCFToNetIncomeData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredOCFToNetIncomeData = pointsInRange;
            }
            return { ...element, data: filteredOCFToNetIncomeData as any, useTimeScale: true, visible, yAxisID: 'y19' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y19' };
        
        case 'free-cf-to-ni':
          if (financialData.fcfToNetIncome && financialData.fcfToNetIncome.length > 0 && financialData.periods.length > 0) {
            const allFCFToNetIncomeDataWithYears = financialData.periods.map((period: Date, index: number) => ({
              x: period,
              y: financialData.fcfToNetIncome[index]
            }));
            
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            
            const pointsInRange = allFCFToNetIncomeDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            
            const pointsBeforeEnd = allFCFToNetIncomeDataWithYears.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate <= end;
            });
            
            let filteredFCFToNetIncomeData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredFCFToNetIncomeData = allFCFToNetIncomeDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredFCFToNetIncomeData = allFCFToNetIncomeDataWithYears.slice(0, Math.min(2, allFCFToNetIncomeDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredFCFToNetIncomeData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredFCFToNetIncomeData = pointsInRange;
            }
            return { ...element, data: filteredFCFToNetIncomeData as any, useTimeScale: true, visible, yAxisID: 'y19' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y19' };
        
        case 'roic':
          if (financialData.roic && financialData.roic.length > 0 && financialData.periods.length > 0) {
            // Convert ROIC data to {x, y} format with dates
            // TTM ROIC has its own LTM dates. Do not remap it onto the generic
            // quarterly income axis, otherwise LTM values can be replaced by or
            // appear identical to quarterly points.
            const allROICDataWithYears = selectedPeriodType === 'TTM' && roicPoints.length > 0
              ? roicPoints.map((point) => ({ x: dateKeyToUTCDate(point.dateKey, false), y: point.value }))
              : financialData.roic.map((value, index) => ({
                  x: financialData.periods[index],
                  y: value,
                }));
            
            // Filter by date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            
            const pointsInRange = allROICDataWithYears.filter(point => {
              const date = new Date(point.x);
              return date >= start && date <= end;
            });
            const pointsBeforeEnd = allROICDataWithYears.filter(point => {
              const date = new Date(point.x);
              return date <= end;
            });
            
            let filteredROICData;
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length > 0) {
                const lastIdx = pointsBeforeEnd.length - 1;
                const startIdx = Math.max(0, lastIdx - 1);
                filteredROICData = allROICDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredROICData = allROICDataWithYears.slice(0, Math.min(2, allROICDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredROICData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredROICData = pointsInRange;
            }
            return { ...element, data: filteredROICData as any, useTimeScale: true, visible, yAxisID: 'y21' };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y21' };
        
        case 'pe':
          if (peData.length > 0) {
            // Convert PE data to {x, y} format
            const allPEDataWithDates = peData.map(point => ({
              x: new Date(point.date),
              y: point.value
            }));
            // Filter PE data by selected date range
            const start = new Date(startDate);
            const end = new Date(endDate);
            const filteredPEData = allPEDataWithDates.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            return {
              ...element,
              data: filteredPEData as any,
              useTimeScale: false,
              visible,
              color: isDarkMode ? '#EEEEEE' : '#000000',
            };
          }
          return {
            ...element,
            data: [],
            useTimeScale: false,
            visible,
            color: isDarkMode ? '#EEEEEE' : '#000000',
          };

        case 'p-fcf-ratio':
          if (blendedPfcfDailySeries.length > 0) {
            const allPfcfDataWithDates = blendedPfcfDailySeries.map(point => ({
              x: point.date,
              y: point.value,
            }));
            const start = new Date(startDate);
            const end = new Date(endDate);
            const filteredPfcfData = allPfcfDataWithDates.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            return {
              ...element,
              data: filteredPfcfData as any,
              useTimeScale: false,
              visible,
              yAxisID: 'y24',
            };
          }
          if (bestPfcfSeries.length > 0) {
            const allPfcfSeriesDataWithDates = bestPfcfSeries.map(point => ({
              x: point.date,
              y: point.value,
            }));
            const start = new Date(startDate);
            const end = new Date(endDate);
            const filteredPfcfSeriesData = allPfcfSeriesDataWithDates.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            return {
              ...element,
              data: filteredPfcfSeriesData as any,
              useTimeScale: false,
              visible,
              yAxisID: 'y24',
            };
          }
          return { ...element, data: [], useTimeScale: false, visible, yAxisID: 'y24' };

        case 'p-ocf-ratio':
          if (blendedPocfDailySeries.length > 0) {
            const allPocfSeriesDataWithDates = blendedPocfDailySeries
              .map(point => ({
                x: point.date,
                y: point.value,
              }));
            const start = new Date(startDate);
            const end = new Date(endDate);
            const filteredPocfSeriesData = allPocfSeriesDataWithDates.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            return {
              ...element,
              data: filteredPocfSeriesData as any,
              useTimeScale: false,
              visible,
              yAxisID: 'y25',
            };
          }
          if (bestPocfSeries.length > 0) {
            const allPocfSeriesDataWithDates = bestPocfSeries
              .map(point => ({
                x: point.date,
                y: point.value,
              }));
            const start = new Date(startDate);
            const end = new Date(endDate);
            const filteredPocfSeriesData = allPocfSeriesDataWithDates.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            return {
              ...element,
              data: filteredPocfSeriesData as any,
              useTimeScale: false,
              visible,
              yAxisID: 'y25',
            };
          }
          if (pocfDailySeries.length > 0) {
            const allPocfDataWithDates = pocfDailySeries.map(point => ({
              x: point.date,
              y: point.value,
            }));
            const start = new Date(startDate);
            const end = new Date(endDate);
            const filteredPocfData = allPocfDataWithDates.filter(point => {
              const pointDate = new Date(point.x);
              return pointDate >= start && pointDate <= end;
            });
            return {
              ...element,
              data: filteredPocfData as any,
              useTimeScale: false,
              visible,
              yAxisID: 'y25',
            };
          }
          return { ...element, data: [], useTimeScale: false, visible, yAxisID: 'y25' };
        
        case 'flat-eps-ttm':
          if (flatEPSTTMData.length > 0) {
            // IMPORTANT: Flat EPS (ttm) must align its X anchors exactly with the EPS series
            // (green points). Some symbols have fiscal period ends / provider dates that differ
            // from simple calendar quarter-ends, so we anchor to the already-rendered EPS periods
            // when in TTM mode.
            const allFlatEPSDataWithDates = (() => {
              const canAnchorToEps =
                selectedPeriodType === 'TTM' &&
                // In at-reported mode, EPS/analysis should shift to filing/accepted dates.
                // Anchoring to fiscal period ends would prevent the visual shift.
                analysisDateModeEps === 'as-reported' &&
                Array.isArray(financialData.periods) &&
                Array.isArray(financialData.eps) &&
                financialData.periods.length > 0 &&
                financialData.periods.length === financialData.eps.length;

              if (canAnchorToEps) {
                return financialData.periods.map((date, idx) => ({
                  x: date,
                  y: financialData.eps[idx]
                }));
              }

              return flatEPSTTMData.map(point => ({
                x: new Date(point.date),
                y: point.value
              }));
            })();

            const localDateKey = (d: Date) => {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${y}-${m}-${day}`;
            };
            const dedupeByDate = (series: Array<{ x: Date; y: number | null }>) => {
              const map = new Map<string, { x: Date; y: number | null }>();
              for (const point of series) {
                const key = localDateKey(point.x);
                const existing = map.get(key);
                if (!existing || point.x.getTime() > existing.x.getTime()) {
                  map.set(key, point);
                }
              }
              return Array.from(map.values()).sort((a, b) => a.x.getTime() - b.x.getTime());
            };

            // IMPORTANT: when the selected range ends mid-quarter, there is no quarter-end data point
            // for that quarter yet. We still want the line to continue (no empty gaps), so we add a
            // synthetic point at endDate using the last known TTM value.
            const start = new Date(startDate);
            const end = new Date(endDate);

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
            const xMinBound = dateKeyToUTCDate(startDate, false);
            const startBound = startOfDay(start);
            const xMaxBound = dateKeyToUTCDate(endDate, true);
            const endBoundRaw = endOfDay(end);
            const endBound = endBoundRaw.getTime() > xMaxBound.getTime() ? xMaxBound : endBoundRaw;

            const sorted = dedupeByDate([...allFlatEPSDataWithDates])
              .sort((a, b) => a.x.getTime() - b.x.getTime());
            const pointsBeforeOrAtEnd = sorted.filter(p => p.x.getTime() <= endBound.getTime());
            if (pointsBeforeOrAtEnd.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
            }

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const addMonths = (d: Date, months: number): Date => new Date(d.getFullYear(), d.getMonth() + months, d.getDate());

            const segmentData: Array<{ x: Date; y: number | null }> = [];

            // at-reported: align step segments to the actual report (filing) dates.
            // This avoids quarter-boundary starts like 01.01.
            if (analysisDateModeEps === 'at-reported') {
              const anchorsAll = [...pointsBeforeOrAtEnd]
                .filter(p => p.x.getTime() <= endBound.getTime())
                .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

              if (anchorsAll.length === 0) {
                return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
              }

              const addSegment = (segStart: Date, segEnd: Date, y: number) => {
                if (!Number.isFinite(y) || y === 0) return;
                if (segEnd.getTime() <= segStart.getTime()) return;
                segmentData.push({ x: segStart, y });
                segmentData.push({ x: segEnd, y });
                segmentData.push({ x: segEnd, y: null });
              };

              // Start segments exactly at the filing/report dates (same X as EPS points),
              // without carrying values from the selection start.
              const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
              if (firstIdx === -1) {
                // No anchors within range; keep prior behavior (flat line) so the series isn't empty.
                const last = anchorsAll[anchorsAll.length - 1];
                addSegment(startBound, endBound, last.y);
                return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
              }

              for (let j = firstIdx; j < anchorsAll.length; j++) {
                // At Reported: the newly-reported quarter's TTM takes effect
                // EXACTLY on its earningsDate (no day shift), so the report date
                // itself shows the NEW quarter's TTM (e.g. 30-03 shows 13.11).
                const segStart = anchorsAll[j].x;
                const nextRaw = anchorsAll[j + 1]?.x;
                const segEnd = nextRaw && nextRaw.getTime() < endBound.getTime() ? nextRaw : endBound;
                addSegment(segStart, segEnd, anchorsAll[j].y);
              }

              return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
            }
            // as-reported (TTM): align to quarter-end dates.
            // IMPORTANT: ONLY horizontal segments (no diagonals).
            // Right-continuous: each quarter-end value applies FORWARD until the next quarter-end,
            // but we MUST cap to the last available quarter-end to avoid overhang into future years.
            const anchorsAll = [...pointsBeforeOrAtEnd]
              .filter(p => p.x.getTime() <= endBound.getTime())
              .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

            if (anchorsAll.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
            }

            const addSegment = (segStart: Date, segEnd: Date, y: number) => {
              if (!Number.isFinite(y) || y === 0) return;
              const safeStart = segStart.getTime() < xMinBound.getTime() ? xMinBound : segStart;
              const safeEnd = segEnd.getTime() > xMaxBound.getTime() ? xMaxBound : segEnd;
              if (safeEnd.getTime() <= safeStart.getTime()) return;
              segmentData.push({ x: safeStart, y });
              segmentData.push({ x: safeEnd, y });
              segmentData.push({ x: safeEnd, y: null });
            };

            const lastAnchor = anchorsAll[anchorsAll.length - 1].x;
            // Allow the last known quarter-end value to extend forward within the *next* quarter.
            // This fills the current (in-progress) quarter when the selected range ends after the
            // last available filing/quarter-end point.
            const nextQuarterEnd = getQuarterEnd(addMonths(getQuarterStart(lastAnchor), 3));
            const endLimit = endBound.getTime() > nextQuarterEnd.getTime() ? nextQuarterEnd : endBound;

            // Find last anchor at or before startBound, to continue correctly when starting mid-interval.

            // Start segments exactly at the quarter-end anchors (same X as EPS points),
            // without carrying values from the selection start.
            const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
            if (firstIdx === -1) {
              const last = anchorsAll[anchorsAll.length - 1];
              addSegment(startBound, endLimit, last.y);
              return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
            }

            for (let j = firstIdx; j < anchorsAll.length; j++) {
              const anchor = anchorsAll[j];
              const segStart = anchor.x;
              const next = anchorsAll[j + 1]?.x;
              const segEnd = next && next.getTime() < endLimit.getTime() ? next : endLimit;
              addSegment(segStart, segEnd, anchor.y);
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y22', stepped: false, showPoints: false };

        case 'flat-ocf-ttm':
          if (flatOCFTTMData.length > 0) {
            const allFlatOCFDataWithDates = flatOCFTTMData.map(point => ({
              x: new Date(point.date),
              y: point.value
            }));

            const start = new Date(startDate);
            const end = new Date(endDate);

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
            const xMinBound = dateKeyToUTCDate(startDate, false);
            const startBound = startOfDay(start);
            const xMaxBound = dateKeyToUTCDate(endDate, true);
            const endBoundRaw = endOfDay(end);
            const endBound = endBoundRaw.getTime() > xMaxBound.getTime() ? xMaxBound : endBoundRaw;
            const lastPriceDate = dailyPricePoints.length > 0 ? dailyPricePoints[dailyPricePoints.length - 1].date : null;
            const lastPriceBound = lastPriceDate ? endOfDay(lastPriceDate) : endBound;
            const cappedEndBound = endBound.getTime() < lastPriceBound.getTime() ? endBound : lastPriceBound;

            const sorted = [...allFlatOCFDataWithDates].sort((a, b) => a.x.getTime() - b.x.getTime());
            const pointsBeforeOrAtEnd = sorted.filter(p => p.x.getTime() <= cappedEndBound.getTime());
            if (pointsBeforeOrAtEnd.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const addMonths = (d: Date, months: number): Date => new Date(d.getFullYear(), d.getMonth() + months, d.getDate());

            const segmentData: Array<{ x: Date; y: number | null }> = [];

            const anchorsAll = [...pointsBeforeOrAtEnd]
              .filter(p => p.x.getTime() <= cappedEndBound.getTime())
              .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

            if (anchorsAll.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const addSegment = (segStart: Date, segEnd: Date, y: number) => {
              if (!Number.isFinite(y) || y === 0) return;
              const safeStart = segStart.getTime() < xMinBound.getTime() ? xMinBound : segStart;
              const safeEnd = segEnd.getTime() > xMaxBound.getTime() ? xMaxBound : segEnd;
              if (safeEnd.getTime() <= safeStart.getTime()) return;
              segmentData.push({ x: safeStart, y });
              segmentData.push({ x: safeEnd, y });
              segmentData.push({ x: safeEnd, y: null });
            };

            if (analysisDateModeOcf === 'at-reported') {
              const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
              if (firstIdx === -1) {
                const last = anchorsAll[anchorsAll.length - 1];
                addSegment(startBound, cappedEndBound, last.y);
                return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
              }

              for (let j = firstIdx; j < anchorsAll.length; j++) {
                const segStart = anchorsAll[j].x;
                const next = anchorsAll[j + 1]?.x;
                const segEnd = next && next.getTime() < cappedEndBound.getTime() ? next : cappedEndBound;
                addSegment(segStart, segEnd, anchorsAll[j].y);
              }

              return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const lastAnchor = anchorsAll[anchorsAll.length - 1].x;
            const nextQuarterEnd = getQuarterEnd(addMonths(getQuarterStart(lastAnchor), 3));
            const endLimit = cappedEndBound.getTime() > nextQuarterEnd.getTime() ? nextQuarterEnd : cappedEndBound;

            const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
            if (firstIdx === -1) {
              const last = anchorsAll[anchorsAll.length - 1];
              addSegment(startBound, endLimit, last.y);
              return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            for (let j = firstIdx; j < anchorsAll.length; j++) {
              const anchor = anchorsAll[j];
              const segStart = anchor.x;
              const next = anchorsAll[j + 1]?.x;
              const segEnd = next && next.getTime() < endLimit.getTime() ? next : endLimit;
              addSegment(segStart, segEnd, anchor.y);
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
        
        case 'fair-price':
          {
            // Requirement: Fair Price = EPS (TTM) × Normalized PE (no fallback).
            // If Normalized PE is 0, we do not render the series at all.
            const fairPriceVisible = baseVisible && normalizedPE > 0;
            if (!fairPriceVisible) {
              return { ...element, data: [], useTimeScale: true, visible: false, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            if (flatEPSTTMData.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            // Fair Price must be price-like (USD), not EPS-like.
            // Build it as: Flat EPS (TTM) × PE multiple, while keeping the EXACT same
            // step shape (including continuity points).
            const peMultiple = normalizedPE;

            // Keep the exact same X anchors as Flat EPS (ttm) / EPS (green).
            const allFlatEPSDataWithDates = (() => {
              const canAnchorToEps =
                !prefersFiscal &&
                selectedPeriodType === 'TTM' &&
                // In at-reported mode, use the computed series' report dates.
                analysisDateModeEps === 'as-reported' &&
                Array.isArray(financialData.periods) &&
                Array.isArray(financialData.eps) &&
                financialData.periods.length > 0 &&
                financialData.periods.length === financialData.eps.length;

              if (canAnchorToEps) {
                return financialData.periods.map((date, idx) => ({
                  x: date,
                  y: financialData.eps[idx]
                }));
              }

              return flatEPSTTMData.map(point => ({
                x: new Date(point.date),
                y: point.value
              }));
            })();

            const start = new Date(startDate);
            const end = new Date(endDate);

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
            const startBound = startOfDay(start);
            const endBound = endOfDay(end);

            const sorted = [...allFlatEPSDataWithDates].sort((a, b) => a.x.getTime() - b.x.getTime());
            const pointsBeforeOrAtEnd = sorted.filter(p => p.x.getTime() <= endBound.getTime());
            if (pointsBeforeOrAtEnd.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const addMonths = (d: Date, months: number): Date => new Date(d.getFullYear(), d.getMonth() + months, d.getDate());
            const getYearEnd = (d: Date) => endOfDay(new Date(d.getFullYear(), 11, 31));

            const segmentData: Array<{ x: Date; y: number | null }> = [];

            // Build Fair Price using the EXACT same step segments as Flat EPS (ttm),
            // then scale by PE multiple. This guarantees Fair Price is always Flat EPS × PE
            // at every plotted point.
            const anchorsAll = [...pointsBeforeOrAtEnd]
              .filter(p => p.x.getTime() <= endBound.getTime())
              .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

            if (anchorsAll.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const addSegment = (segStart: Date, segEnd: Date, baseY: number) => {
              if (!Number.isFinite(baseY) || baseY === 0) return;
              if (segEnd.getTime() <= segStart.getTime()) return;
              const y = baseY * peMultiple;
              if (!Number.isFinite(y) || y === 0) return;
              segmentData.push({ x: segStart, y });
              segmentData.push({ x: segEnd, y });
              segmentData.push({ x: segEnd, y: null });
            };

            if (analysisDateModeEps === 'at-reported') {
              const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
              if (firstIdx === -1) {
                const last = anchorsAll[anchorsAll.length - 1];
                addSegment(startBound, endBound, last.y);
                return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
              }

              for (let j = firstIdx; j < anchorsAll.length; j++) {
                const segStart = anchorsAll[j].x;
                const next = anchorsAll[j + 1]?.x;
                const segEnd = next && next.getTime() < endBound.getTime() ? next : endBound;
                addSegment(segStart, segEnd, anchorsAll[j].y);
              }

              return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const lastAnchor = anchorsAll[anchorsAll.length - 1].x;
            const nextQuarterEnd = getQuarterEnd(addMonths(getQuarterStart(lastAnchor), 3));
            const endLimit = endBound.getTime() > nextQuarterEnd.getTime() ? nextQuarterEnd : endBound;

            const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
            if (firstIdx === -1) {
              const last = anchorsAll[anchorsAll.length - 1];
              addSegment(startBound, endLimit, last.y);
              return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            for (let j = firstIdx; j < anchorsAll.length; j++) {
              const anchor = anchorsAll[j];
              const segStart = anchor.x;
              const next = anchorsAll[j + 1]?.x;
              const segEnd = next && next.getTime() < endLimit.getTime() ? next : endLimit;
              addSegment(segStart, segEnd, anchor.y);
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y23', stepped: false, showPoints: false };
          }

        case 'fair-price-ocf':
          {
            // Requirement: Fair Price (OCF) = OCF (TTM per share) × Normalized P/OCF.
            // If Normalized P/OCF is 0, we do not render the series at all.
            const fairPriceVisible = baseVisible && normalizedPOCF > 0;
            if (!fairPriceVisible) {
              return { ...element, data: [], useTimeScale: true, visible: false, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            if (flatOCFTTMData.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const pocfMultiple = normalizedPOCF;
            const allFlatOCFDataWithDates = flatOCFTTMData.map(point => ({
              x: new Date(point.date),
              y: point.value
            }));

            const start = new Date(startDate);
            const end = new Date(endDate);

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
            const startBound = startOfDay(start);
            const endBound = endOfDay(end);

            const sorted = [...allFlatOCFDataWithDates].sort((a, b) => a.x.getTime() - b.x.getTime());
            const pointsBeforeOrAtEnd = sorted.filter(p => p.x.getTime() <= endBound.getTime());
            if (pointsBeforeOrAtEnd.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const addMonths = (d: Date, months: number): Date => new Date(d.getFullYear(), d.getMonth() + months, d.getDate());
            const segmentData: Array<{ x: Date; y: number | null }> = [];

            const anchorsAll = [...pointsBeforeOrAtEnd]
              .filter(p => p.x.getTime() <= endBound.getTime())
              .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

            if (anchorsAll.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const addSegment = (segStart: Date, segEnd: Date, baseY: number) => {
              if (!Number.isFinite(baseY) || baseY === 0) return;
              if (segEnd.getTime() <= segStart.getTime()) return;
              const y = baseY * pocfMultiple;
              if (!Number.isFinite(y) || y === 0) return;
              segmentData.push({ x: segStart, y });
              segmentData.push({ x: segEnd, y });
              segmentData.push({ x: segEnd, y: null });
            };

            if (analysisDateModeOcf === 'at-reported') {
              const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
              if (firstIdx === -1) {
                const last = anchorsAll[anchorsAll.length - 1];
                addSegment(startBound, endBound, last.y);
                return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
              }

              for (let j = firstIdx; j < anchorsAll.length; j++) {
                const segStart = anchorsAll[j].x;
                const next = anchorsAll[j + 1]?.x;
                const segEnd = next && next.getTime() < endBound.getTime() ? next : endBound;
                addSegment(segStart, segEnd, anchorsAll[j].y);
              }

              return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const lastAnchor = anchorsAll[anchorsAll.length - 1].x;
            const nextQuarterEnd = getQuarterEnd(addMonths(getQuarterStart(lastAnchor), 3));
            const endLimit = endBound.getTime() > nextQuarterEnd.getTime() ? nextQuarterEnd : endBound;

            const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
            if (firstIdx === -1) {
              const last = anchorsAll[anchorsAll.length - 1];
              addSegment(startBound, endLimit, last.y);
              return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            for (let j = firstIdx; j < anchorsAll.length; j++) {
              const anchor = anchorsAll[j];
              const segStart = anchor.x;
              const next = anchorsAll[j + 1]?.x;
              const segEnd = next && next.getTime() < endLimit.getTime() ? next : endLimit;
              addSegment(segStart, segEnd, anchor.y);
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible: fairPriceVisible, yAxisID: 'y16', stepped: false, showPoints: false };
          }

        case 'entry-point-1':
        case 'entry-point-2':
          // Entry/Exit Price are derived from Fair Price, and must use the same y-axis (y23).
          // Their multipliers are applied in the chart layer via the element.adjustment value.
          // Here we only build the same discrete quarter segments as Fair Price (horizontal, unconnected, no points).
          {
            // Requirement: Entry/Exit are derived from Fair Price, which is EPS (TTM) × Normalized PE.
            // If Normalized PE is 0, we do not render Entry/Exit at all.
            const entryExitVisible = baseVisible && normalizedPE > 0;
            if (!entryExitVisible) {
              return { ...element, data: [], useTimeScale: true, visible: false, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            if (flatEPSTTMData.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const peMultiple = normalizedPE;
            const adjustmentRatio = Number.isFinite(element.adjustment)
              ? element.adjustment
              : (element.id === 'entry-point-1' ? entryMultipleRatioEps : exitMultipleRatioEps);
            const applyEntryExitAdjustment = (fairY: number) => (
              element.id === 'entry-point-1'
                ? fairY * Math.max(0, 1 - adjustmentRatio)
                : fairY * (1 + adjustmentRatio)
            );

            // Keep the exact same X anchors as Flat EPS (ttm) / EPS (green).
            const allFlatEPSDataWithDates = (() => {
              const canAnchorToEps =
                !prefersFiscal &&
                selectedPeriodType === 'TTM' &&
                // In at-reported mode, use the computed series' report dates.
                analysisDateModeEps === 'as-reported' &&
                Array.isArray(financialData.periods) &&
                Array.isArray(financialData.eps) &&
                financialData.periods.length > 0 &&
                financialData.periods.length === financialData.eps.length;

              if (canAnchorToEps) {
                return financialData.periods.map((date, idx) => ({
                  x: date,
                  y: financialData.eps[idx]
                }));
              }

              return flatEPSTTMData.map(point => ({
                x: new Date(point.date),
                y: point.value
              }));
            })();

            const start = new Date(startDate);
            const end = new Date(endDate);

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
            const startBound = startOfDay(start);
            const endBound = endOfDay(end);

            const sorted = [...allFlatEPSDataWithDates].sort((a, b) => a.x.getTime() - b.x.getTime());
            const pointsBeforeOrAtEnd = sorted.filter(p => p.x.getTime() <= endBound.getTime());
            if (pointsBeforeOrAtEnd.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const addMonths = (d: Date, months: number): Date => new Date(d.getFullYear(), d.getMonth() + months, d.getDate());
            const segmentData: Array<{ x: Date; y: number | null }> = [];

            // at-reported: align step segments to the actual report (filing) dates.
            if (analysisDateModeEps === 'at-reported') {
              const anchorsAll = [...pointsBeforeOrAtEnd]
                .filter(p => p.x.getTime() <= endBound.getTime())
                .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

              if (anchorsAll.length === 0) {
                return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
              }

              const addSegment = (segStart: Date, segEnd: Date, baseY: number) => {
                if (!Number.isFinite(baseY) || baseY === 0) return;
                if (segEnd.getTime() <= segStart.getTime()) return;
                const fairY = baseY * peMultiple;
                const adjustedY = applyEntryExitAdjustment(fairY);
                if (!Number.isFinite(adjustedY) || adjustedY === 0) return;
                segmentData.push({ x: segStart, y: adjustedY });
                segmentData.push({ x: segEnd, y: adjustedY });
                segmentData.push({ x: segEnd, y: null });
              };

              const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
              if (firstIdx === -1) {
                const last = anchorsAll[anchorsAll.length - 1];
                addSegment(startBound, endBound, last.y);
                return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
              }

              for (let j = firstIdx; j < anchorsAll.length; j++) {
                const segStart = anchorsAll[j].x;
                const next = anchorsAll[j + 1]?.x;
                const segEnd = next && next.getTime() < endBound.getTime() ? next : endBound;
                addSegment(segStart, segEnd, anchorsAll[j].y);
              }

              return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            // as-reported (TTM): align to quarter-end anchors and allow the last anchor to extend
            // into the next quarter end (same behavior as Flat EPS / Fair Price).
            const addSegment = (segStart: Date, segEnd: Date, baseY: number) => {
              if (!Number.isFinite(baseY) || baseY === 0) return;
              if (segEnd.getTime() <= segStart.getTime()) return;
              const fairY = baseY * peMultiple;
              const adjustedY = applyEntryExitAdjustment(fairY);
              if (!Number.isFinite(adjustedY) || adjustedY === 0) return;
              segmentData.push({ x: segStart, y: adjustedY });
              segmentData.push({ x: segEnd, y: adjustedY });
              segmentData.push({ x: segEnd, y: null });
            };

            const anchorsAll = [...pointsBeforeOrAtEnd]
              .filter(p => p.x.getTime() <= endBound.getTime())
              .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

            if (anchorsAll.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const lastAnchor = anchorsAll[anchorsAll.length - 1].x;
            const nextQuarterEnd = getQuarterEnd(addMonths(getQuarterStart(lastAnchor), 3));
            const endLimit = endBound.getTime() > nextQuarterEnd.getTime() ? nextQuarterEnd : endBound;

            const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
            if (firstIdx === -1) {
              const last = anchorsAll[anchorsAll.length - 1];
              addSegment(startBound, endLimit, last.y);
              return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            for (let j = firstIdx; j < anchorsAll.length; j++) {
              const anchor = anchorsAll[j];
              const segStart = anchor.x;
              const next = anchorsAll[j + 1]?.x;
              const segEnd = next && next.getTime() < endLimit.getTime() ? next : endLimit;
              addSegment(segStart, segEnd, anchor.y);
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y23', stepped: false, showPoints: false };
          }

        case 'entry-point-1-ocf':
        case 'entry-point-2-ocf':
          {
            // Entry/Exit Price (OCF) are derived from Fair Price (OCF).
            const entryExitVisible = baseVisible && normalizedPOCF > 0;
            if (!entryExitVisible) {
              return { ...element, data: [], useTimeScale: true, visible: false, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            if (flatOCFTTMData.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const pocfMultiple = normalizedPOCF;
            const adjustmentRatio = Number.isFinite(element.adjustment)
              ? element.adjustment
              : (element.id === 'entry-point-1-ocf' ? entryMultipleRatioOcf : exitMultipleRatioOcf);
            const applyEntryExitAdjustment = (fairY: number) => (
              element.id === 'entry-point-1-ocf'
                ? fairY * Math.max(0, 1 - adjustmentRatio)
                : fairY * (1 + adjustmentRatio)
            );
            const allFlatOCFDataWithDates = flatOCFTTMData.map(point => ({
              x: new Date(point.date),
              y: point.value
            }));

            const start = new Date(startDate);
            const end = new Date(endDate);

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
            const startBound = startOfDay(start);
            const endBound = endOfDay(end);

            const sorted = [...allFlatOCFDataWithDates].sort((a, b) => a.x.getTime() - b.x.getTime());
            const pointsBeforeOrAtEnd = sorted.filter(p => p.x.getTime() <= endBound.getTime());
            if (pointsBeforeOrAtEnd.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const addMonths = (d: Date, months: number): Date => new Date(d.getFullYear(), d.getMonth() + months, d.getDate());
            const segmentData: Array<{ x: Date; y: number | null }> = [];

            const anchorsAll = [...pointsBeforeOrAtEnd]
              .filter(p => p.x.getTime() <= endBound.getTime())
              .sort((a, b) => a.x.getTime() - b.x.getTime()) as Array<{ x: Date; y: number }>;

            if (anchorsAll.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const addSegment = (segStart: Date, segEnd: Date, baseY: number) => {
              if (!Number.isFinite(baseY) || baseY === 0) return;
              if (segEnd.getTime() <= segStart.getTime()) return;
              const y = baseY * pocfMultiple;
              const adjustedY = applyEntryExitAdjustment(y);
              if (!Number.isFinite(adjustedY) || adjustedY === 0) return;
              segmentData.push({ x: segStart, y: adjustedY });
              segmentData.push({ x: segEnd, y: adjustedY });
              segmentData.push({ x: segEnd, y: null });
            };

            if (analysisDateModeOcf === 'at-reported') {
              const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
              if (firstIdx === -1) {
                const last = anchorsAll[anchorsAll.length - 1];
                addSegment(startBound, endBound, last.y);
                return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
              }

              for (let j = firstIdx; j < anchorsAll.length; j++) {
                const segStart = anchorsAll[j].x;
                const next = anchorsAll[j + 1]?.x;
                const segEnd = next && next.getTime() < endBound.getTime() ? next : endBound;
                addSegment(segStart, segEnd, anchorsAll[j].y);
              }

              return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            const lastAnchor = anchorsAll[anchorsAll.length - 1].x;
            const nextQuarterEnd = getQuarterEnd(addMonths(getQuarterStart(lastAnchor), 3));
            const endLimit = endBound.getTime() > nextQuarterEnd.getTime() ? nextQuarterEnd : endBound;

            const firstIdx = anchorsAll.findIndex(a => a.x.getTime() >= startBound.getTime());
            if (firstIdx === -1) {
              const last = anchorsAll[anchorsAll.length - 1];
              addSegment(startBound, endLimit, last.y);
              return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
            }

            for (let j = firstIdx; j < anchorsAll.length; j++) {
              const anchor = anchorsAll[j];
              const segStart = anchor.x;
              const next = anchorsAll[j + 1]?.x;
              const segEnd = next && next.getTime() < endLimit.getTime() ? next : endLimit;
              addSegment(segStart, segEnd, anchor.y);
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible: entryExitVisible, yAxisID: 'y16', stepped: false, showPoints: false };
          }

        case 'fair-price-flat-eps-ttm':
          if (fairPriceFlatEPSTTMData.length > 0) {
            // Render as discrete quarter segments with small gaps (same style as Flat EPS / Fair Price).
            // Don't filter by date range - always show last 4 quarters.
            const allFairPriceFlatEPSTTMDataWithDates = fairPriceFlatEPSTTMData.map(point => ({
              x: new Date(point.date),
              y: point.value
            }));

            const sorted = [...allFairPriceFlatEPSTTMDataWithDates].sort((a, b) => a.x.getTime() - b.x.getTime());
            if (sorted.length === 0) {
              return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
            }

            const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
            const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

            const getQuarterStart = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return startOfDay(new Date(year, qStartMonth, 1));
            };
            const getQuarterEnd = (d: Date) => {
              const year = d.getFullYear();
              const month = d.getMonth();
              const qStartMonth = Math.floor(month / 3) * 3;
              return endOfDay(new Date(year, qStartMonth + 3, 0));
            };

            const startBound = getQuarterStart(sorted[0].x);
            const endBound = getQuarterEnd(sorted[sorted.length - 1].x);

            const rangeMs = Math.max(1, endBound.getTime() - startBound.getTime());
            const VISUAL_GAP_MS = Math.max(
              60 * 1000,
              Math.min(7 * 24 * 60 * 60 * 1000, Math.round(rangeMs / 400))
            );

            const segmentData: Array<{ x: Date; y: number | null }> = [];

            for (const p of sorted) {
              const qStart = getQuarterStart(p.x);
              const qEnd = getQuarterEnd(p.x);

              // Make a small gap between consecutive quarters.
              const startClamped = qStart;
              const endClamped = new Date(qEnd.getTime() - VISUAL_GAP_MS);
              if (endClamped.getTime() <= startClamped.getTime()) continue;

              segmentData.push({ x: startClamped, y: p.y });
              segmentData.push({ x: endClamped, y: p.y });
              segmentData.push({ x: endClamped, y: null });
            }

            return { ...element, data: segmentData as any, useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
          }
          return { ...element, data: [], useTimeScale: true, visible, yAxisID: 'y23', stepped: false, showPoints: false };
        
        case 'net-income':
          if (financialData.netIncome.length > 0 && financialData.periods.length > 0) {
            // Convert net income data to {x, y} format with periods as dates
            const allNetIncomeDataWithYears = financialData.periods.map((date, idx) => ({
              x: date,
              y: financialData.netIncome[idx]
            }));

            // Filter net income data by selected date range (same logic as revenue)
            const start = new Date(startDate);
            const end = new Date(endDate);
            
            const pointsBeforeEnd = allNetIncomeDataWithYears.filter(p => p.x <= end);
            const pointsInRange = allNetIncomeDataWithYears.filter(point => point.x >= start && point.x <= end);
            
            let filteredNetIncomeData;
            
            if (pointsInRange.length === 0) {
              if (pointsBeforeEnd.length >= 2) {
                filteredNetIncomeData = pointsBeforeEnd.slice(-2);
              } else if (pointsBeforeEnd.length === 1) {
                const lastIdx = allNetIncomeDataWithYears.findIndex(p => p.x.getTime() === pointsBeforeEnd[0].x.getTime());
                const startIdx = Math.max(0, lastIdx - 1);
                filteredNetIncomeData = allNetIncomeDataWithYears.slice(startIdx, lastIdx + 1);
              } else {
                filteredNetIncomeData = allNetIncomeDataWithYears.slice(0, Math.min(2, allNetIncomeDataWithYears.length));
              }
            } else if (pointsInRange.length === 1) {
              const lastPointBeforeStart = pointsBeforeEnd.filter(p => p.x < start).slice(-1);
              filteredNetIncomeData = [...lastPointBeforeStart, ...pointsInRange];
            } else {
              filteredNetIncomeData = pointsInRange;
            }

            return { ...element, data: filteredNetIncomeData as any, useTimeScale: true, visible };
          }
          return { ...element, data: [], useTimeScale: true, visible };
        default:
          return { ...element, visible };
      }
    }));
    if (dataLoaded && loadedSymbolRef.current === normalizedSymbol) {
      setElementsReadySymbol(normalizedSymbol);
    } else {
      setElementsReadySymbol(null);
    }
  }, [
    filteredPriceDataWithDates,
    dailyPricePoints,
    fmpPricePoints,
    financialData,
    selectedRange,
    startDate,
    endDate,
    peData,
    pfcfSeries,
    bestPfcfSeries,
    bestPfcfDailySeries,
    pocfSeries,
    bestPocfSeries,
    bestPocfDailySeries,
    pocfDailySeries,
    pfcfDailySeries,
    flatEPSTTMData,
    flatOCFTTMData,
    normalizedPE,
    normalizedPOCF,
    fairPriceFlatEPSTTMData,
    elementVisibility,
    epsAnchorDates,
    analysisDateModeEps,
    analysisDateModeOcf,
    isDarkMode,
    selectedPeriodType,
    entryMultipleRatioEps,
    exitMultipleRatioEps,
    entryMultipleRatioOcf,
    exitMultipleRatioOcf,
    prefersFiscal,
    dataLoaded,
    normalizedSymbol,
    isRestrictedAnalysisUser,
    hiddenAnalysisElementIdSet,
  ]);

  const handleToggleElement = (elementId: string) => {
    if (isRestrictedAnalysisUser && hiddenAnalysisElementIdSet.has(elementId)) {
      return;
    }
    const analysisEpsLockIds = new Set(['entry-point-1', 'entry-point-2']);
    const analysisOcfLockIds = new Set(['entry-point-1-ocf', 'entry-point-2-ocf']);
    const analysisEpsIds = new Set(['flat-eps-ttm'].concat(Array.from(analysisEpsLockIds)));
    const analysisOcfIds = new Set(['flat-ocf-ttm'].concat(Array.from(analysisOcfLockIds)));
    const isAnalysisEps = analysisEpsIds.has(elementId);
    const isAnalysisOcf = analysisOcfIds.has(elementId);
    flushSync(() => {
      setVisibilitySuppressed(true);
      setChartMasked(true);
    });

    let didToggle = false;
    let nextVisibility: Record<string, boolean> | null = null;
    setElementVisibility(prev => {
      const willEnable = !prev[elementId];
      if (willEnable) {
        if (analysisEpsLockIds.has(elementId) && Array.from(analysisOcfIds).some(id => prev[id])) return prev;
        if (analysisOcfLockIds.has(elementId) && Array.from(analysisEpsIds).some(id => prev[id])) return prev;
        if (elementId === 'flat-eps-ttm' && Array.from(analysisOcfLockIds).some(id => prev[id])) return prev;
        if (elementId === 'flat-ocf-ttm' && Array.from(analysisEpsLockIds).some(id => prev[id])) return prev;
      }
      const updated = clampRestrictedVisibility({
        ...prev,
        [elementId]: !prev[elementId],
      });
      nextVisibility = updated;
      didToggle = true;
      return updated;
    });
    if (!didToggle) return;
    if (toggleMaskRafRef.current !== null) {
      window.cancelAnimationFrame(toggleMaskRafRef.current);
      toggleMaskRafRef.current = null;
    }
    if (toggleMaskRaf2Ref.current !== null) {
      window.cancelAnimationFrame(toggleMaskRaf2Ref.current);
      toggleMaskRaf2Ref.current = null;
    }
    toggleMaskRafRef.current = window.requestAnimationFrame(() => {
      toggleMaskRaf2Ref.current = window.requestAnimationFrame(() => {
        if (isSymbolReadyForRenderRef.current) {
          setVisibilitySuppressed(false);
          setChartMasked(false);
        }
      });
    });
    setElements(prev => {
      let currentOrder = orderCounter;
      const updated = prev.map(element => {
        if (element.id === elementId) {
          const newVisible = !element.visible;
          if (newVisible) {
            // Element is being made visible - assign order
            const assignedOrder = currentOrder;
            currentOrder += 1;
            let nextAdjustment = element.adjustment;
            if (element.id === 'entry-point-1') nextAdjustment = clampEntryExitRatio(entryMultipleRatioEps);
            if (element.id === 'entry-point-2') nextAdjustment = clampEntryExitRatio(exitMultipleRatioEps);
            if (element.id === 'entry-point-1-ocf') nextAdjustment = clampEntryExitRatio(entryMultipleRatioOcf);
            if (element.id === 'entry-point-2-ocf') nextAdjustment = clampEntryExitRatio(exitMultipleRatioOcf);
            return { ...element, visible: true, orderIndex: assignedOrder, adjustment: nextAdjustment };
          } else {
            // Element is being hidden - remove order
            return { ...element, visible: false, orderIndex: undefined };
          }
        }
        return element;
      });
      if (currentOrder !== orderCounter) {
        setOrderCounter(currentOrder);
      }
      return updated;
    });
    if (nextVisibility) {
      updateChartsStateRef({ elementVisibility: nextVisibility });
      saveChartsStateLocalImmediate({ elementVisibility: nextVisibility });
      saveChartsStateImmediate({ elementVisibility: nextVisibility });
    }
  };

  const handlePreToggleElement = useCallback(() => {
    if (toggleMaskRafRef.current !== null) {
      window.cancelAnimationFrame(toggleMaskRafRef.current);
      toggleMaskRafRef.current = null;
    }
    if (toggleMaskRaf2Ref.current !== null) {
      window.cancelAnimationFrame(toggleMaskRaf2Ref.current);
      toggleMaskRaf2Ref.current = null;
    }
    flushSync(() => {
      setVisibilitySuppressed(true);
      setChartMasked(true);
    });
  }, []);

  const handleAdjustElement = (elementId: string, adjustment: number) => {
    const shouldSyncEntry = elementVisibility['entry-point-1'] && elementVisibility['entry-point-1-ocf'];
    const shouldSyncExit = elementVisibility['entry-point-2'] && elementVisibility['entry-point-2-ocf'];
    const updates: Partial<typeof chartsStateRef.current> = {};

    if (elementId === 'entry-point-1' || elementId === 'entry-point-1-ocf') {
      adjustment = clampEntryExitRatio(adjustment);
      if (shouldSyncEntry) {
        setEntryMultipleRatioEps(adjustment);
        setEntryMultipleRatioOcf(adjustment);
        updates.entryMultipleRatioEps = adjustment;
        updates.entryMultipleRatioOcf = adjustment;
      } else if (elementId === 'entry-point-1') {
        setEntryMultipleRatioEps(adjustment);
        updates.entryMultipleRatioEps = adjustment;
      } else {
        setEntryMultipleRatioOcf(adjustment);
        updates.entryMultipleRatioOcf = adjustment;
      }
    }

    if (elementId === 'entry-point-2' || elementId === 'entry-point-2-ocf') {
      adjustment = clampEntryExitRatio(adjustment);
      if (shouldSyncExit) {
        setExitMultipleRatioEps(adjustment);
        setExitMultipleRatioOcf(adjustment);
        updates.exitMultipleRatioEps = adjustment;
        updates.exitMultipleRatioOcf = adjustment;
      } else if (elementId === 'entry-point-2') {
        setExitMultipleRatioEps(adjustment);
        updates.exitMultipleRatioEps = adjustment;
      } else {
        setExitMultipleRatioOcf(adjustment);
        updates.exitMultipleRatioOcf = adjustment;
      }
    }

    if (Object.keys(updates).length > 0) {
      updateChartsStateRef(updates);
      saveChartsStateLocalImmediate(updates);
      saveChartsStateImmediate(updates);
    }

    setElements(prev =>
      prev.map(element => {
        if (element.id === elementId) return { ...element, adjustment };
        if (shouldSyncEntry && (elementId === 'entry-point-1' || elementId === 'entry-point-1-ocf')) {
          if (element.id === 'entry-point-1' || element.id === 'entry-point-1-ocf') {
            return { ...element, adjustment };
          }
        }
        if (shouldSyncExit && (elementId === 'entry-point-2' || elementId === 'entry-point-2-ocf')) {
          if (element.id === 'entry-point-2' || element.id === 'entry-point-2-ocf') {
            return { ...element, adjustment };
          }
        }
        return element;
      })
    );
  };

  // Ensure entry/exit ratios rehydrate into element adjustments.
  useEffect(() => {
    if (!dataLoaded) return;
    setElements(prev => {
      let changed = false;
      const next = prev.map(el => {
        if (el.id === 'entry-point-1' && el.adjustment !== entryMultipleRatioEps) {
          changed = true;
          return { ...el, adjustment: entryMultipleRatioEps };
        }
        if (el.id === 'entry-point-2' && el.adjustment !== exitMultipleRatioEps) {
          changed = true;
          return { ...el, adjustment: exitMultipleRatioEps };
        }
        if (el.id === 'entry-point-1-ocf' && el.adjustment !== entryMultipleRatioOcf) {
          changed = true;
          return { ...el, adjustment: entryMultipleRatioOcf };
        }
        if (el.id === 'entry-point-2-ocf' && el.adjustment !== exitMultipleRatioOcf) {
          changed = true;
          return { ...el, adjustment: exitMultipleRatioOcf };
        }
        return el;
      });
      return changed ? next : prev;
    });
  }, [clampEntryExitRatio, dataLoaded, entryMultipleRatioEps, exitMultipleRatioEps, entryMultipleRatioOcf, exitMultipleRatioOcf]);

  // Handle chart resize start
  const handleResizeStart = (e: React.MouseEvent, direction: 'vertical' | 'horizontal' | 'both') => {
    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStart({ x: e.clientX, y: e.clientY });
    
    // Get actual width from container if chartWidth is null
    const actualWidth = chartWidth !== null ? chartWidth : (containerRef.current?.clientWidth || 800);
    
    // Store the current vh value for height (not pixels)
    setSizeStart({ 
      width: actualWidth,
      height: chartHeight // Store vh value directly
    });
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle resize move with useEffect
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      // Apply constraints based on resize direction
      if (resizeDirection === 'vertical' || resizeDirection === 'both') {
        // Convert pixel delta to vh: reduce sensitivity and convert to vh units
        const pixelDelta = Math.round(deltaY / 5) * 5; // Round to 5px steps
        const vhDelta = (pixelDelta / window.innerHeight) * 100;
        const newHeightVh = Math.max(20, Math.min(80, sizeStart.height + vhDelta));
        const roundedVh = Math.round(newHeightVh);
        
        if (roundedVh !== chartHeight) {
          setChartHeight(roundedVh);
        }
      }
      
      if (resizeDirection === 'horizontal' || resizeDirection === 'both') {
        // Reduce sensitivity and round to nearest 10px
        const adjustedDelta = Math.round(deltaX / 10) * 10;
        const newWidth = Math.max(400, Math.min(2000, sizeStart.width + adjustedDelta));
        const roundedWidth = Math.round(newWidth / 10) * 10;
        
        if (roundedWidth !== chartWidth) {
          setChartWidth(roundedWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, sizeStart, resizeDirection, chartHeight, chartWidth]);

  // Show only elements marked as visible
  const visibleElements = visibilitySuppressed
    ? []
    : elements.filter(e => e.visible);
  const backgroundColor = isDarkMode ? '#111827' : '#ffffff';
  const textColor = isDarkMode ? '#e5e7eb' : '#374151';

  const isSymbolReady = dataLoaded
    && loadedSymbolRef.current === normalizedSymbol
    && !isSwitchingSymbol;
  const isSymbolReadyForRender = isSymbolReady
    && !didSymbolChangeThisRender
    && (!isChartsScope || chartsHydrated)
    && elementsReadySymbol === normalizedSymbol;

  useEffect(() => {
    isSymbolReadyForRenderRef.current = isSymbolReadyForRender;
  }, [isSymbolReadyForRender]);

  const visibilityKey = React.useMemo(() => {
    const entries = Object.entries(elementVisibility).sort(([a], [b]) => a.localeCompare(b));
    return entries.map(([key, value]) => `${key}:${value ? '1' : '0'}`).join('|');
  }, [elementVisibility]);

  useLayoutEffect(() => {
    if (visibilityKeyRef.current === null) {
      visibilityKeyRef.current = visibilityKey;
      return;
    }

    if (visibilityKeyRef.current !== visibilityKey) {
      if (toggleMaskRafRef.current !== null) {
        window.cancelAnimationFrame(toggleMaskRafRef.current);
        toggleMaskRafRef.current = null;
      }
      if (toggleMaskRaf2Ref.current !== null) {
        window.cancelAnimationFrame(toggleMaskRaf2Ref.current);
        toggleMaskRaf2Ref.current = null;
      }
      setVisibilitySuppressed(true);
      setChartMasked(true);
      toggleMaskRafRef.current = window.requestAnimationFrame(() => {
        toggleMaskRaf2Ref.current = window.requestAnimationFrame(() => {
          if (isSymbolReadyForRenderRef.current) {
            setVisibilitySuppressed(false);
            setChartMasked(false);
          }
        });
      });
    }

    visibilityKeyRef.current = visibilityKey;
  }, [visibilityKey]);

  useEffect(() => {
    if (!isSymbolReadyForRender) {
      setChartMasked(true);
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      setChartMasked(false);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isSymbolReadyForRender]);

  useEffect(() => {
    if (elementsReadySymbol === normalizedSymbol && isSymbolReady) {
      setVisibilitySuppressed(false);
    } else {
      setVisibilitySuppressed(true);
    }
  }, [elementsReadySymbol, isSymbolReady, normalizedSymbol]);

  // Show loading message if data not loaded yet
  if (!isSymbolReadyForRender) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor, color: textColor, padding: '0.625rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {dataLoadError ? (
          <div style={{ fontSize: '1.125rem' }}>{dataLoadError}</div>
        ) : (
          <CenteredLoadingSpinner isDarkMode={isDarkMode} />
        )}
      </div>
    );
  }

  return (
    // Header is a fixed 70px-tall element (4.375rem) rendered above this screen.
    // Constrain this screen height so the left menu can reliably overflow + scroll
    // across browsers/DPIs (otherwise the panel can grow with content and never show a scrollbar).
    <div
      id="advanced-chart-screen"
      style={{
        backgroundColor,
        color: textColor,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: disableMinHeight ? 'auto' : 'calc(100vh - 4.375rem)',
        minHeight: disableMinHeight ? 'unset' : 'calc(100vh - 4.375rem)',
      }}
    >
      {/* Main Layout: Left Panel (fixed), Center Content Area, and Right Adjustment Panel */}
      <div
        id="main-layout"
        style={{
          display: 'flex',
          gap: '0.625rem',
          padding: '0.4rem 2.5rem',
          flex: 1,
          minHeight: 0,
          height: '100%',
          alignItems: 'stretch',
          '--chart-panel-height': `${chartHeight}vh`,
        } as React.CSSProperties}
      >
        {/* Left Panel (fixed column) */}
        <LeftControlPanel
          elements={elements}
          onToggleElement={handleToggleElement}
          onPreToggleElement={handlePreToggleElement}
          isDarkMode={isDarkMode}
          chartHeight={chartHeight}
          showStandalonePriceItem={prefersFiscal}
          analysisDateModeEps={analysisDateModeEps}
          onAnalysisDateModeEpsChange={(mode) => {
            applyUnifiedAnalysisDateMode(mode);
          }}
          analysisDateModeOcf={analysisDateModeOcf}
          onAnalysisDateModeOcfChange={(mode) => {
            applyUnifiedAnalysisDateMode(mode);
          }}
          normalizedPE={normalizedPE}
          normalizedPOCF={normalizedPOCF}
          hiddenElementIds={isRestrictedAnalysisUser ? hiddenAnalysisElementIds : []}
          hiddenCategoryItemKeys={isRestrictedAnalysisUser ? hiddenAnalysisCategoryItemKeys : []}
          sourceLabelByElementId={chartSourceLabelByElementId}
          defaultExpandedCategoryId={prefsScope === 'Charts' ? 'none' : (prefersFiscal ? 'financials' : undefined)}
        />

        {/* Center Content Area - Full Height Layout */}
        <div id="center-content-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.625rem', height: '100%', minHeight: 0 }}>
          {/* Date Range Selector - Full Width */}
          <DateRangeSelector
            selectedRange={selectedRange}
            maxDate={maxAllowedDate}
            onRangeChange={(range, start, end) => {
              visibleRangeChangeSourceRef.current = 'date-range-container';
              setSelectedRange(range);
              // For 5D: choose the last 5 AVAILABLE price points (trading days),
              // not "today - 5" calendar days. This avoids weekend/timezone gaps
              // that can make the chart look like zeros.
              if (range === '5D' && allPriceDataWithDates.length > 0) {
                const lastIndex = allPriceDataWithDates.length - 1;
                const startIndex = Math.max(0, lastIndex - 4);
                const slice = allPriceDataWithDates.slice(startIndex, lastIndex + 1);
                const computedStartKey = toUTCDateKey(slice[0].timestamp);
                const computedEndKey = toUTCDateKey(slice[slice.length - 1].timestamp);
                updateChartsStateRef({
                  selectedRange: range,
                  startDate: computedStartKey,
                  endDate: computedEndKey,
                });
                setStartDate(computedStartKey);
                setEndDate(computedEndKey);
                saveChartsStateLocalImmediate({
                  selectedRange: range,
                  startDate: computedStartKey,
                  endDate: computedEndKey,
                });
                saveChartsStateImmediate({
                  selectedRange: range,
                  startDate: computedStartKey,
                  endDate: computedEndKey,
                });
                return;
              }

              const constrainedEnd = end > maxAllowedDate ? maxAllowedDate : end;
              const nextStartKey = toUTCDateKey(start);
              const nextEndKey = toUTCDateKey(constrainedEnd);
              updateChartsStateRef({
                selectedRange: range,
                startDate: nextStartKey,
                endDate: nextEndKey,
              });
              setStartDate(nextStartKey);
              setEndDate(nextEndKey);
              saveChartsStateLocalImmediate({
                selectedRange: range,
                startDate: nextStartKey,
                endDate: nextEndKey,
              });
              saveChartsStateImmediate({
                selectedRange: range,
                startDate: nextStartKey,
                endDate: nextEndKey,
              });
            }}
            theme={isDarkMode ? 'dark' : 'light'}
            customStartDate={new Date(safeStartKey)}
            customEndDate={new Date(safeEndKey)}
            selectedPeriodType={selectedPeriodType}
            onPeriodTypeChange={(periodType) => {
              updateChartsStateRef({ selectedPeriodType: periodType });
              setSelectedPeriodType(periodType);
              saveChartsStateLocalImmediate({ selectedPeriodType: periodType });
              saveChartsStateImmediate({ selectedPeriodType: periodType });
            }}
            onCustomDateChange={(start, end) => {
              if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
                return;
              }
              visibleRangeChangeSourceRef.current = 'date-range-container';
              const constrainedEnd = end > maxAllowedDate ? maxAllowedDate : end;
              const nextStartKey = toUTCDateKey(start);
              const nextEndKey = toUTCDateKey(constrainedEnd);
              if (!nextStartKey || !nextEndKey) {
                return;
              }
              latestRangeRef.current = { startKey: nextStartKey, endKey: nextEndKey };
              updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
              setSelectedRange('CUSTOM');
              setStartDate(nextStartKey);
              setEndDate(nextEndKey);
              saveChartsStateLocalImmediate({
                selectedRange: 'CUSTOM',
                startDate: nextStartKey,
                endDate: nextEndKey,
              });
              saveChartsStateImmediate({
                selectedRange: 'CUSTOM',
                startDate: nextStartKey,
                endDate: nextEndKey,
              });
            }}
          />

          {/* Tooltip Data Display - Full Width */}
          <div id="tooltip-data-display" className="tooltip-container" style={{
              padding: '0.5rem',
              backgroundColor: isDarkMode ? '#111827' : '#ffffff',
              borderRadius: '0.5rem',
              border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
              width: '96%',
              boxSizing: 'border-box',
              height: '3.8rem',
              position: 'relative',
              zIndex: 0,
              
            }}>
            {tooltipData ? (
                <div id="tooltip-no-data" style={{ display: 'flex', alignItems: 'stretch', gap: '0.75rem', position: 'relative', minHeight: '4rem', width: '100%' }}>
                  <div id="tooltip-left-data" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 auto', minWidth: 0, width: showNoTooltipNormalizedFields ? 'calc(100% - 430px)' : 'calc(100% - 80px)', maxWidth: showNoTooltipNormalizedFields ? 'calc(100% - 430px)' : 'calc(100% - 80px)' }}>
                    <div id="tooltip-date-display" style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#0D0D0D', height: '1.25rem' }}>
                      {(() => {
                        // Show the cursor date (updates with mouse move). Fallback to the most recent PRICE date.
                        const dateKey = tooltipData?.date
                          ? toUTCDateKey(tooltipData.date)
                          : (endDate || toUTCDateKey(maxAllowedDate));

                        const [year, month, day] = dateKey.split('-');
                        return (
                          <>
                            <span>{selectedStock}</span>
                            <span style={{ marginLeft: '0.5rem' }}>{`${day}/${month}/${year}`}</span>
                          </>
                        );
                      })()}
                    </div>
                    {/* Build array of visible items */}
                    {(() => {
                      const flatEpsColor = elements.find(e => e.id === 'flat-eps-ttm')?.color ?? '#f97316';
                      const flatOcfColor = elements.find(e => e.id === 'flat-ocf-ttm')?.color ?? '#22c55e';
                      const priceColor = elements.find(e => e.id === 'price-line')?.color ?? '#3b82f6';
                      const entryPriceColor = elements.find(e => e.id === 'entry-point-1')?.color ?? '#10b981';
                      const exitPriceColor = elements.find(e => e.id === 'entry-point-2')?.color ?? '#ef4444';
                      const entryPriceOcfColor = elements.find(e => e.id === 'entry-point-1-ocf')?.color ?? '#0ea5e9';
                      const exitPriceOcfColor = elements.find(e => e.id === 'entry-point-2-ocf')?.color ?? '#f97316';

                      // Keep tooltip order consistent with the left-side `categories-container`.
                      // Source of truth is currently the static category item ordering in
                      // src/components/LeftControlPanel.tsx.
                      const tooltipOrderIds: string[] = [
                        // Standalone
                        'price-line',
                        // Financials
                        'revenue',
                        'gross-profit',
                        'operating-income',
                        'pretax-income',
                        'net-income',
                        'eps-diluted',
                        'cash-equivalents',
                        'total-assets',
                        'short-term-debt',
                        'long-term-debt',
                        'total-liabilities',
                        'shareholders-equity',
                        'operating-cash-flow',
                        'free-cash-flow',
                        'dividends',
                        'buybacks',
                        // Ratios
                        'pe',
                        'p-fcf-ratio',
                        'p-ocf-ratio',
                        'gross-profit-margin',
                        'operating-margin',
                        'net-margin',
                        'operating-cf-margin',
                        'free-cf-margin',
                        'operating-cf-to-ni',
                        'free-cf-to-ni',
                        'roic',
                        // Analysis
                        'flat-eps-ttm',
                        'fair-price',
                        'fair-price-flat-eps-ttm',
                        'entry-point-1',
                        'entry-point-2',
                        'flat-ocf-ttm',
                        'fair-price-ocf',
                        'entry-point-1-ocf',
                        'entry-point-2-ocf',
                      ];

                      const tooltipOrderIndex = new Map<string, number>(
                        tooltipOrderIds.map((id, idx) => [id, idx])
                      );

                      // Get visible elements in the fixed UI list order.
                      // IMPORTANT: do NOT sort by orderIndex (selection order), so Price
                      // remains first whenever it's visible.
                      const visibleElements = elements
                        .filter(e => e.visible)
                        .slice()
                        .sort((a, b) => {
                          const ai = tooltipOrderIndex.get(a.id) ?? 9999;
                          const bi = tooltipOrderIndex.get(b.id) ?? 9999;
                          if (ai !== bi) return ai - bi;
                          return a.id.localeCompare(b.id);
                        });
                      
                      // Build items in the correct order
                      const items = [];

                      for (const element of visibleElements) {
                        switch (element.id) {
                          case 'price-line':
                            items.push(
                              <div id="tooltip-price-item" key="price" style={getTooltipItemStyle('Price', 'number2')}>
                                <span style={{ color: priceColor, fontWeight: 'bold', fontSize: '0.875rem' }}>●</span>
                                <span style={{ color: priceColor }}>{`Price:${tooltipData.graphPrice.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          case 'pe':
                            {
                              const itemColor = element.color ?? '#000000';
                            items.push(
                              <div id="tooltip-pe-item" key="pe" style={getTooltipItemStyle('PE', 'number2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>
                                  {`PE:${(Math.floor(tooltipData.graphPE * 100) / 100).toFixed(2)}`}
                                  {peAttractiveness ? (
                                    <span style={{ color: '#000000' }}>{` [Median PE:${peAttractiveness.medianPe.toFixed(2)}]`}</span>
                                  ) : null}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'p-fcf-ratio':
                            {
                              const itemColor = element.color ?? '#38bdf8';
                              items.push(
                                <div id="tooltip-pfcf-item" key="p-fcf-ratio" style={getTooltipItemStyle('P/FCF', 'number2')}>
                                  <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                  <span style={{ color: itemColor }}>{`P/FCF:${(tooltipData.graphPfcf ?? tooltipData.graphPE ?? 0).toFixed(2)}`}</span>
                                </div>
                              );
                            }
                            break;
                          case 'p-ocf-ratio':
                            {
                              const itemColor = element.color ?? '#f472b6';
                              items.push(
                                <div id="tooltip-pocf-item" key="p-ocf-ratio" style={getTooltipItemStyle('P/OCF ratio', 'number2')}>
                                  <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                  <span style={{ color: itemColor }}>
                                    {`P/OCF ratio:${(tooltipData.graphPocf ?? tooltipData.graphPE ?? 0).toFixed(2)}`}
                                    {pocfAttractiveness ? (
                                      <span style={{ color: itemColor }}>{` [Median P/OCF:${pocfAttractiveness.medianPocf.toFixed(2)}]`}</span>
                                    ) : null}
                                  </span>
                                </div>
                              );
                            }
                            break;
                          case 'revenue':
                            {
                              const itemColor = element.color ?? '#22c55e';
                            items.push(
                              <div id="tooltip-revenue-item" key="revenue" style={getTooltipItemStyle('Revenue', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Revenue:${formatRevenueWithUnits(tooltipData.graphRevenue)}`}</span>
                              </div>
                            );
                            }
                            break;
                          case 'flat-eps-ttm':
                            items.push(
                              <div id="tooltip-flat-eps-ttm-item" key="flat-eps-ttm" style={getTooltipItemStyle('Flat EPS (ttm)', 'number2')}>
                                <span style={{ color: flatEpsColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: flatEpsColor }}>{`Flat EPS (ttm):${tooltipData.graphFlatEPSTTM.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          case 'flat-ocf-ttm':
                            items.push(
                              <div id="tooltip-flat-ocf-ttm-item" key="flat-ocf-ttm" style={getTooltipItemStyle('Flat OCF/Share (ttm)', 'number2')}>
                                <span style={{ color: flatOcfColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: flatOcfColor }}>{`Flat OCF/Share (ttm):${(tooltipData.graphFlatOCFTTM ?? 0).toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          case 'fair-price': {
                            if (!(normalizedPE > 0)) break;
                            const isHovered = tooltipData.hoveredElementId === 'fair-price';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphFairPrice || 0);
                            const itemColor = element.color ?? '#8b5cf6';
                            items.push(
                              <div id="tooltip-fair-price-item" key="fair-price" style={getTooltipItemStyle('Fair Price', 'money2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Fair Price:${value.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          }
                          case 'fair-price-ocf': {
                            if (!(normalizedPOCF > 0)) break;
                            const isHovered = tooltipData.hoveredElementId === 'fair-price-ocf';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphFairPriceOCF || 0);
                            const itemColor = element.color ?? '#6366f1';
                            items.push(
                              <div id="tooltip-fair-price-ocf-item" key="fair-price-ocf" style={getTooltipItemStyle('Fair Price', 'money2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Fair Price:${value.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          }
                          case 'fair-price-flat-eps-ttm': {
                            const isHovered = tooltipData.hoveredElementId === 'fair-price-flat-eps-ttm';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphFairPriceFlatEPSTTM || 0);
                            const itemColor = element.color ?? '#06b6d4';
                            items.push(
                              <div id="tooltip-fair-price-flat-eps-ttm-item" key="fair-price-flat-eps-ttm" style={getTooltipItemStyle('Fair Price Flat EPS (ttm)', 'money2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>
                                  Fair Price Flat EPS (ttm):${value.toFixed(2)}
                                </span>
                              </div>
                            );
                            break;
                          }
                          case 'entry-point-1': {
                            if (!(normalizedPE > 0)) break;
                            const isHovered = tooltipData.hoveredElementId === 'entry-point-1';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphEntryPrice || 0);
                            items.push(
                              <div id="no-tooltip-entry-price-item" key="entry-point-1" style={getTooltipItemStyle('Entry Price', 'money2')}>
                                <span style={{ color: entryPriceColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: entryPriceColor }}>{`Entry Price:${value.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          }
                          case 'entry-point-1-ocf': {
                            if (!(normalizedPOCF > 0)) break;
                            const isHovered = tooltipData.hoveredElementId === 'entry-point-1-ocf';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphEntryPriceOCF || 0);
                            items.push(
                              <div id="tooltip-entry-price-ocf-item" key="entry-point-1-ocf" style={getTooltipItemStyle('Entry Price', 'money2')}>
                                <span style={{ color: entryPriceOcfColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: entryPriceOcfColor }}>{`Entry Price:${value.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          }
                          case 'entry-point-2': {
                            if (!(normalizedPE > 0)) break;
                            const isHovered = tooltipData.hoveredElementId === 'entry-point-2';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphExitPrice || 0);
                            items.push(
                              <div id="no-tooltip-exit-price-item" key="entry-point-2" style={getTooltipItemStyle('Exit Price', 'money2')}>
                                <span style={{ color: exitPriceColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: exitPriceColor }}>{`Exit Price:${value.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          }
                          case 'entry-point-2-ocf': {
                            if (!(normalizedPOCF > 0)) break;
                            const isHovered = tooltipData.hoveredElementId === 'entry-point-2-ocf';
                            const value = (isHovered && tooltipData.hoveredElementValue !== null && tooltipData.hoveredElementValue !== undefined)
                              ? tooltipData.hoveredElementValue
                              : (tooltipData.graphExitPriceOCF || 0);
                            items.push(
                              <div id="tooltip-exit-price-ocf-item" key="entry-point-2-ocf" style={getTooltipItemStyle('Exit Price', 'money2')}>
                                <span style={{ color: exitPriceOcfColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: exitPriceOcfColor }}>{`Exit Price:${value.toFixed(2)}`}</span>
                              </div>
                            );
                            break;
                          }
                          case 'gross-profit':
                            {
                              const itemColor = element.color ?? '#8b5cf6';
                            items.push(
                              <div id="tooltip-grossprofit-item" key="grossprofit" style={getTooltipItemStyle('Gross Profit', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Gross Profit:${formatRevenueWithUnits(tooltipData.graphGrossProfit)}`}</span>
                              </div>
                            );
                            }
                            break;
                          case 'operating-income':
                            {
                              const itemColor = element.color ?? '#ea580c';
                            items.push(
                              <div id="tooltip-operatingincome-item" key="operatingincome" style={getTooltipItemStyle('Operating Income', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Operating Income:${formatRevenueWithUnits(tooltipData.graphOperatingIncome)}`}</span>
                              </div>
                            );
                            }
                            break;
                          case 'pretax-income':
                            {
                              const itemColor = element.color ?? '#06b6d4';
                            items.push(
                              <div id="tooltip-pretaxincome-item" key="pretaxincome" style={getTooltipItemStyle('Pretax Income', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Pretax Income:${formatRevenueWithUnits(tooltipData.graphPretaxIncome)}`}</span>
                              </div>
                            );
                            }
                            break;
                          case 'net-income':
                            {
                              const itemColor = element.color ?? '#f59e0b';
                            items.push(
                              <div id="tooltip-netincome-item" key="netincome" style={getTooltipItemStyle('Net Income', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`Net Income:${formatRevenueWithUnits(tooltipData.graphNetIncome)}`}</span>
                              </div>
                            );
                            }
                            break;
                          case 'eps-diluted':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-eps-item" key="eps" style={getTooltipItemStyle('EPS', 'number2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>{`EPS:${tooltipData.graphEps.toFixed(2)}`}</span>
                              </div>
                            );
                            }
                            break;
                          case 'cash-equivalents':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-cash-item" key="cash" style={getTooltipItemStyle('Cash', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>
                                  Cash:{formatRevenueWithUnits(tooltipData.graphCash)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'total-assets':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-totalassets-item" key="totalassets" style={getTooltipItemStyle('Total Assets', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>
                                  Total Assets:{formatRevenueWithUnits(tooltipData.graphTotalAssets)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'short-term-debt':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-shorttermdebt-item" key="shorttermdebt" style={getTooltipItemStyle('Short-Term Debt', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                                <span style={{ color: itemColor }}>
                                  Short-Term Debt:{formatRevenueWithUnits(tooltipData.graphShortTermDebt)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'long-term-debt':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-longtermdebt-item" key="longtermdebt" style={getTooltipItemStyle('Long-Term Debt', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Long-Term Debt:{formatRevenueWithUnits(tooltipData.graphLongTermDebt)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'total-liabilities':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-totalliabilities-item" key="totalliabilities" style={getTooltipItemStyle('Total Liabilities', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Total Liabilities:{formatRevenueWithUnits(tooltipData.graphTotalLiabilities)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'shareholders-equity':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-shareholdersequity-item" key="shareholdersequity" style={getTooltipItemStyle("Shareholders' Equity", 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Shareholders' Equity:{formatRevenueWithUnits(tooltipData.graphShareholdersEquity)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'operating-cash-flow':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-operatingcashflow-item" key="operatingcashflow" style={getTooltipItemStyle('Operating Cash Flow', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Operating Cash Flow:{formatRevenueWithUnits(tooltipData.graphOperatingCashFlow)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'free-cash-flow':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-freecashflow-item" key="freecashflow" style={getTooltipItemStyle('Free Cash Flow', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Free Cash Flow:{formatRevenueWithUnits(tooltipData.graphFreeCashFlow)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'dividends':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-dividends-item" key="dividends" style={getTooltipItemStyle('Dividends', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Dividends:{formatRevenueWithUnits(tooltipData.graphDividends)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'buybacks':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-buybacks-item" key="buybacks" style={getTooltipItemStyle('Buybacks', 'k')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Buybacks:{formatRevenueWithUnits(tooltipData.graphBuybacks)}
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'gross-profit-margin':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-gross-profit-margin-item" key="gross-profit-margin" style={getTooltipItemStyle('Gross Profit Margin', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Gross Profit Margin:{tooltipData.graphGrossProfitMargin.toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'operating-margin':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-operating-margin-item" key="operating-margin" style={getTooltipItemStyle('Operating Margin', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Operating Margin:{tooltipData.graphOperatingMargin.toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'net-margin':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-net-margin-item" key="net-margin" style={getTooltipItemStyle('Net Margin', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Net Margin:{tooltipData.graphNetMargin.toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'operating-cf-margin':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-operating-cf-margin-item" key="operating-cf-margin" style={getTooltipItemStyle('Operating Cash Flow Margin', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Operating Cash Flow Margin:{tooltipData.graphOperatingCFMargin.toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'free-cf-margin':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-free-cf-margin-item" key="free-cf-margin" style={getTooltipItemStyle('Free Cash Flow Margin', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  Free Cash Flow Margin:{(tooltipData.graphFreeCFMargin || 0).toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'operating-cf-to-ni':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-ocf-to-ni-item" key="ocf-to-ni" style={getTooltipItemStyle('OCF to Net Income', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  OCF to Net Income:{(tooltipData.graphOCFToNetIncome || 0).toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'free-cf-to-ni':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-fcf-to-ni-item" key="fcf-to-ni" style={getTooltipItemStyle('FCF to Net Income', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  FCF to Net Income:{(tooltipData.graphFCFToNetIncome || 0).toFixed(2)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                          case 'roic':
                            {
                              const itemColor = element.color ?? textColor;
                            items.push(
                              <div id="tooltip-roic-item" key="roic" style={getTooltipItemStyle('ROIC', 'percent2')}>
                                <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                                <span style={{ color: itemColor }}>
                                  ROIC:{(tooltipData.graphROIC || 0).toFixed(1)}%
                                </span>
                              </div>
                            );
                            }
                            break;
                        }
                      }

                      // Create key based on visible elements order
                      const gridKey = elements
                        .filter(e => e.visible)
                        .sort((a, b) => (a.orderIndex ?? 9999) - (b.orderIndex ?? 9999))
                        .map(e => e.id)
                        .join('-');

                      return (
                        <div
                          key={gridKey}
                          id="tooltip-data-row"
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'nowrap',
                            justifyContent: 'flex-start',
                            gap: 'clamp(16px, 2vw, 36px)',
                            fontSize: '0.875rem',
                            width: '100%',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflowX: 'hidden',
                          }}
                        >
                          {items}
                        </div>
                    );
                  })()}
                  </div>
                  
                  {/* Divider line */}
                  <div id="tooltip-divider" className="tooltip-divider" style={{
                    width: '1px',
                    height: '2rem',
                    backgroundColor: isDarkMode ? '#374151' : '#D1D5DB',
                    position: 'absolute',
                    right: showNoTooltipNormalizedFields ? '430px' : '80px',
                    top: '5px',
                    transition: 'none',
                  }} />

                  <button
                    id="tooltip-normalized-toggle"
                    type="button"
                    onClick={() => setShowNoTooltipNormalizedFields((prev) => !prev)}
                    style={{
                      position: 'absolute',
                      right: showNoTooltipNormalizedFields ? '400px' : '16px',
                      top: '8px',
                      background: 'transparent',
                      border: 'none',
                      padding: '0 6px',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 3,
                      transition: 'none',
                    }}
                    aria-label="Toggle normalized fields"
                  >
                    <img
                      id="tooltip-normalized-toggle-icon"
                      src="/images/chevrons-left.svg"
                      alt="Toggle"
                      style={{
                        width: '20px',
                        height: '20px',
                        transform: showNoTooltipNormalizedFields ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'none',
                      }}
                    />
                  </button>
                  
                  {/* Right side - Normalized PE and P/OCF */}
                  <div
                    id="tooltip-normalized-fields-container"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '1rem',
                      alignItems: 'center',
                      position: 'absolute',
                      right: '36px',
                      top: '8px',
                      opacity: showNoTooltipNormalizedFields ? 1 : 0,
                      transform: 'none',
                      maxWidth: showNoTooltipNormalizedFields ? '380px' : '0px',
                      overflow: 'hidden',
                      pointerEvents: showNoTooltipNormalizedFields ? 'auto' : 'none',
                      zIndex: 2,
                      transition: 'none',
                    }}
                  >
                    {/* Normalized PE */}
                    <div id="tooltip-normalized-pe-container" className="normalized-pe-container" style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}>
                      <span
                        id="tooltip-normalized-pe-label"
                        style={{ fontSize: '0.875rem', color: isDarkMode ? '#F9FAFB' : '#6B7280', whiteSpace: 'nowrap' }}
                      >
                        Normalized PE
                      </span>
                      <input
                        id="tooltip-normalized-pe-input"
                        type="text"
                        value={normalizedPEInput}
                        onChange={handleNormalizedPEChange}
                        style={{
                          fontSize: '0.8125rem',
                          padding: '0',
                          border: `1px solid ${isDarkMode ? '#374151' : '#D1D5DB'}`,
                          borderRadius: '0.1875rem',
                          backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
                          color: isDarkMode ? '#D1D5DB' : '#5A5A5A',
                          width: '60px',
                          outline: 'none',
                          height: '1.875rem',
                          lineHeight: '1.875rem',
                          textAlign: 'center',
                        }}
                      />
                    </div>
                    
                  
                  
                    
                    {/* Normalized P/OCF */}
                    <div id="tooltip-normalized-pocf-container" className="normalized-pocf-container" style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}>
                      <span
                        id="tooltip-normalized-pocf-label"
                        style={{ fontSize: '0.875rem', color: isDarkMode ? '#F9FAFB' : '#6B7280', whiteSpace: 'nowrap' }}
                      >
                        Normalized P/OCF
                      </span>
                      <input
                        id="tooltip-normalized-pocf-input"
                        type="text"
                        value={normalizedPOCFInput}
                        onChange={handleNormalizedPOCFChange}
                        style={{
                          fontSize: '0.8125rem',
                          padding: '0',
                          border: `1px solid ${isDarkMode ? '#374151' : '#D1D5DB'}`,
                          borderRadius: '0.1875rem',
                          backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
                          color: isDarkMode ? '#D1D5DB' : '#5A5A5A',
                          width: '60px',
                          outline: 'none',
                          height: '1.875rem',
                          lineHeight: '1.875rem',
                          textAlign: 'center',
                        }}
                      />
                    </div>
                  </div>
                </div>
            ) : (
                <div id="tooltip-no-data" style={{ display: 'flex', alignItems: 'stretch', gap: '0.75rem', position: 'relative', minHeight: '4rem', width: '100%' }}>
                  <div
                    id="tooltip-left-no-data"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      flex: '1 1 auto',
                      minWidth: 0,
                      width: showNoTooltipNormalizedFields ? 'calc(100% - 430px)' : 'calc(100% - 80px)',
                      maxWidth: showNoTooltipNormalizedFields ? 'calc(100% - 430px)' : 'calc(100% - 80px)',
                      transition: 'none',
                    }}
                  >
                  <div id="tooltip-date-placeholder" style={{ fontSize: '0.875rem', fontWeight: 'bold', color: isDarkMode ? '#F9FAFB' : '#0D0D0D', height: '1.25rem' }}>
                    {(() => {
                      // When not hovering, show the current navigator end date (the "latest" visible date).
                      const dateKey = endDate || toUTCDateKey(maxAllowedDate);
                      const [year, month, day] = dateKey.split('-');

                      return (
                        <>
                          <span>{selectedStock}</span>
                          <span style={{ marginLeft: '0.5rem' }}>{`${day}/${month}/${year}`}</span>
                        </>
                      );
                    })()}
                  </div>
                  <div
                    id="no-tooltip-data-row"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      justifyContent: 'flex-start',
                      gap: 'clamp(16px, 2vw, 36px)',
                      fontSize: '0.875rem',
                      width: '100%',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflowX: 'hidden',
                    }}
                  >
                  {/* Build array of visible items */}
                  {(() => {
                    const flatEpsColor = elements.find(e => e.id === 'flat-eps-ttm')?.color ?? '#f97316';
                    const flatOcfColor = elements.find(e => e.id === 'flat-ocf-ttm')?.color ?? '#22c55e';
                    const priceColor = elements.find(e => e.id === 'price-line')?.color ?? '#3b82f6';
                    const entryPriceColor = elements.find(e => e.id === 'entry-point-1')?.color ?? '#10b981';
                    const exitPriceColor = elements.find(e => e.id === 'entry-point-2')?.color ?? '#ef4444';
                    const entryPriceOcfColor = elements.find(e => e.id === 'entry-point-1-ocf')?.color ?? '#0ea5e9';
                    const exitPriceOcfColor = elements.find(e => e.id === 'entry-point-2-ocf')?.color ?? '#f97316';

                    // Use tooltipData values if available (mouse over graph), otherwise use last values
                    const currentTooltip = tooltipData as AdvancedChartTooltipData | null;
                    // When not hovering, derive values from the navigator end date.
                    // This keeps the info tooltip consistent with the visible range.
                    const tooltipEndKey = endDate || toUTCDateKey(maxAllowedDate);
                    const tooltipEnd = dateKeyToUTCDate(tooltipEndKey, true);

                    const valueAtOrBeforeFromDatedSeries = <T extends { date: string | Date; value: number | null }>(rows: T[]): number => {
                      if (!Array.isArray(rows) || rows.length === 0) return 0;
                      for (let i = rows.length - 1; i >= 0; i--) {
                        const t = new Date(rows[i].date).getTime();
                        const v = rows[i].value;
                        if (Number.isFinite(t) && t <= tooltipEnd.getTime() && typeof v === 'number' && Number.isFinite(v)) {
                          return v;
                        }
                      }
                      const fallback = rows[rows.length - 1]?.value;
                      return typeof fallback === 'number' && Number.isFinite(fallback) ? fallback : 0;
                    };

                    const valueAtOrBeforeFromPeriods = (values?: Array<number | null> | null): number => {
                      const periods = financialData.periods;
                      if (!Array.isArray(periods) || periods.length === 0) return 0;
                      if (!Array.isArray(values) || values.length === 0) return 0;
                      const len = Math.min(periods.length, values.length);
                      for (let i = len - 1; i >= 0; i--) {
                        const t = periods[i]?.getTime?.() ?? new Date(periods[i] as any).getTime();
                        if (Number.isFinite(t) && t <= tooltipEnd.getTime()) {
                          const v = values[i];
                          if (typeof v === 'number' && Number.isFinite(v)) return v;
                        }
                      }
                      for (let i = len - 1; i >= 0; i--) {
                        const v = values[i];
                        if (typeof v === 'number' && Number.isFinite(v)) return v;
                      }
                      return 0;
                    };

                    const valueAtOrBeforeFromPrice = (): number => {
                      if (!Array.isArray(filteredPriceDataWithDates) || filteredPriceDataWithDates.length === 0) return 0;
                      for (let i = filteredPriceDataWithDates.length - 1; i >= 0; i--) {
                        const p = filteredPriceDataWithDates[i] as any;
                        const k = toUTCDateKey(p.timestamp);
                        if (k <= tooltipEndKey) {
                          return Number(p.close) || 0;
                        }
                      }
                      return Number((filteredPriceDataWithDates as any)[filteredPriceDataWithDates.length - 1]?.close) || 0;
                    };

                    const valueAtOrBeforeFromElementSeries = (elementId: string): number => {
                      const el = elements.find(e => e.id === elementId);
                      if (!el || !Array.isArray(el.data) || el.data.length === 0) return 0;

                      for (let i = el.data.length - 1; i >= 0; i--) {
                        const point = el.data[i] as any;
                        if (!point || typeof point !== 'object') continue;

                        const rawDate = point.x ?? point.date ?? point.timestamp;
                        const t = rawDate instanceof Date ? rawDate.getTime() : new Date(rawDate).getTime();
                        const rawValue = point.y ?? point.value;
                        const v = typeof rawValue === 'number' ? rawValue : Number(rawValue);

                        if (Number.isFinite(t) && t <= tooltipEnd.getTime() && Number.isFinite(v)) {
                          return v;
                        }
                      }

                      for (let i = el.data.length - 1; i >= 0; i--) {
                        const point = el.data[i] as any;
                        if (!point || typeof point !== 'object') continue;
                        const rawValue = point.y ?? point.value;
                        const v = typeof rawValue === 'number' ? rawValue : Number(rawValue);
                        if (Number.isFinite(v)) return v;
                      }

                      return 0;
                    };

                    const priceValue = currentTooltip?.graphPrice ?? valueAtOrBeforeFromPrice();
                    const peValue = currentTooltip?.graphPE ?? valueAtOrBeforeFromDatedSeries(peData);
                    const pfcfValue = currentTooltip?.graphPfcf ?? valueAtOrBeforeFromDatedSeries(bestPfcfDailySeries);
                    const pocfValue = currentTooltip?.graphPocf ?? valueAtOrBeforeFromDatedSeries(bestPocfDailySeries);
                    const revenueValue = currentTooltip?.graphRevenue ?? valueAtOrBeforeFromPeriods(financialData.revenue);
                    const grossProfitValue = currentTooltip?.graphGrossProfit ?? valueAtOrBeforeFromPeriods(financialData.grossProfit);
                    const operatingIncomeValue = currentTooltip?.graphOperatingIncome ?? valueAtOrBeforeFromPeriods(financialData.operatingIncome);
                    const pretaxIncomeValue = currentTooltip?.graphPretaxIncome ?? valueAtOrBeforeFromPeriods(financialData.pretaxIncome);
                    const netIncomeValue = currentTooltip?.graphNetIncome ?? valueAtOrBeforeFromPeriods(financialData.netIncome);
                    const epsValue = currentTooltip?.graphEps ?? valueAtOrBeforeFromPeriods(financialData.eps);
                    const cashValue = currentTooltip?.graphCash ?? valueAtOrBeforeFromPeriods(financialData.cash);
                    const totalAssetsValue = currentTooltip?.graphTotalAssets ?? valueAtOrBeforeFromPeriods(financialData.totalAssets);
                    const shortTermDebtValue = currentTooltip?.graphShortTermDebt ?? valueAtOrBeforeFromPeriods(financialData.shortTermDebt);
                    const longTermDebtValue = currentTooltip?.graphLongTermDebt ?? valueAtOrBeforeFromPeriods(financialData.longTermDebt);
                    const totalLiabilitiesValue = currentTooltip?.graphTotalLiabilities ?? valueAtOrBeforeFromPeriods(financialData.totalLiabilities);
                    const shareholdersEquityValue = currentTooltip?.graphShareholdersEquity ?? valueAtOrBeforeFromPeriods(financialData.totalStockholdersEquity);
                    const operatingCashFlowValue = currentTooltip?.graphOperatingCashFlow ?? valueAtOrBeforeFromPeriods(financialData.operatingCashFlow);
                    const freeCashFlowValue = currentTooltip?.graphFreeCashFlow ?? valueAtOrBeforeFromPeriods(financialData.freeCashFlow);
                    const dividendsValue = currentTooltip?.graphDividends ?? Math.abs(valueAtOrBeforeFromPeriods(financialData.dividends));
                    const buybacksValue = currentTooltip?.graphBuybacks ?? Math.abs(valueAtOrBeforeFromPeriods(financialData.buybacks));
                    const grossProfitMarginValue = currentTooltip?.graphGrossProfitMargin ?? valueAtOrBeforeFromPeriods(financialData.grossProfitMargin);
                    const operatingMarginValue = currentTooltip?.graphOperatingMargin ?? valueAtOrBeforeFromPeriods(financialData.operatingMargin);
                    const netMarginValue = currentTooltip?.graphNetMargin ?? valueAtOrBeforeFromPeriods(financialData.netMargin);
                    const operatingCFMarginValue = currentTooltip?.graphOperatingCFMargin ?? valueAtOrBeforeFromPeriods(financialData.operatingCashFlowMargin);
                    const freeCFMarginValue = currentTooltip?.graphFreeCFMargin ?? valueAtOrBeforeFromPeriods(financialData.freeCashFlowMargin);
                    const ocfToNetIncomeValue = currentTooltip?.graphOCFToNetIncome ?? valueAtOrBeforeFromPeriods(financialData.ocfToNetIncome);
                    const fcfToNetIncomeValue = currentTooltip?.graphFCFToNetIncome ?? valueAtOrBeforeFromPeriods(financialData.fcfToNetIncome);
                    const roicValue = currentTooltip?.graphROIC ?? valueAtOrBeforeFromPeriods(financialData.roic);
                    const flatEPSTTMValue = currentTooltip?.graphFlatEPSTTM ?? valueAtOrBeforeFromDatedSeries(flatEPSTTMData);
                    const fairPriceValue = currentTooltip?.graphFairPrice ?? valueAtOrBeforeFromDatedSeries(fairPriceData);
                    const fairPriceFlatEPSTTMValue = currentTooltip?.graphFairPriceFlatEPSTTM ?? valueAtOrBeforeFromDatedSeries(fairPriceFlatEPSTTMData);
                    const flatOCFTTMValue = currentTooltip?.graphFlatOCFTTM ?? valueAtOrBeforeFromDatedSeries(flatOCFTTMData);
                    const fairPriceOcfValue = currentTooltip?.graphFairPriceOCF ?? valueAtOrBeforeFromDatedSeries(fairPriceOcfData);

                    const entryElement = elements.find(e => e.id === 'entry-point-1');
                    const exitElement = elements.find(e => e.id === 'entry-point-2');
                    const entryAdj = typeof entryElement?.adjustment === 'number' ? entryElement.adjustment : 0;
                    const exitAdj = typeof exitElement?.adjustment === 'number' ? exitElement.adjustment : 0;

                    const entryOcfElement = elements.find(e => e.id === 'entry-point-1-ocf');
                    const exitOcfElement = elements.find(e => e.id === 'entry-point-2-ocf');
                    const entryOcfAdj = typeof entryOcfElement?.adjustment === 'number' ? entryOcfElement.adjustment : 0;
                    const exitOcfAdj = typeof exitOcfElement?.adjustment === 'number' ? exitOcfElement.adjustment : 0;

                    const entryPriceSeriesValue = valueAtOrBeforeFromElementSeries('entry-point-1');
                    const exitPriceSeriesValue = valueAtOrBeforeFromElementSeries('entry-point-2');
                    const entryPriceOcfSeriesValue = valueAtOrBeforeFromElementSeries('entry-point-1-ocf');
                    const exitPriceOcfSeriesValue = valueAtOrBeforeFromElementSeries('entry-point-2-ocf');

                    // Entry/Exit are derived from Fair Price, with adjustment as decimal.
                    // Entry goes downward: 0.2 => 0.8x. Exit goes upward: 0.2 => 1.2x.
                    const entryPriceValue = currentTooltip?.graphEntryPrice
                      ?? (entryPriceSeriesValue > 0
                        ? entryPriceSeriesValue
                        : (fairPriceValue > 0 ? fairPriceValue * Math.max(0, 1 - entryAdj) : 0));
                    const exitPriceValue = currentTooltip?.graphExitPrice
                      ?? (exitPriceSeriesValue > 0
                        ? exitPriceSeriesValue
                        : (fairPriceValue > 0 ? fairPriceValue * (1 + exitAdj) : 0));
                    const entryPriceOcfValue = currentTooltip?.graphEntryPriceOCF
                      ?? (entryPriceOcfSeriesValue > 0
                        ? entryPriceOcfSeriesValue
                        : (fairPriceOcfValue > 0 ? fairPriceOcfValue * Math.max(0, 1 - entryOcfAdj) : 0));
                    const exitPriceOcfValue = currentTooltip?.graphExitPriceOCF
                      ?? (exitPriceOcfSeriesValue > 0
                        ? exitPriceOcfSeriesValue
                        : (fairPriceOcfValue > 0 ? fairPriceOcfValue * (1 + exitOcfAdj) : 0));
                    
                    // Keep tooltip order consistent with the left-side `categories-container`.
                    // Source of truth is currently the static category item ordering in
                    // src/components/LeftControlPanel.tsx.
                    const tooltipOrderIds: string[] = [
                      // Standalone
                      'price-line',
                      // Financials
                      'revenue',
                      'gross-profit',
                      'operating-income',
                      'pretax-income',
                      'net-income',
                      'eps-diluted',
                      'cash-equivalents',
                      'total-assets',
                      'short-term-debt',
                      'long-term-debt',
                      'total-liabilities',
                      'shareholders-equity',
                      'operating-cash-flow',
                      'free-cash-flow',
                      'dividends',
                      'buybacks',
                      // Ratios
                      'pe',
                      'p-fcf-ratio',
                      'p-ocf-ratio',
                      'gross-profit-margin',
                      'operating-margin',
                      'net-margin',
                      'operating-cf-margin',
                      'free-cf-margin',
                      'operating-cf-to-ni',
                      'free-cf-to-ni',
                      'roic',
                      // Analysis
                      'flat-eps-ttm',
                      'fair-price',
                      'fair-price-flat-eps-ttm',
                      'entry-point-1',
                      'entry-point-2',
                      'flat-ocf-ttm',
                      'fair-price-ocf',
                      'entry-point-1-ocf',
                      'entry-point-2-ocf',
                    ];

                    const tooltipOrderIndex = new Map<string, number>(
                      tooltipOrderIds.map((id, idx) => [id, idx])
                    );

                    const visibleElements = elements
                      .filter(e => e.visible)
                      .slice()
                      .sort((a, b) => {
                        const ai = tooltipOrderIndex.get(a.id) ?? 9999;
                        const bi = tooltipOrderIndex.get(b.id) ?? 9999;
                        if (ai !== bi) return ai - bi;
                        return a.id.localeCompare(b.id);
                      });
                    
                    // Build items in the correct order
                    const items = [];

                    // Add remaining visible elements in order
                    for (const element of visibleElements) {
                      if (element.id === 'price-line') {
                        const itemColor = element.color ?? priceColor;
                        items.push(
                          <span id="no-tooltip-price-item" key="price" style={getTooltipItemStyle('Price', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold', fontSize: '0.875rem' }}>●</span>
                            <span style={{ color: itemColor }}>{`Price:${priceValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'pe') {
                        const itemColor = element.color ?? '#000000';
                        items.push(
                          <span id="no-tooltip-pe-item" key="pe" style={getTooltipItemStyle('PE', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>
                              {`PE:${(Math.floor(peValue * 100) / 100).toFixed(2)}`}
                              {peAttractiveness ? (
                                <span style={{ color: '#000000' }}>{` [Median PE:${peAttractiveness.medianPe.toFixed(2)}]`}</span>
                              ) : null}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'p-fcf-ratio') {
                        const itemColor = element.color ?? '#38bdf8';
                        items.push(
                          <span id="no-tooltip-pfcf-item" key="p-fcf-ratio" style={getTooltipItemStyle('P/FCF', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`P/FCF:${pfcfValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'p-ocf-ratio') {
                        const itemColor = element.color ?? '#f472b6';
                        items.push(
                          <span id="no-tooltip-pocf-item" key="p-ocf-ratio" style={getTooltipItemStyle('P/OCF ratio', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>
                              {`P/OCF ratio:${pocfValue.toFixed(2)}`}
                              {pocfAttractiveness ? (
                                <span style={{ color: itemColor }}>{` [Median P/OCF:${pocfAttractiveness.medianPocf.toFixed(2)}]`}</span>
                              ) : null}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'fair-price') {
                        if (!(normalizedPE > 0)) continue;
                        const itemColor = element.color ?? '#8b5cf6';
                        items.push(
                          <span id="no-tooltip-fair-price-item" key="fair-price" style={getTooltipItemStyle('Fair Price', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Fair Price:${fairPriceValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'fair-price-flat-eps-ttm') {
                        const itemColor = element.color ?? '#06b6d4';
                        items.push(
                          <span id="no-tooltip-fair-price-flat-eps-ttm-item" key="fair-price-flat-eps-ttm" style={getTooltipItemStyle('Fair Price Flat EPS (ttm)', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                            <span style={{ color: itemColor }}>
                              Fair Price Flat EPS (ttm):${fairPriceFlatEPSTTMValue.toFixed(2)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'flat-eps-ttm') {
                        const itemColor = element.color ?? flatEpsColor;
                        items.push(
                          <span id="no-tooltip-flat-eps-ttm-item" key="flat-eps-ttm" style={getTooltipItemStyle('Flat EPS (ttm)', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Flat EPS (ttm):${flatEPSTTMValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'flat-ocf-ttm') {
                        const itemColor = element.color ?? flatOcfColor;
                        items.push(
                          <span id="no-tooltip-flat-ocf-ttm-item" key="flat-ocf-ttm" style={getTooltipItemStyle('Flat OCF/Share (ttm)', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Flat OCF/Share (ttm):${flatOCFTTMValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'entry-point-1') {
                        if (!(normalizedPE > 0)) continue;
                        const itemColor = element.color ?? entryPriceColor;
                        items.push(
                          <span id="no-tooltip-entry-price-item" key="entry-point-1" style={getTooltipItemStyle('Entry Price', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Entry Price:${entryPriceValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'fair-price-ocf') {
                        if (!(normalizedPOCF > 0)) continue;
                        const itemColor = element.color ?? '#6366f1';
                        items.push(
                          <span id="no-tooltip-fair-price-ocf-item" key="fair-price-ocf" style={getTooltipItemStyle('Fair Price', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Fair Price:${fairPriceOcfValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'entry-point-1-ocf') {
                        if (!(normalizedPOCF > 0)) continue;
                        const itemColor = element.color ?? entryPriceOcfColor;
                        items.push(
                          <span id="no-tooltip-entry-price-ocf-item" key="entry-point-1-ocf" style={getTooltipItemStyle('Entry Price', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Entry Price:${entryPriceOcfValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'entry-point-2') {
                        if (!(normalizedPE > 0)) continue;
                        const itemColor = element.color ?? exitPriceColor;
                        items.push(
                          <span id="no-tooltip-exit-price-item" key="entry-point-2" style={getTooltipItemStyle('Exit Price', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Exit Price:${exitPriceValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'entry-point-2-ocf') {
                        if (!(normalizedPOCF > 0)) continue;
                        const itemColor = element.color ?? exitPriceOcfColor;
                        items.push(
                          <span id="no-tooltip-exit-price-ocf-item" key="entry-point-2-ocf" style={getTooltipItemStyle('Exit Price', 'money2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Exit Price:${exitPriceOcfValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'revenue') {
                        const itemColor = element.color ?? '#22c55e';
                        items.push(
                          <span id="no-tooltip-revenue-item" key="revenue" style={getTooltipItemStyle('Revenue', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Revenue:${formatRevenueWithUnits(revenueValue)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'gross-profit') {
                        const itemColor = element.color ?? '#8b5cf6';
                        items.push(
                          <span id="no-tooltip-grossprofit-item" key="grossprofit" style={getTooltipItemStyle('Gross Profit', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Gross Profit:${formatRevenueWithUnits(grossProfitValue)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'operating-income') {
                        const itemColor = element.color ?? '#ea580c';
                        items.push(
                          <span id="no-tooltip-operatingincome-item" key="operatingincome" style={getTooltipItemStyle('Operating Income', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Operating Income:${formatRevenueWithUnits(operatingIncomeValue)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'pretax-income') {
                        const itemColor = element.color ?? '#06b6d4';
                        items.push(
                          <span id="no-tooltip-pretaxincome-item" key="pretaxincome" style={getTooltipItemStyle('Pretax Income', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Pretax Income:${formatRevenueWithUnits(pretaxIncomeValue)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'net-income') {
                        const itemColor = element.color ?? '#f59e0b';
                        items.push(
                          <span id="no-tooltip-netincome-item" key="netincome" style={getTooltipItemStyle('Net Income', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`Net Income:${formatRevenueWithUnits(netIncomeValue)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'eps-diluted') {
                        const itemColor = element.color ?? '#10b981';
                        items.push(
                          <span id="no-tooltip-eps-item" key="eps" style={getTooltipItemStyle('EPS', 'number2')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: itemColor }}>{`EPS:${epsValue.toFixed(2)}`}</span>
                          </span>
                        );
                      }
                      if (element.id === 'cash-equivalents') {
                        const itemColor = element.color ?? '#22d3ee';
                        items.push(
                          <span id="no-tooltip-cash-item" key="cash" style={getTooltipItemStyle('Cash', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                            <span style={{ color: itemColor }}>
                              Cash:{formatRevenueWithUnits(cashValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'total-assets') {
                        const itemColor = element.color ?? '#14b8a6';
                        items.push(
                          <span id="no-tooltip-totalassets-item" key="totalassets" style={getTooltipItemStyle('Total Assets', 'k')}>
                            <span style={{ color: itemColor, fontWeight: 'bold' }}>● </span>
                            <span style={{ color: itemColor }}>
                              Total Assets:{formatRevenueWithUnits(totalAssetsValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'short-term-debt') {
                        items.push(
                          <span id="no-tooltip-shorttermdebt-item" key="shorttermdebt" style={getTooltipItemStyle('Short-Term Debt', 'k')}>
                            <span style={{ color: '#a855f7', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Short-Term Debt:{formatRevenueWithUnits(shortTermDebtValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'long-term-debt') {
                        items.push(
                          <span id="no-tooltip-longtermdebt-item" key="longtermdebt" style={getTooltipItemStyle('Long-Term Debt', 'k')}>
                            <span style={{ color: '#ef4444', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Long-Term Debt:{formatRevenueWithUnits(longTermDebtValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'total-liabilities') {
                        items.push(
                          <span id="no-tooltip-totalliabilities-item" key="totalliabilities" style={getTooltipItemStyle('Total Liabilities', 'k')}>
                            <span style={{ color: '#6366f1', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Total Liabilities:{formatRevenueWithUnits(totalLiabilitiesValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'shareholders-equity') {
                        items.push(
                          <span id="no-tooltip-shareholdersequity-item" key="shareholdersequity" style={getTooltipItemStyle("Shareholders' Equity", 'k')}>
                            <span style={{ color: '#059669', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Shareholders' Equity:{formatRevenueWithUnits(shareholdersEquityValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'operating-cash-flow') {
                        items.push(
                          <span id="no-tooltip-operatingcashflow-item" key="operatingcashflow" style={getTooltipItemStyle('Operating Cash Flow', 'k')}>
                            <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Operating Cash Flow:{formatRevenueWithUnits(operatingCashFlowValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'free-cash-flow') {
                        items.push(
                          <span id="no-tooltip-freecashflow-item" key="freecashflow" style={getTooltipItemStyle('Free Cash Flow', 'k')}>
                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Free Cash Flow:{formatRevenueWithUnits(freeCashFlowValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'dividends') {
                        items.push(
                          <span id="no-tooltip-dividends-item" key="dividends" style={getTooltipItemStyle('Dividends', 'k')}>
                            <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Dividends:{formatRevenueWithUnits(dividendsValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'buybacks') {
                        items.push(
                          <span id="no-tooltip-buybacks-item" key="buybacks" style={getTooltipItemStyle('Buybacks', 'k')}>
                            <span style={{ color: '#14b8a6', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Buybacks:{formatRevenueWithUnits(buybacksValue)}
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'gross-profit-margin') {
                        items.push(
                          <span id="no-tooltip-gross-profit-margin-item" key="gross-profit-margin" style={getTooltipItemStyle('Gross Profit Margin', 'percent2')}>
                            <span style={{ color: '#ec4899', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Gross Profit Margin:{grossProfitMarginValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'operating-margin') {
                        items.push(
                          <span id="no-tooltip-operating-margin-item" key="operating-margin" style={getTooltipItemStyle('Operating Margin', 'percent2')}>
                            <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Operating Margin:{operatingMarginValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'net-margin') {
                        items.push(
                          <span id="no-tooltip-net-margin-item" key="net-margin" style={getTooltipItemStyle('Net Margin', 'percent2')}>
                            <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Net Margin:{netMarginValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'operating-cf-margin') {
                        items.push(
                          <span id="no-tooltip-operating-cf-margin-item" key="operating-cf-margin" style={getTooltipItemStyle('Operating Cash Flow Margin', 'percent2')}>
                            <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Operating Cash Flow Margin:{operatingCFMarginValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'free-cf-margin') {
                        items.push(
                          <span id="no-tooltip-free-cf-margin-item" key="free-cf-margin" style={getTooltipItemStyle('Free Cash Flow Margin', 'percent2')}>
                            <span style={{ color: '#84cc16', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              Free Cash Flow Margin:{freeCFMarginValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'operating-cf-to-ni') {
                        items.push(
                          <span id="no-tooltip-ocf-to-ni-item" key="ocf-to-ni" style={getTooltipItemStyle('OCF to Net Income', 'percent2')}>
                            <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              OCF to Net Income:{ocfToNetIncomeValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'free-cf-to-ni') {
                        items.push(
                          <span id="no-tooltip-fcf-to-ni-item" key="fcf-to-ni" style={getTooltipItemStyle('FCF to Net Income', 'percent2')}>
                            <span style={{ color: '#14b8a6', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              FCF to Net Income:{fcfToNetIncomeValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                      if (element.id === 'roic') {
                        items.push(
                          <span id="no-tooltip-roic-item" key="roic" style={getTooltipItemStyle('ROIC', 'percent2')}>
                            <span style={{ color: '#dc2626', fontWeight: 'bold' }}>● </span>
                            <span style={{ color: textColor }}>
                              ROIC:{roicValue.toFixed(2)}%
                            </span>
                          </span>
                        );
                      }
                    }

                    return items;
                  })()}
                  </div>
                  </div>
                  
                  {/* Divider line */}
                  <div
                    id="no-tooltip-divider"
                    className="tooltip-divider"
                    style={{
                      width: '1px',
                      height: '2.2rem',
                      backgroundColor: isDarkMode ? '#374151' : '#D1D5DB',
                      position: 'absolute',
                      right: showNoTooltipNormalizedFields ? '430px' : '80px',
                      top: '5px',
                      transition: 'none',
                    }}
                  />

                  <button
                    id="no-tooltip-normalized-toggle"
                    type="button"
                    onClick={() => setShowNoTooltipNormalizedFields((prev) => !prev)}
                    style={{
                      position: 'absolute',
                      right: showNoTooltipNormalizedFields ? '400px' : '16px',
                      top: '8px',
                      background: 'transparent',
                      border: 'none',
                      padding: '0 6px',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 3,
                      transition: 'none',
                    }}
                    aria-label="Toggle normalized fields"
                  >
                    <img
                      id="no-tooltip-normalized-toggle-icon"
                      src="/images/chevrons-left.svg"
                      alt="Toggle"
                      style={{
                        width: '20px',
                        height: '20px',
                        transform: showNoTooltipNormalizedFields ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'none',
                      }}
                    />
                  </button>
                  
                  {/* Right side - Normalized PE and P/OCF */}
                  <div
                    id="no-tooltip-normalized-fields-container"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '1rem',
                      alignItems: 'center',
                      position: 'absolute',
                      right: '36px',
                      top: '8px',
                      opacity: showNoTooltipNormalizedFields ? 1 : 0,
                      transform: 'none',
                      maxWidth: showNoTooltipNormalizedFields ? '380px' : '0px',
                      overflow: 'hidden',
                      pointerEvents: showNoTooltipNormalizedFields ? 'auto' : 'none',
                      zIndex: 2,
                      transition: 'none',
                    }}
                  >
                    {/* Normalized PE */}
                    <div id="no-tooltip-normalized-pe-container" className="normalized-pe-container" style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}>
                      <span
                        id="no-tooltip-normalized-pe-label"
                        style={{ fontSize: '0.875rem', color: isDarkMode ? '#F9FAFB' : '#6B7280', whiteSpace: 'nowrap' }}
                      >
                        Normalized PE
                      </span>
                      <input
                        id="no-tooltip-normalized-pe-input"
                        type="text"
                        value={normalizedPEInput}
                        onChange={handleNormalizedPEChange}
                        style={{
                          fontSize: '0.8125rem',
                          padding: '0',
                          border: `1px solid ${isDarkMode ? '#374151' : '#D1D5DB'}`,
                          borderRadius: '0.1875rem',
                          backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
                          color: isDarkMode ? '#D1D5DB' : '#5A5A5A',
                          width: '60px',
                          outline: 'none',
                          height: '1.875rem',
                          lineHeight: '1.875rem',
                          textAlign: 'center',
                        }}
                      />
                    </div>
                    
                   
                    
                    {/* Normalized P/OCF */}
                    <div id="no-tooltip-normalized-pocf-container" className="normalized-pocf-container" style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}>
                      <span id="no-tooltip-normalized-pocf-label" style={{ fontSize: '0.875rem', color: isDarkMode ? '#F9FAFB' : '#6B7280', whiteSpace: 'nowrap' }}>
                        Normalized P/OCF
                      </span>
                      <input
                        id="no-tooltip-normalized-pocf-input"
                        type="text"
                        value={normalizedPOCFInput}
                        onChange={handleNormalizedPOCFChange}
                        style={{
                          fontSize: '0.8125rem',
                          padding: '0',
                          border: `1px solid ${isDarkMode ? '#374151' : '#D1D5DB'}`,
                          borderRadius: '0.1875rem',
                          backgroundColor: isDarkMode ? '#111827' : '#FFFFFF',
                          color: isDarkMode ? '#D1D5DB' : '#5A5A5A',
                          width: '60px',
                          outline: 'none',
                          height: '1.875rem',
                          lineHeight: '1.875rem',
                          textAlign: 'center',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chart Row: chart + right panel */}
            <div
              id="chart-row"
              style={{
                display: 'flex',
                gap: '0.625rem',
                alignItems: 'stretch',
                minHeight: 0,
                position: 'relative',
                zIndex: 5,
              }}
            >
              {/* Chart Container with Right Adjustment Panel */}
              <div id="chart-and-panel-container" style={{ display: 'flex', gap: '0.25rem', flex: 1, alignItems: 'stretch', minWidth: 0 }}>
                {/* Resizable Chart Container */}
                <div
                  id="resizable-chart-container"
                  ref={containerRef}
                  style={{
                    flex: chartWidth !== null ? 'none' : 0.955,
                    width: chartWidth !== null ? `${chartWidth}px` : undefined,
                    height: `${chartHeight}vh`,
                    position: 'relative',
                    zIndex: 2,
                    transition: isResizing ? 'none' : 'all 0.2s ease',
                    border: 'none',
                    borderRadius: '0rem',
                    overflow: 'visible',
                  }}
                >
                  <div
                    id="chart-main-container"
                    style={{
                      width: '100%',
                      height: '100%',
                      overflow: 'visible',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      paddingBottom: '2.5rem',
                      boxSizing: 'border-box',
                      backgroundColor: isDarkMode ? '#111827' : 'transparent',
                      border: `1px solid ${isDarkMode ? '#374151' : 'transparent'}`,
                      borderRadius: isDarkMode ? '0.25rem' : '0',
                    }}
                  >
                    {(showChartOverlayDebug && chartsDebug && (jumpDebugText || quarterEndDebugText)) && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          left: '0.5rem',
                          zIndex: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          pointerEvents: 'none',
                          maxWidth: '70%'
                        }}
                      >
                        {jumpDebugText && (
                          <pre
                            style={{
                              margin: 0,
                              padding: '0.5rem 0.75rem',
                              background: 'rgba(0, 0, 0, 0.7)',
                              color: '#f9fafb',
                              fontSize: '0.75rem',
                              lineHeight: 1.3,
                              borderRadius: '0.375rem',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {jumpDebugText}
                          </pre>
                        )}
                        {quarterEndDebugText && (
                          <pre
                            style={{
                              margin: 0,
                              padding: '0.5rem 0.75rem',
                              background: 'rgba(0, 0, 0, 0.7)',
                              color: '#f9fafb',
                              fontSize: '0.75rem',
                              lineHeight: 1.3,
                              borderRadius: '0.375rem',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {quarterEndDebugText}
                          </pre>
                        )}
                      </div>
                    )}
                    {chartMasked && (
                      <CenteredLoadingSpinner
                        isDarkMode={isDarkMode}
                        variant="overlay"
                        overlayBackground="solid"
                      />
                    )}
                    {!chartMasked && (
                      <AdvancedChart
                        key={`advanced-chart-${normalizedSymbol}-${visibilityKey}`}
                        elements={visibleElements}
                        labels={currentLabels}
                        isDarkMode={isDarkMode}
                        chartsDebug={chartsDebug}
                        xMin={dateKeyToUTCDate(safeStartKey, false)}
                        xMax={dateKeyToUTCDate(safeEndKey, true)}
                        normalizedPEOverlayValue={elementVisibility['pe']
                          ? normalizedPEOverlayValue
                          : null}
                        normalizedPOCFOverlayValue={elementVisibility['p-ocf-ratio']
                          ? normalizedPOCFOverlayValue
                          : null}
                        onTooltipChange={setTooltipData}
                        onChartAreaInsetsChange={(insets) => {
                          setNavigatorInsets({ leftInsetPx: insets.leftInsetPx, rightInsetPx: insets.rightInsetPx });
                        }}
                      />
                    )}
                  </div>

                  {/* Bottom Resize Handle */}
                  <div
                    id="vertical-resize-handle-bottom"
                    onMouseDown={(e) => handleResizeStart(e, 'vertical')}
                    style={{
                      position: 'absolute',
                      bottom: '0rem',
                      right: '24px',
                      left: '0px',
                      height: '0.375rem',
                      cursor: 'ns-resize',
                      backgroundColor: isDarkMode ? '#1f2937' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.2s ease',
                      border: 'none',
                      borderRadius: '0rem',
                      zIndex: 8
                    }}
                    onMouseEnter={(e) => {
                      if (!isResizing) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#e5e7eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isResizing) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                      }
                    }}
                  >
                    {/* Resize icon - horizontal lines */}
                    <svg width="24" height="6" viewBox="0 0 24 6" style={{ opacity: 0.5 }}>
                      <line x1="4" y1="1" x2="20" y2="1"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <line x1="4" y1="5" x2="20" y2="5"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  {/* Right Resize Handle */}
                  <div
                    id="horizontal-resize-handle-right"
                    onMouseDown={(e) => handleResizeStart(e, 'horizontal')}
                    style={{
                      position: 'absolute',
                      top: '0px',
                      right: '0px',
                      bottom: '24px',
                      width: '6px',
                      cursor: 'ew-resize',
                      backgroundColor: isDarkMode ? '#1f2937' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.2s ease',
                      border: 'none',
                      zIndex: 8
                    }}
                    onMouseEnter={(e) => {
                      if (!isResizing) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#e5e7eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isResizing) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                      }
                    }}
                  >
                    {/* Resize icon - vertical lines */}
                    <svg width="6" height="24" viewBox="0 0 6 24" style={{ opacity: 0.5 }}>
                      <line y1="4" x1="1" y2="20" x2="1"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <line y1="4" x1="5" y2="20" x2="5"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  {/* Corner Resize Handle - Both directions */}
                  <div
                    id="corner-resize-handle-both"
                    onMouseDown={(e) => handleResizeStart(e, 'both')}
                    style={{
                      position: 'absolute',
                      bottom: '0px',
                      right: '0px',
                      width: '24px',
                      height: '1.5rem',
                      cursor: 'nwse-resize',
                      backgroundColor: isDarkMode ? '#1f2937' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.2s ease',
                      border: 'none',
                      borderRadius: '0rem',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      if (!isResizing) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#e5e7eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isResizing) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                      }
                    }}
                  >
                    {/* Resize icon - diagonal lines */}
                    <svg width="14" height="14" viewBox="0 0 14 14" style={{ opacity: 0.5 }}>
                      <line x1="2" y1="12" x2="12" y2="2"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <line x1="6" y1="12" x2="12" y2="6"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <line x1="10" y1="12" x2="12" y2="10"
                        stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <AdvancedDateNavigator
                    startDate={new Date(safeStartKey)}
                    endDate={new Date(safeEndKey)}
                    minDate={minAllowedDate}
                    maxDate={maxAllowedDate}
                    onRangeChange={(start, end) => {
                      visibleRangeChangeSourceRef.current = 'date-range-container';
                      const constrainedEnd = end > maxAllowedDate ? maxAllowedDate : end;
                      setStartDate(start.toISOString().split('T')[0]);
                      setEndDate(constrainedEnd.toISOString().split('T')[0]);
                    }}
                    theme={isDarkMode ? 'dark' : 'light'}
                    leftInsetPx={navigatorInsets?.leftInsetPx}
                    rightInsetPx={navigatorInsets?.rightInsetPx}
                  />

                  {/* Quarter / Year navigation buttons */}
                  {(() => {
                    const shiftRangeByDays = (daysDelta: number, pulseKey: string) => {
                      visibleRangeChangeSourceRef.current = 'prev-next-quarter-year';
                      const startUtc = dateKeyToUTCDate(startDate, false);
                      const endUtc = dateKeyToUTCDate(endDate, true);
                      const maxAllowedUtc = dateKeyToUTCDate(toUTCDateKey(maxAllowedDate), true);

                      let nextEndMs = endUtc.getTime() + daysDelta * 86400000;

                      if (nextEndMs > maxAllowedUtc.getTime()) {
                        nextEndMs = maxAllowedUtc.getTime();
                      }
                      if (nextEndMs < startUtc.getTime()) {
                        nextEndMs = startUtc.getTime();
                      }

                      const nextEndKey = toUTCDateKey(new Date(nextEndMs));
                      const nextStartKey = startDate;

                      if (nextEndKey === endDate) {
                        return;
                      }

                      triggerJumpPulse(pulseKey);

                      updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                      setSelectedRange('CUSTOM');
                      setStartDate(nextStartKey);
                      setEndDate(nextEndKey);
                      saveChartsStateLocalImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                      saveChartsStateImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                    };

                    const shiftRange = (monthsDelta: number, pulseKey: string) => {
                      visibleRangeChangeSourceRef.current = 'prev-next-quarter-year';
                      const currentStartDate = startDate;
                      const currentEndDate = endDate;
                      const startUtc = dateKeyToUTCDate(currentStartDate, false);
                      const endUtc = dateKeyToUTCDate(currentEndDate, true);
                      const maxAllowedUtc = dateKeyToUTCDate(toUTCDateKey(maxAllowedDate), true);

                      const dateMode: FinancialDateMode =
                        analysisDateModeEps === 'at-reported' || analysisDateModeOcf === 'at-reported'
                          ? 'at-reported'
                          : 'as-reported';

                      const unit: 'quarter' | 'year' = Math.abs(monthsDelta) >= 12 ? 'year' : 'quarter';
                      const step = monthsDelta >= 0 ? 1 : -1;

                      const setDebug = (lines: string[]) => {
                        if (!chartsDebug) return;
                        setJumpDebugText(lines.filter(Boolean).join('\n'));
                      };

                      // Map every known fiscal date (earningDate, period end, report
                      // date) to its real "FY{year} Q{quarter}" so quarter labels are
                      // correct for non-calendar fiscal years (e.g. MSFT ends in June).
                      const fiscalQuarterByDateKey = new Map<string, string>();
                      fiscalIncomeQuarter.forEach((row) => {
                        const fy = (row as any).fiscalYear;
                        const fq = (row as any).fiscalQuarter;
                        if (fy == null || fq == null) return;
                        const label = `FY${fy} Q${fq}`;
                        [row.earningDate, (row as any).periodEndDate, (row as any).date, (row as any).reportDate]
                          .map((v) => String(v || '').slice(0, 10))
                          .filter((k) => /^\d{4}-\d{2}-\d{2}$/.test(k))
                          .forEach((k) => {
                            if (!fiscalQuarterByDateKey.has(k)) fiscalQuarterByDateKey.set(k, label);
                          });
                      });

                      const formatQuarterLabel = (raw?: string | Date) => {
                        if (!raw) return '';
                        const dt = raw instanceof Date ? raw : new Date(raw);
                        if (!Number.isFinite(dt.getTime())) return '';
                        const key = toUTCDateKey(dt);
                        // Prefer the real fiscal quarter from the data (handles
                        // non-calendar fiscal years like MSFT). Match by earningDate,
                        // period end, or report date; fall back to calendar quarter.
                        const fiscalLabel = fiscalQuarterByDateKey.get(key);
                        if (fiscalLabel) return `${key} (${fiscalLabel})`;
                        const month = dt.getUTCMonth() + 1;
                        const quarter = Math.floor((month - 1) / 3) + 1;
                        return `${key} (Q${quarter})`;
                      };

                      const getQuarterStartUtc = (d: Date) => {
                        const y = d.getUTCFullYear();
                        const m = d.getUTCMonth();
                        const qStartMonth = Math.floor(m / 3) * 3;
                        return new Date(Date.UTC(y, qStartMonth, 1, 0, 0, 0, 0));
                      };

                      const getQuarterEndUtc = (d: Date) => {
                        const y = d.getUTCFullYear();
                        const m = d.getUTCMonth();
                        const qStartMonth = Math.floor(m / 3) * 3;
                        return new Date(Date.UTC(y, qStartMonth + 3, 0, 23, 59, 59, 999));
                      };

                      const getYearStartUtc = (d: Date) =>
                        new Date(Date.UTC(d.getUTCFullYear(), 0, 1, 0, 0, 0, 0));

                      const getYearEndUtc = (d: Date) =>
                        new Date(Date.UTC(d.getUTCFullYear(), 11, 31, 23, 59, 59, 999));

                      const addUtcDays = (d: Date, days: number) => new Date(d.getTime() + days * 86400000);
                      const toPenultimateUtc = (d: Date) => addUtcDays(d, -1);
                      const toTwoDaysBeforeUtc = (d: Date) => addUtcDays(d, -2);
                      const toThreeDaysBeforeUtc = (d: Date) => addUtcDays(d, -3);
                      const toFourDaysBeforeUtc = (d: Date) => addUtcDays(d, -4);

                      const quarterEndKeys = (() => {
                        const keys = new Set<string>();
                        const addKey = (raw?: string | Date) => {
                          if (!raw) return;
                          const dt = raw instanceof Date ? raw : new Date(raw);
                          if (!Number.isFinite(dt.getTime())) return;
                          const key = toUTCDateKey(dt);
                          if (key) keys.add(key);
                        };

                        fiscalIncomeQuarter.forEach((row) => {
                          addKey(row.periodEndDate || row.date);
                        });
                        fiscalCashQuarter.forEach((row) => {
                          addKey(row.periodEndDate || row.date);
                        });

                        if (keys.size === 0 && selectedPeriodType === 'Quarterly Data') {
                          financialData.periods.forEach((dt) => addKey(dt));
                        }

                        return Array.from(keys)
                          .filter((key) => key.length >= 10)
                          .sort();
                      })();

                      const annualEndKeys = (() => {
                        const keys = new Set<string>();
                        const addKey = (raw?: string | Date) => {
                          if (!raw) return;
                          const dt = raw instanceof Date ? raw : new Date(raw);
                          if (!Number.isFinite(dt.getTime())) return;
                          const key = toUTCDateKey(dt);
                          if (key) keys.add(key);
                        };

                        fiscalIncomeAnnual.forEach((row) => {
                          addKey(row.periodEndDate || row.date);
                        });
                        fiscalCashAnnual.forEach((row) => {
                          addKey(row.periodEndDate || row.date);
                        });

                        if (keys.size === 0 && selectedPeriodType === 'Annual Data') {
                          financialData.periods.forEach((dt) => addKey(dt));
                        }

                        return Array.from(keys)
                          .filter((key) => key.length >= 10)
                          .sort();
                      })();

                      const quarterEndAnchorPenultimateDates = quarterEndKeys
                        .map((key) => {
                          const eod = dateKeyToUTCDate(key, true);
                          return toPenultimateUtc(eod).getTime();
                        })
                        .filter((t) => typeof t === 'number' && Number.isFinite(t))
                        .sort((a, b) => a - b);

                      const toUtcEodFromRaw = (raw?: string | Date): Date | null => {
                        if (!raw) return null;
                        const dt = raw instanceof Date ? raw : new Date(raw);
                        if (!Number.isFinite(dt.getTime())) return null;
                        const key = toUTCDateKey(dt);
                        if (!key) return null;
                        return dateKeyToUTCDate(key, true);
                      };

                      const atReportedQuarterEndAnchors = (() => {
                        const anchors = new Set<number>();
                        const addAnchorFromPeriodEnd = (raw?: string | Date) => {
                          const eod = toUtcEodFromRaw(raw);
                          if (!eod) return;
                          const anchorMs = toTwoDaysBeforeUtc(eod).getTime();
                          if (Number.isFinite(anchorMs)) anchors.add(anchorMs);
                        };
                        const addAnchorFromFiling = (raw?: string | Date) => {
                          const eod = toUtcEodFromRaw(raw);
                          if (!eod) return;
                          const anchorMs = toTwoDaysBeforeUtc(eod).getTime();
                          if (Number.isFinite(anchorMs)) anchors.add(anchorMs);
                        };

                        fiscalIncomeQuarter.forEach((row) => {
                          const periodEnd = row.periodEndDate || row.date;
                          const periodKey = periodEnd ? periodEnd.slice(0, 10) : '';
                          const filing = (periodKey ? quarterlyFilingDates.get(periodKey) : undefined) || row.earningDate || row.filingDate;
                          if (filing) {
                            addAnchorFromFiling(filing);
                            return;
                          }
                          if (periodEnd) addAnchorFromPeriodEnd(periodEnd);
                        });

                        fiscalCashQuarter.forEach((row) => {
                          const periodEnd = row.periodEndDate || row.date;
                          const periodKey = periodEnd ? periodEnd.slice(0, 10) : '';
                          const filing = (periodKey ? quarterlyFilingDates.get(periodKey) : undefined) || row.filingDate;
                          if (filing) {
                            addAnchorFromFiling(filing);
                            return;
                          }
                          if (periodEnd) addAnchorFromPeriodEnd(periodEnd);
                        });

                        quarterlyFilingDates.forEach((_value, key) => {
                          if (key) addAnchorFromPeriodEnd(key);
                        });

                        if (anchors.size === 0 && quarterEndKeys.length > 0) {
                          quarterEndKeys.forEach((key) => {
                            const eod = dateKeyToUTCDate(key, true);
                            const anchorMs = toTwoDaysBeforeUtc(eod).getTime();
                            if (Number.isFinite(anchorMs)) anchors.add(anchorMs);
                          });
                        }

                        if (anchors.size === 0) {
                          const fallback = epsAnchorDates.length > 0
                            ? epsAnchorDates
                            : flatEPSTTMData.map((point) => point?.date as unknown);
                          fallback.forEach((raw) => {
                            const dt = raw instanceof Date ? raw : new Date(raw as any);
                            if (!Number.isFinite(dt.getTime())) return;
                            const anchorEod = getQuarterEndUtc(dt);
                            const anchorMs = toTwoDaysBeforeUtc(anchorEod).getTime();
                            if (Number.isFinite(anchorMs)) anchors.add(anchorMs);
                          });
                        }

                        return Array.from(anchors).sort((a, b) => a - b);
                      })();

                      const atReportedYearAnchors = (() => {
                        const anchorsByYear = new Map<number, { ms: number; source: string }>();
                        const addAnchorForYear = (raw?: string | Date) => {
                          const eod = toUtcEodFromRaw(raw);
                          if (!eod) return;
                          const year = eod.getUTCFullYear();
                          const anchorMs = toTwoDaysBeforeUtc(eod).getTime();
                          if (!Number.isFinite(anchorMs)) return;
                          const sourceKey = toUTCDateKey(eod);
                          const existing = anchorsByYear.get(year);
                          if (!existing || anchorMs > existing.ms) {
                            anchorsByYear.set(year, { ms: anchorMs, source: sourceKey });
                          }
                        };
                        const addQuarterAnchor = (row: FiscalIncomeRow | FiscalCashRow) => {
                          const periodEnd = row.periodEndDate || row.date;
                          const periodKey = periodEnd ? periodEnd.slice(0, 10) : '';
                          const filing = (periodKey ? quarterlyFilingDates.get(periodKey) : undefined) || row.earningDate || row.filingDate;
                          addAnchorForYear(filing || periodEnd);
                        };

                        fiscalIncomeQuarter.forEach((row) => addQuarterAnchor(row));
                        fiscalCashQuarter.forEach((row) => addQuarterAnchor(row));

                        if (anchorsByYear.size === 0 && quarterEndKeys.length > 0) {
                          quarterEndKeys.forEach((key) => {
                            const filing = quarterlyFilingDates.get(key);
                            addAnchorForYear(filing || key);
                          });
                        }

                        if (anchorsByYear.size === 0) {
                          fiscalIncomeAnnual.forEach((row) => addAnchorForYear(row.periodEndDate || row.date));
                          fiscalCashAnnual.forEach((row) => addAnchorForYear(row.periodEndDate || row.date));
                          fmpFiscalYearEndKeys.forEach((key) => addAnchorForYear(key));
                        }

                        return Array.from(anchorsByYear.entries())
                          .sort((a, b) => a[0] - b[0])
                          .map(([year, data]) => ({ year, ms: data.ms, source: data.source }));
                      })();

                      // The At Reported TTM series has already applied its reporting-date
                      // shift. Navigation must use those exact series dates so Previous
                      // followed by Next returns to the same quarter.
                      const atReportedPrimaryAnchors = (() => {
                        if (dateMode === 'at-reported' && flatEPSTTMData.length > 0) {
                          const unique = new Set<number>();
                          flatEPSTTMData.forEach((point) => {
                            const key = toUTCDateKey(point.date);
                            if (!key) return;
                            const ms = dateKeyToUTCDate(key, true).getTime();
                            if (Number.isFinite(ms)) unique.add(ms);
                          });
                          return Array.from(unique).sort((a, b) => a - b);
                        }

                        if (prefersFiscal && fiscalFlatEpsTtmPoints.length > 0) {
                          const unique = new Set<number>();
                          fiscalFlatEpsTtmPoints.forEach((point) => {
                            const key = toUTCDateKey(point.date);
                            if (!key) return;
                            const ms = dateKeyToUTCDate(key, true).getTime();
                            if (Number.isFinite(ms)) unique.add(ms);
                          });
                          return Array.from(unique).sort((a, b) => a - b);
                        }

                        if (analysisDateModeEps === 'at-reported' && epsAnchorDates.length > 0) {
                          const unique = new Set<number>();
                          epsAnchorDates.forEach((date) => {
                            const key = toUTCDateKey(date);
                            if (!key) return;
                            const ms = dateKeyToUTCDate(key, true).getTime();
                            if (Number.isFinite(ms)) unique.add(ms);
                          });
                          if (unique.size > 0) {
                            return Array.from(unique).sort((a, b) => a - b);
                          }
                        }

                        return atReportedQuarterEndAnchors;
                      })();

                      if (chartsDebug) {
                        const currentMs = dateKeyToUTCDate(currentEndDate, true).getTime();
                        const preview = atReportedPrimaryAnchors
                          .slice(-8)
                          .map((ms) => formatQuarterLabel(new Date(ms)))
                          .join(', ');
                        setDebug([
                          `mode=${dateMode} unit=${unit} dir=${step > 0 ? 'next' : 'prev'}`,
                          `currentEnd=${formatQuarterLabel(new Date(currentMs))}`,
                          `anchors(count)=${atReportedPrimaryAnchors.length}`,
                          `anchors(last8)=${preview || 'n/a'}`,
                        ]);
                      }

                      const atReportedYearAnchorsFromPrimary = (() => {
                        if (atReportedPrimaryAnchors.length === 0) return [] as Array<{ year: number; ms: number }>;
                        const byYear = new Map<number, number>();
                        atReportedPrimaryAnchors.forEach((ms) => {
                          const year = new Date(ms).getUTCFullYear();
                          const existing = byYear.get(year);
                          if (!existing || ms > existing) byYear.set(year, ms);
                        });
                        return Array.from(byYear.entries())
                          .sort((a, b) => a[0] - b[0])
                          .map(([year, ms]) => ({ year, ms }));
                      })();

                      // In At Reported mode, quarter jumps should anchor to the
                      // day-before quarter end (per spec), using real quarter endpoints.
                      if (dateMode === 'at-reported' && unit === 'quarter') {
                        const currentMs = dateKeyToUTCDate(endDate, true).getTime();
                        const pickShiftedAnchor = (anchors: number[], ms: number, dir: 1 | -1): number | undefined => {
                          if (!Array.isArray(anchors) || anchors.length === 0) return undefined;

                          if (dir < 0) {
                            let floorIndex = -1;
                            for (let idx = 0; idx < anchors.length; idx += 1) {
                              if (anchors[idx] <= ms) floorIndex = idx;
                            }
                            if (floorIndex < 0) return undefined;
                            const isExact = anchors[floorIndex] === ms;
                            const targetIndex = isExact ? floorIndex - 1 : floorIndex;
                            if (targetIndex < 0 || targetIndex >= anchors.length) return undefined;
                            return anchors[targetIndex];
                          }

                          let ceilIndex = -1;
                          for (let idx = 0; idx < anchors.length; idx += 1) {
                            if (anchors[idx] >= ms) {
                              ceilIndex = idx;
                              break;
                            }
                          }
                          if (ceilIndex < 0) return undefined;
                          const isExact = anchors[ceilIndex] === ms;
                          const targetIndex = isExact ? ceilIndex + 1 : ceilIndex;
                          if (targetIndex < 0 || targetIndex >= anchors.length) return undefined;
                          return anchors[targetIndex];
                        };

                        const buildAtReportedQuarterAnchorsByPeriod = () => {
                          const anchors = quarterEndKeys
                            .map((periodKey) => {
                              const incomeRow = fiscalIncomeQuarter.find((row) => {
                                const key = String(row?.periodEndDate || row?.date || '').slice(0, 10);
                                return key === periodKey;
                              });
                              const cashRow = fiscalCashQuarter.find((row) => {
                                const key = String(row?.periodEndDate || row?.date || '').slice(0, 10);
                                return key === periodKey;
                              });

                              const filingRaw = quarterlyFilingDates.get(periodKey)
                                || incomeRow?.earningDate
                                || incomeRow?.filingDate
                                || cashRow?.filingDate;

                              const filingEod = toUtcEodFromRaw(filingRaw);
                              const periodEod = dateKeyToUTCDate(periodKey, true);
                              const anchorMs = filingEod
                                ? toTwoDaysBeforeUtc(filingEod).getTime()
                                : toPenultimateUtc(periodEod).getTime();

                              return {
                                periodKey,
                                anchorMs,
                                periodMs: periodEod.getTime(),
                              };
                            })
                            .filter((item) => Number.isFinite(item.anchorMs) && Number.isFinite(item.periodMs))
                            .sort((a, b) => a.periodMs - b.periodMs);

                          return anchors;
                        };

                        const quarterAnchorsByPeriod = buildAtReportedQuarterAnchorsByPeriod();

                        let candidateMs: number | undefined;
                        const hasPeriodAnchors = quarterAnchorsByPeriod.length > 0;
                        const anchorMsByPeriod = hasPeriodAnchors
                          ? quarterAnchorsByPeriod.map((row) => row.anchorMs)
                          : [];
                        const fallbackAnchorPool = atReportedPrimaryAnchors.length > 0
                          ? atReportedPrimaryAnchors
                          : atReportedQuarterEndAnchors;
                        const latestAnchorMs = hasPeriodAnchors
                          ? anchorMsByPeriod[anchorMsByPeriod.length - 1]
                          : fallbackAnchorPool[fallbackAnchorPool.length - 1];
                        const maxMs = maxAllowedUtc.getTime();

                        if (step > 0 && returnToMaxOnNextQuarterRef.current) {
                          const pendingKey = returnToMaxOnNextQuarterRef.current;
                          const pendingMs = dateKeyToUTCDate(pendingKey, true).getTime();
                          if (Number.isFinite(pendingMs) && currentMs < pendingMs) {
                            candidateMs = pendingMs;
                          }
                          returnToMaxOnNextQuarterRef.current = null;
                        }

                        if ((typeof candidateMs !== 'number' || !Number.isFinite(candidateMs)) && hasPeriodAnchors) {
                          // Shift relative to current end date while preserving the
                          // "today -> latest available quarter" first step behavior.
                          candidateMs = pickShiftedAnchor(anchorMsByPeriod, currentMs, step as 1 | -1);

                          // When quarter anchors exist, keep navigation symmetric by
                          // staying on this canonical anchor set. If there is no next
                          // quarter anchor, a single Next must return to max date.
                          if ((typeof candidateMs !== 'number' || !Number.isFinite(candidateMs)) && step > 0) {
                            if (currentMs >= latestAnchorMs && currentMs < maxMs) {
                              candidateMs = maxMs;
                            }
                          }
                        }

                        if (!hasPeriodAnchors && (typeof candidateMs !== 'number' || !Number.isFinite(candidateMs)) && atReportedPrimaryAnchors.length > 0) {
                          candidateMs = pickShiftedAnchor(atReportedPrimaryAnchors, currentMs, step as 1 | -1);
                        }

                        if (!hasPeriodAnchors && (typeof candidateMs !== 'number' || !Number.isFinite(candidateMs)) && atReportedQuarterEndAnchors.length > 0) {
                          candidateMs = pickShiftedAnchor(atReportedQuarterEndAnchors, currentMs, step as 1 | -1);
                        }

                        if (step < 0) {
                          const startedAboveLatestAnchor = Number.isFinite(latestAnchorMs) && currentMs > (latestAnchorMs as number);
                          const movedBackward = typeof candidateMs === 'number' && Number.isFinite(candidateMs) && candidateMs < currentMs;
                          returnToMaxOnNextQuarterRef.current = startedAboveLatestAnchor && movedBackward
                            ? currentEndDate
                            : null;
                        } else if (step > 0) {
                          returnToMaxOnNextQuarterRef.current = null;
                        }

                        // If we're already on the latest At Reported quarter anchor,
                        // there is no "next" anchor. In that case, one Next click
                        // should return directly to today (max allowed date) instead
                        // of falling through to generic day-based shifting.
                        if ((typeof candidateMs !== 'number' || !Number.isFinite(candidateMs)) && step > 0) {
                          if (currentMs < maxMs) {
                            candidateMs = maxMs;
                          }
                        }

                        const preview = atReportedPrimaryAnchors
                          .slice(-8)
                          .map((ms) => formatQuarterLabel(new Date(ms)))
                          .join(', ');
                        setDebug([
                          `mode=at-reported unit=quarter dir=${step > 0 ? 'next' : 'prev'}`,
                          `currentEnd=${formatQuarterLabel(new Date(currentMs))}`,
                          `candidate=${candidateMs ? formatQuarterLabel(new Date(candidateMs)) : 'n/a'}`,
                          `anchors(count)=${atReportedPrimaryAnchors.length}`,
                          `anchors(last8)=${preview || 'n/a'}`,
                        ]);

                        if (typeof candidateMs === 'number' && Number.isFinite(candidateMs)) {
                          const clampedMs = Math.min(
                            maxAllowedUtc.getTime(),
                            Math.max(startUtc.getTime(), candidateMs)
                          );
                          const nextEndKey = toUTCDateKey(new Date(clampedMs));
                          const nextStartKey = currentStartDate;

                          if (nextEndKey !== currentEndDate) {
                            triggerJumpPulse(pulseKey);

                            latestRangeRef.current = { startKey: nextStartKey, endKey: nextEndKey };
                            updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            setSelectedRange('CUSTOM');
                            setStartDate(nextStartKey);
                            setEndDate(nextEndKey);
                            saveChartsStateLocalImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            saveChartsStateImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                          }
                          return;
                        }
                      }

                      // In As Reported mode, quarter jumps must use the REAL fiscal
                      // quarter-end dates from the data (quarterEndKeys), not calendar
                      // quarters. Calendar quarters only line up for stocks whose fiscal
                      // year matches the calendar (e.g. GOOG); stocks like NVDA end their
                      // fiscal quarters on different dates, so the generic calendar path
                      // would land on the wrong dates. Anchor exactly on the fiscal
                      // period-end so prev/next match the chart anchors for every stock.
                      if (dateMode === 'as-reported' && unit === 'quarter') {
                        const asReportedQuarterAnchors = (() => {
                          const unique = new Set<number>();
                          quarterEndKeys.forEach((key) => {
                            const ms = toPenultimateUtc(dateKeyToUTCDate(key, true)).getTime();
                            if (Number.isFinite(ms)) unique.add(ms);
                          });
                          return Array.from(unique).sort((a, b) => a - b);
                        })();

                        if (asReportedQuarterAnchors.length > 0) {
                          const currentMs = dateKeyToUTCDate(currentEndDate, true).getTime();
                          const candidateMs = step > 0
                            ? asReportedQuarterAnchors.find((t) => t > currentMs)
                            : [...asReportedQuarterAnchors].reverse().find((t) => t < currentMs);

                          if (chartsDebug) {
                            const preview = asReportedQuarterAnchors
                              .slice(-8)
                              .map((ms) => formatQuarterLabel(new Date(ms)))
                              .join(', ');
                            setDebug([
                              `mode=as-reported unit=quarter dir=${step > 0 ? 'next' : 'prev'}`,
                              `currentEnd=${formatQuarterLabel(new Date(currentMs))}`,
                              `candidate=${typeof candidateMs === 'number' ? formatQuarterLabel(new Date(candidateMs)) : 'n/a'}`,
                              `anchors(count)=${asReportedQuarterAnchors.length}`,
                              `anchors(last8)=${preview || 'n/a'}`,
                            ]);
                          }

                          if (typeof candidateMs === 'number' && Number.isFinite(candidateMs)) {
                            const clampedMs = Math.min(
                              maxAllowedUtc.getTime(),
                              Math.max(startUtc.getTime(), candidateMs)
                            );
                            const nextEndKey = toUTCDateKey(new Date(clampedMs));
                            const nextStartKey = currentStartDate;

                            if (nextEndKey !== currentEndDate) {
                              triggerJumpPulse(pulseKey);

                              latestRangeRef.current = { startKey: nextStartKey, endKey: nextEndKey };
                              updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                              setSelectedRange('CUSTOM');
                              setStartDate(nextStartKey);
                              setEndDate(nextEndKey);
                              saveChartsStateLocalImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                              saveChartsStateImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            }
                            return;
                          }
                        }
                      }

                      const getFiscalQ1YearAnchors = (): Array<{ fy: number; ms: number }> => {
                        const byYear = new Map<number, number>();
                        const addCandidate = (fyRaw: unknown, raw?: string | Date) => {
                          const fy = Number(fyRaw);
                          if (!Number.isFinite(fy) || !raw) return;
                          const key = (() => {
                            if (typeof raw === 'string') {
                              const s = raw.trim();
                              const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
                              if (m) return m[1];
                            }
                            return toUTCDateKey(raw);
                          })();
                          if (!key) return;
                          const anchorEod = dateKeyToUTCDate(key, true);
                          const ms = toPenultimateUtc(anchorEod).getTime();
                          if (!Number.isFinite(ms)) return;
                          const existing = byYear.get(fy);
                          // Keep the latest for stability when sources differ by a day.
                          if (existing == null || ms > existing) {
                            byYear.set(fy, ms);
                          }
                        };

                        const collectFromRow = (row: FiscalIncomeRow | FiscalCashRow) => {
                          const fy = (row as any).fiscalYear;
                          const fq = (row as any).fiscalQuarter;
                          if (fq !== 1) return;

                          if (dateMode === 'at-reported') {
                            const periodEnd = row.periodEndDate || row.date;
                            const periodKey = periodEnd ? String(periodEnd).slice(0, 10) : '';
                            const reportRaw = row.earningDate || row.filingDate || (periodKey ? quarterlyFilingDates.get(periodKey) : undefined) || periodEnd;
                            addCandidate(fy, reportRaw);
                            return;
                          }

                          addCandidate(fy, row.periodEndDate || row.date);
                        };

                        fiscalIncomeQuarter.forEach((row) => collectFromRow(row));
                        fiscalCashQuarter.forEach((row) => collectFromRow(row));

                        return Array.from(byYear.entries())
                          .sort((a, b) => a[0] - b[0])
                          .map(([fy, ms]) => ({ fy, ms }));
                      };

                      // Previous/Next Year should use ordered Fiscal Q1 anchors (day-before)
                      // so prev and next are symmetric. If there's no future anchor, next
                      // returns to "today" (max allowed date).
                      if (unit === 'year' && step < 0) {
                        const currentEndKeyMs = dateKeyToUTCDate(currentEndDate, true).getTime();
                        const yearAnchors = getFiscalQ1YearAnchors();
                        const candidateMs = [...yearAnchors]
                          .reverse()
                          .find((item) => item.ms < currentEndKeyMs)?.ms ?? null;

                        if (chartsDebug) {
                          setDebug([
                            `mode=${dateMode} unit=year dir=prev`,
                            `currentEnd=${formatQuarterLabel(new Date(currentEndKeyMs))}`,
                            `anchors=${yearAnchors.length}`,
                            `candidate=${candidateMs ? formatQuarterLabel(new Date(candidateMs)) : 'n/a'}`,
                          ]);
                        }

                        if (typeof candidateMs === 'number' && Number.isFinite(candidateMs)) {
                          const clampedMs = Math.min(
                            maxAllowedUtc.getTime(),
                            Math.max(startUtc.getTime(), candidateMs)
                          );
                          const nextEndKey = toUTCDateKey(new Date(clampedMs));
                          const nextStartKey = currentStartDate;

                          if (nextEndKey !== currentEndDate) {
                            triggerJumpPulse(pulseKey);

                            latestRangeRef.current = { startKey: nextStartKey, endKey: nextEndKey };
                            updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            setSelectedRange('CUSTOM');
                            setStartDate(nextStartKey);
                            setEndDate(nextEndKey);
                            saveChartsStateLocalImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            saveChartsStateImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                          }
                          return;
                        }
                      }

                      if (unit === 'year' && step > 0) {
                        const currentEndKeyMs = dateKeyToUTCDate(currentEndDate, true).getTime();
                        const yearAnchors = getFiscalQ1YearAnchors();
                        const nextAnchorMs = yearAnchors.find((item) => item.ms > currentEndKeyMs)?.ms ?? null;
                        const candidateMs = nextAnchorMs ?? (currentEndKeyMs < maxAllowedUtc.getTime() ? maxAllowedUtc.getTime() : null);

                        if (chartsDebug) {
                          setDebug([
                            `mode=${dateMode} unit=year dir=next`,
                            `currentEnd=${formatQuarterLabel(new Date(currentEndKeyMs))}`,
                            `anchors=${yearAnchors.length}`,
                            `candidate=${candidateMs ? formatQuarterLabel(new Date(candidateMs)) : 'n/a'}`,
                          ]);
                        }

                        if (typeof candidateMs === 'number' && Number.isFinite(candidateMs)) {
                          const clampedMs = Math.min(
                            maxAllowedUtc.getTime(),
                            Math.max(startUtc.getTime(), candidateMs)
                          );
                          const nextEndKey = toUTCDateKey(new Date(clampedMs));
                          const nextStartKey = currentStartDate;

                          if (nextEndKey !== currentEndDate) {
                            triggerJumpPulse(pulseKey);

                            latestRangeRef.current = { startKey: nextStartKey, endKey: nextEndKey };
                            updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            setSelectedRange('CUSTOM');
                            setStartDate(nextStartKey);
                            setEndDate(nextEndKey);
                            saveChartsStateLocalImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                            saveChartsStateImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                          }
                          return;
                        }
                      }

                      const getPeriodStart = (d: Date) => (unit === 'quarter' ? getQuarterStartUtc(d) : getYearStartUtc(d));
                      const getPeriodEnd = (d: Date) => (unit === 'quarter' ? getQuarterEndUtc(d) : getYearEndUtc(d));

                      const getShiftedPeriodStart = (d: Date, periodsDelta: number) => {
                        if (unit === 'quarter') {
                          const s = getQuarterStartUtc(d);
                          return new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + periodsDelta * 3, 1, 0, 0, 0, 0));
                        }
                        const s = getYearStartUtc(d);
                        return new Date(Date.UTC(s.getUTCFullYear() + periodsDelta, 0, 1, 0, 0, 0, 0));
                      };

                      const filingDatesUtc = Array.from(quarterlyFilingDates.values())
                        .map((raw) => new Date(`${raw}T23:59:59.999Z`))
                        .filter((d) => Number.isFinite(d.getTime()));

                      const resolvePeriodPenultimate = (periodStart: Date): Date => {
                        const periodEnd = getPeriodEnd(periodStart);

                        if (dateMode === 'as-reported') {
                          return toPenultimateUtc(periodEnd);
                        }

                        const windowStart = getPeriodStart(periodStart).getTime();
                        const windowEnd = getPeriodEnd(periodStart).getTime();
                        const inPeriod = filingDatesUtc
                          .map((d) => d.getTime())
                          .filter((t) => t >= windowStart && t <= windowEnd)
                          .sort((a, b) => a - b);

                        if (inPeriod.length === 0) return toPenultimateUtc(periodEnd);
                        return toPenultimateUtc(new Date(inPeriod[inPeriod.length - 1]));
                      };

                      const currentPeriodStart = getPeriodStart(endUtc);
                      const currentPenultimate = resolvePeriodPenultimate(currentPeriodStart);
                      const shouldAdvance = step > 0
                        ? endUtc.getTime() >= currentPenultimate.getTime()
                        : endUtc.getTime() <= currentPenultimate.getTime();

                      const targetPeriodStart = shouldAdvance
                        ? getShiftedPeriodStart(currentPeriodStart, step)
                        : currentPeriodStart;

                      const candidateEnd = resolvePeriodPenultimate(targetPeriodStart);
                      let nextEndMs = candidateEnd.getTime();

                      // Cannot jump forward beyond current date.
                      if (nextEndMs > maxAllowedUtc.getTime()) {
                        nextEndMs = maxAllowedUtc.getTime();
                      }
                      // Cannot jump backward beyond left slider date.
                      if (nextEndMs < startUtc.getTime()) {
                        nextEndMs = startUtc.getTime();
                      }

                      const nextEndKey = toUTCDateKey(new Date(nextEndMs));
                      const nextStartKey = currentStartDate;

                      if (nextEndKey === currentEndDate) {
                        return;
                      }

                      triggerJumpPulse(pulseKey);

                      latestRangeRef.current = { startKey: nextStartKey, endKey: nextEndKey };
                      updateChartsStateRef({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                      setSelectedRange('CUSTOM');
                      setStartDate(nextStartKey);
                      setEndDate(nextEndKey);
                      saveChartsStateLocalImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                      saveChartsStateImmediate({ selectedRange: 'CUSTOM', startDate: nextStartKey, endDate: nextEndKey });
                    };

                    const baseBtnStyle: React.CSSProperties = {
                      width: 140,
                      height: 32,
                      paddingTop: 2,
                      paddingRight: 10,
                      paddingBottom: 2,
                      paddingLeft: 10,
                      borderTopWidth: 1,
                      borderBottomWidth: 1,
                      borderLeftWidth: 1,
                      borderRightWidth: 1,
                      borderStyle: 'solid',
                      borderColor: '#D9D9D9',
                      borderTopLeftRadius: 6,
                      borderBottomLeftRadius: 6,
                      borderTopRightRadius: 6,
                      borderBottomRightRadius: 6,
                      background: isDarkMode ? '#1f2937' : '#ffffff',
                      fontFamily: 'Manrope, ui-sans-serif, system-ui',
                      fontWeight: 300,
                      fontSize: 12,
                      lineHeight: '100%',
                      letterSpacing: '0.01em',
                      textAlign: 'center',
                      color: isDarkMode ? '#e5e7eb' : '#5A5A5A',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                      boxSizing: 'border-box',
                      opacity: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      transition: 'transform 140ms ease, box-shadow 140ms ease',
                    };

                    const getBtnStyle = (key: string): React.CSSProperties => ({
                      ...baseBtnStyle,
                      transform: jumpPulseKey === key ? 'translateY(-1px)' : 'translateY(0)',
                      boxShadow: jumpPulseKey === key ? '0 2px 6px rgba(0, 0, 0, 0.18)' : 'none',
                    });

                    const showYearShiftButtons = false;

                    return (
                      <div
                        id="date-navigator-jump-buttons"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '28px',
                          width: 692,
                          height: 32,
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          position: 'relative',
                          zIndex: 20,
                          pointerEvents: 'auto',
                          opacity: 1,
                        }}
                      >
                        {showChartsInlineAttribution && chartsUseFiscalSource ? (
                          <div
                            style={{
                              position: 'absolute',
                              left: '-380px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <DataSourceAttribution
                              isDarkMode={isDarkMode}
                              sourceLabel="Fiscal.ai"
                              sourceUrl="https://fiscal.ai"
                              style={{ marginTop: 0 }}
                            />
                          </div>
                        ) : null}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {showYearShiftButtons ? (
                            <button type="button" style={getBtnStyle('prev-year')} onClick={() => shiftRange(-12, 'prev-year')}>
                              <img src="/images/previus_years.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24 }} />
                              Previous Year
                            </button>
                          ) : null}
                          <button type="button" style={getBtnStyle('prev-quarter')} onClick={() => shiftRange(-3, 'prev-quarter')}>
                            <img src="/images/previus_quarter.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24 }} />
                            Previous Quarter
                          </button>
                          <button type="button" style={getBtnStyle('prev-day')} onClick={() => shiftRangeByDays(-1, 'prev-day')}>
                            <img src="/images/previus_quarter.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24 }} />
                            Previous Day
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button type="button" style={getBtnStyle('next-day')} onClick={() => shiftRangeByDays(1, 'next-day')}>
                            Next Day
                            <img src="/images/next_quarter.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24 }} />
                          </button>
                          <button type="button" style={getBtnStyle('next-quarter')} onClick={() => shiftRange(3, 'next-quarter')}>
                            Next Quarter
                            <img src="/images/next_quarter.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24 }} />
                          </button>
                          {showYearShiftButtons ? (
                            <button type="button" style={getBtnStyle('next-year')} onClick={() => shiftRange(12, 'next-year')}>
                              Next Year
                              <img src="/images/next_years.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24 }} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>

            {/* Right Adjustment Panel - Positioned next to chart */}
            {(() => {
              const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
              const heightScale = chartHeight / 50;
              const widthScale = chartWidth !== null ? chartWidth / 900 : 1;
              const uiScale = clamp(Math.min(heightScale, widthScale), 0.5, 1.25);

              return (
                <div
                  id="right-panel-container"
                  style={{
                    height: `${chartHeight}vh`,
                    width: '3.9375rem',
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'stretch',
                    overflow: 'hidden',
                  }}
                >
              <RightAdjustmentPanel
                elements={elements}
                onAdjustElement={handleAdjustElement}
                isDarkMode={isDarkMode}
                isActive={true}
                chartHeight={chartHeight}
                uiScale={uiScale}
              />
                </div>
              );
            })()}
              </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdvancedChartScreen;






